import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import SignatureInput from '../components/SignatureInput';
import AIOasisSummaryGenerator from '../components/AIOasisSummaryGenerator';
import { 
  FileText, Save, ArrowLeft, ArrowRight, User, Brain, Heart, 
  Activity, Pill, Stethoscope, Home, CheckCircle, Download, Printer, Eye, Users, ClipboardList, Thermometer, Droplet, Shield, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper component for select with voice dictation indicator
const DictationSelect = ({ label, value, onChange, options, className = '', helpText = '' }) => (
  <div className={className}>
    <div className="flex items-center gap-2">
      <Label className="text-sm font-medium">{label}</Label>
    </div>
    {helpText && <p className="text-xs text-gray-500 mb-1">{helpText}</p>}
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12] text-sm"
    >
      <option value="">Select...</option>
      {options.map((opt, idx) => (
        <option key={idx} value={typeof opt === 'object' ? opt.value : opt}>
          {typeof opt === 'object' ? opt.label : opt}
        </option>
      ))}
    </select>
  </div>
);

// Helper for Yes/No radio groups with voice dictation
const YesNoRadio = ({ label, value, onChange, name, helpText = '' }) => (
  <div className="space-y-1">
    <div className="flex items-center gap-2">
      <Label className="text-sm font-medium">{label}</Label>
    </div>
    {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
    <div className="flex gap-4">
      <label className="flex items-center gap-2">
        <input type="radio" name={name} checked={value === 'No'} onChange={() => onChange('No')} className="w-4 h-4" />
        <span className="text-sm">No</span>
      </label>
      <label className="flex items-center gap-2">
        <input type="radio" name={name} checked={value === 'Yes'} onChange={() => onChange('Yes')} className="w-4 h-4" />
        <span className="text-sm">Yes</span>
      </label>
    </div>
  </div>
);

const SOCROCAssessmentFormV2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('id');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  
  // Complete form data state matching the PDF structure verbatim
  const [formData, setFormData] = useState({
    // Patient Selection
    patientId: '',
    
    // Page 1 - Header
    reasonForAssessment: '', // Start of Care or Resumption of Care
    assessmentDate: '',
    timeIn: '',
    timeOut: '',
    
    // Section A - Administrative Information (Pages 1-6)
    // M0018 National Provider Identifier
    m0018NPI: '',
    physicianLastName: '',
    physicianFirstName: '',
    physicianMI: '',
    physicianSuffix: '',
    physicianAddress: '',
    physicianUK: false, // UK - Unknown or Not Available
    physicianPhone: '',
    physicianFax: '',
    physicianEmail: '',
    physicianCity: '',
    physicianState: '',
    physicianZipCode: '',
    
    // M0010-M0060
    m0010CMSCertificationNumber: '',
    m0014BranchState: '',
    m0016BranchIDNumber: '',
    m0020PatientIDNumber: '',
    medicalRecordNumber: '',
    m0030StartOfCareDate: '',
    m0032ResumptionOfCareDate: '',
    m0032NA: false, // NA - Not Applicable
    m0040PatientFirstName: '',
    m0040PatientMI: '',
    m0040PatientLastName: '',
    m0040PatientSuffix: '',
    m0050PatientStateOfResidence: '',
    m0060PatientZipCode: '',
    m0064SocialSecurityNumber: '',
    m0064UK: false, // UK - Unknown or Not Available
    emergencyPreparednessCode: '',
    
    // Page 2 - Section A Continued
    m0063MedicareNumber: '',
    m0063NANoMedicare: false,
    m0063ClaimNumber: '',
    m0065MedicaidNumber: '',
    m0065NANoMedicaid: false,
    m0065ClaimNumber: '',
    m0069Gender: '', // 1. Male, 2. Female
    m0066BirthDate: '',
    patientSelfIdentifiesAs: '', // If not Male or Female
    
    // A1005 Ethnicity
    a1005EthnicityA: false, // No, not of Hispanic, Latino/a, or Spanish origin
    a1005EthnicityB: false, // Yes, Mexican, Mexican American, Chicano/a
    a1005EthnicityC: false, // Yes, Puerto Rican
    a1005EthnicityD: false, // Yes, Cuban
    a1005EthnicityE: false, // Yes, another Hispanic, Latino, or Spanish origin
    a1005EthnicityX: false, // Patient unable to respond
    a1005EthnicityY: false, // Patient declines to respond
    
    // A1010 Race
    a1010RaceA: false, // White
    a1010RaceB: false, // Black or African American
    a1010RaceC: false, // American Indian or Alaska Native
    a1010RaceD: false, // Asian Indian
    a1010RaceE: false, // Chinese
    a1010RaceF: false, // Filipino
    a1010RaceG: false, // Japanese
    a1010RaceH: false, // Korean
    a1010RaceI: false, // Vietnamese
    a1010RaceJ: false, // Other Asian
    a1010RaceK: false, // Native Hawaiian
    a1010RaceL: false, // Guamanian or Chamorro
    a1010RaceM: false, // Samoan
    a1010RaceN: false, // Other Pacific Islander
    a1010RaceX: false, // Patient unable to respond
    a1010RaceY: false, // Patient declines to respond
    a1010RaceZ: false, // None of the above
    
    // M0150 Current Payment Source
    m0150PaymentSource0: false, // None; no charge for current services
    m0150PaymentSource1: false, // Medicare (traditional fee-for-service)
    m0150PaymentSource2: false, // Medicare (HMO/managed care/Advantage plan)
    m0150PaymentSource3: false, // Medicaid (traditional fee-for-service)
    m0150PaymentSource4: false, // Medicaid (HMO/managed care)
    m0150PaymentSource5: false, // Workers' compensation
    m0150PaymentSource6: false, // Title programs (for example, Title III, V, XX)
    m0150PaymentSource7: false, // Other government (for example, TriCare, VA)
    m0150PaymentSource8: false, // Private insurance
    m0150PaymentSource9: false, // Private HMO/managed care
    m0150PaymentSource10: false, // Self-pay
    m0150PaymentSource11: false, // Other (specify)
    m0150PaymentSource11Specify: '',
    m0150PaymentSourceUK: false, // Unknown
    additionalComments: '',
    
    // Page 3 - Patient Contacts/Caregivers
    presentDuringVisitFamily: false,
    presentDuringVisitRepresentative: false,
    presentDuringVisitCaregivers: false,
    presentDuringVisitOther: false,
    presentDuringVisitOtherSpecify: '',
    rocContactInfoConfirmedWithPatient: false,
    rocChangesDocumented: false,
    rocNoChanges: false,
    
    doesPatientHaveRepresentative: '', // No, Yes
    representativeIsCourtDeclared: false,
    representativeIsPatientSelected: false,
    
    // Emergency Contact
    emergencyContactIsRepresentative: false,
    emergencyContactIsCaregiver: false,
    emergencyContactIsOther: false,
    emergencyContactOtherSpecify: '',
    emergencyContactName: '',
    emergencyContactRelationshipFamily: false,
    emergencyContactRelationshipFriend: false,
    emergencyContactRelationshipOther: false,
    emergencyContactRelationshipOtherSpecify: '',
    emergencyContactAddress: '',
    emergencyContactCity: '',
    emergencyContactState: '',
    emergencyContactZipCode: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    
    // Representative
    representativeName: '',
    representativeRelationshipFamily: false,
    representativeRelationshipFriend: false,
    representativeRelationshipOther: false,
    representativeRelationshipOtherSpecify: '',
    representativeAddress: '',
    representativeCity: '',
    representativeState: '',
    representativeZipCode: '',
    representativePhone: '',
    representativeEmail: '',
    
    // Primary Caregiver(s)
    primaryCaregiverNA: false,
    primaryCaregiverNoneAvailable: false,
    caregiver1Name: '',
    caregiver1RelationshipFamily: false,
    caregiver1RelationshipFriend: false,
    caregiver1RelationshipOther: false,
    caregiver1RelationshipOtherSpecify: '',
    caregiver1Address: '',
    caregiver1City: '',
    caregiver1State: '',
    caregiver1ZipCode: '',
    caregiver1Phone: '',
    caregiver1Email: '',
    
    caregiver2Name: '',
    caregiver2RelationshipFamily: false,
    caregiver2RelationshipFriend: false,
    caregiver2RelationshipOther: false,
    caregiver2RelationshipOtherSpecify: '',
    caregiver2Address: '',
    caregiver2City: '',
    caregiver2State: '',
    caregiver2ZipCode: '',
    caregiver2Phone: '',
    caregiver2Email: '',
    
    // Paid Service
    paidServiceOtherThanHomeHealth: '', // No, Yes
    paidServiceCompanyName: '',
    paidServicePhone: '',
    paidServiceContactName: '',
    
    // Critical Situation Contact
    criticalSituationContactAvailable: '', // No, Yes
    criticalSituationContactName: '',
    criticalSituationContactPhone: '',
    
    // Supportive Assistance
    priorAssistanceFrequency: '', // None received, At least daily, One to two times per week, Three or more times per week, Less often than weekly, Unknown
    assistanceTypeNone: false,
    assistanceTypeMeals: false,
    assistanceTypeADLs: false,
    assistanceTypeTransportation: false,
    assistanceTypeSupervisionSupport: false,
    assistanceTypeMedications: false,
    assistanceTypeHomeMaintenance: false,
    assistanceTypeOther: false,
    assistanceTypeOtherSpecify: '',
    caregiverWillingToAssist: '', // Yes, No, Unknown
    caregiverWillingToAssistExplain: '',
    caregiverNeedsTraining: '', // Yes, No, Unknown
    caregiverNeedsTrainingExplain: '',
    caregiverNoSetSchedule: false,
    
    // Caregiver Availability Schedule
    caregiverAvailSundayAM: '',
    caregiverAvailSundayPM: '',
    caregiverAvailSundayNight: '',
    caregiverAvailMondayAM: '',
    caregiverAvailMondayPM: '',
    caregiverAvailMondayNight: '',
    caregiverAvailTuesdayAM: '',
    caregiverAvailTuesdayPM: '',
    caregiverAvailTuesdayNight: '',
    caregiverAvailWednesdayAM: '',
    caregiverAvailWednesdayPM: '',
    caregiverAvailWednesdayNight: '',
    caregiverAvailThursdayAM: '',
    caregiverAvailThursdayPM: '',
    caregiverAvailThursdayNight: '',
    caregiverAvailFridayAM: '',
    caregiverAvailFridayPM: '',
    caregiverAvailFridayNight: '',
    caregiverAvailSaturdayAM: '',
    caregiverAvailSaturdayPM: '',
    caregiverAvailSaturdayNight: '',
    
    // Advance Directives
    hasLivingWill: '', // No, Yes
    advanceDirectivesDiscussedWithPatient: false,
    advanceDirectivesDiscussedWithFamily: false,
    advanceDirectivesDiscussedWithRepresentative: false,
    advanceDirectivesDiscussedWithCaregiver: false,
    hasAdvanceDirectiveOrders: '', // No, Yes
    advanceDirectiveNoCPR: false,
    advanceDirectiveDNI: false,
    advanceDirectiveDNR: false,
    advanceDirectiveNoArtificialNutrition: false,
    medicalPowerOfAttorneyName: '',
    medicalPowerOfAttorneyPhone: '',
    financialPowerOfAttorneyName: '',
    financialPowerOfAttorneyPhone: '',
    stateSpecificForms: '',
    copiesOnFileWithPCP: false,
    copiesOnFileWithOther: false,
    copiesOnFileWithOtherSpecify: '',
    advanceDirectivesComments: '',
    
    // Page 4 - Section A Continued
    // A1110 Language
    a1110PreferredLanguage: '',
    a1110NeedsInterpreter: '', // 0. No, 1. Yes, 9. Unable to determine
    
    // Language Barriers
    languageBarrierNoProblem: false,
    languageBarrierNeedsInterpreter: false,
    languageBarrierSignLanguage: false,
    languageBarrierSignLanguageType: '',
    languageBarrierAphasicReceptive: false,
    languageBarrierAphasicExpressive: false,
    
    // M0080 Discipline
    m0080Discipline: '', // 1. RN, 2. PT, 3. SLP/ST, 4. OT
    
    // M0090 Date Assessment Completed
    m0090DateAssessmentCompleted: '',
    
    // M0100 Reason for Assessment
    m0100ReasonForAssessment: '', // 1. Start of care - further visits planned, 3. Resumption of care
    
    // M0102 Physician-ordered SOC/ROC Date
    m0102PhysicianOrderedSOCROCDate: '',
    m0102NA: false, // NA – No specific SOC/ROC date ordered
    m0102NotInitiatedExplain: '',
    
    // M0104 Date of Referral
    m0104DateOfReferral: '',
    m0104NotInitiatedExplain: '',
    
    // M0110 Episode Timing
    m0110EpisodeTiming: '', // 1. Early, 2. Later, UK Unknown, NA Not Applicable
    
    // A1250 Transportation
    a1250TransportationA: false, // Yes, kept from medical appointments
    a1250TransportationB: false, // Yes, kept from non-medical
    a1250TransportationC: false, // No
    a1250TransportationX: false, // Patient unable to respond
    a1250TransportationY: false, // Patient declines to respond
    
    // Page 5 - Patient History
    primaryReasonForHomeHealthAdmission: '',
    
    // Pertinent History
    historyHypertension: false,
    historyHypotension: false,
    historyCardiac: false,
    historyRespiratory: false,
    historyOsteoporosis: false,
    historyFractures: false,
    historyCancer: false,
    historyCancerSite: '',
    historyInfection: false,
    historyImmunosuppressed: false,
    historyOpenWound: false,
    historyOpenWoundEtiology: '',
    historyFallsWithoutInjury: false,
    historyFallsWithInjury: false,
    historyHospitalizations: false,
    historyERVisits: false,
    historyRecentSurgeries: false,
    pertinentDetails: '',
    surgeryProcedureExpectedInFuture: '', // No, Yes
    surgeryProcedureExpectedExplain: '',
    
    // Vital Signs
    temperature: '',
    temperatureMethodOral: false,
    temperatureMethodTemporalForehead: false,
    temperatureMethodRectal: false,
    temperatureMethodAxillary: false,
    temperatureMethodTympanic: false,
    
    pulse: '',
    pulseApical: false,
    pulseBrachial: false,
    pulseRadial: false,
    pulseCarotid: false,
    pulseRegular: false,
    pulseIrregular: false,
    
    pulseOximetryAtRest: '',
    pulseOximetryAfterActivity: '',
    pulseOximetryActivitySpecify: '',
    
    respirations: '',
    respirationsRegular: false,
    respirationsIrregular: false,
    respirationsApneaPeriods: '',
    
    // Blood Pressure
    bloodPressureLeftSittingLying: '',
    bloodPressureRightSittingLying: '',
    bloodPressureLeftStanding: '',
    bloodPressureRightStanding: '',
    bloodPressureLeftAtRest: '',
    bloodPressureRightAtRest: '',
    bloodPressureLeftWithActivity: '',
    bloodPressureRightWithActivity: '',
    bloodPressureLeftPostActivity: '',
    bloodPressureRightPostActivity: '',
    bloodPressureObserved: false,
    bloodPressureReported: false,
    
    // Immunizations
    immunizationInfluenza: false,
    immunizationPneumonia: false,
    immunizationTetanus: false,
    immunizationShingles: false,
    immunizationHepatitisC: false,
    immunizationOther: false,
    immunizationOtherSpecify: '',
    immunizationNeeds: '',
    
    // COVID-19 Vaccination
    covidVaccineInitialSeries: false,
    covidVaccineBooster1: false,
    covidVaccineBooster2: false,
    covidVaccineBooster3: false,
    covidVaccineBooster4: false,
    covidVaccineBooster5: false,
    medicalRestrictionsImpactingImmunizations: '',
    
    // M1000 Inpatient Facilities
    m1000InpatientFacility1: false, // Long-term nursing facility (NF)
    m1000InpatientFacility2: false, // Skilled nursing facility (SNF/TCU)
    m1000InpatientFacility3: false, // Short-stay acute hospital (IPPS)
    m1000InpatientFacility4: false, // Long-term care hospital (LTCH)
    m1000InpatientFacility5: false, // Inpatient rehabilitation hospital or unit (IRF)
    m1000InpatientFacility6: false, // Psychiatric hospital or unit
    m1000InpatientFacility7: false, // Other
    m1000InpatientFacility7Specify: '',
    m1000InpatientFacilityNA: false, // Patient was not discharged from an inpatient facility
    nameOfInpatientFacilities: '',
    
    // M1005 Inpatient Discharge Date
    m1005InpatientDischargeDate: '',
    m1005UK: false, // UK - Unknown or Not Available
    noInpatientAdmission: false,
    
    // Continue with Section B, C, D, E, F, G, GG, H, I, J, K, M, N, O...
    // This would continue for hundreds more fields
    
    // Section B - Hearing, Speech, Vision (Page 6)
    // B0200 Hearing
    b0200Hearing: '', // 0-3 scale
    earsNoProblem: false,
    earsHOHRight: false,
    earsHOHLeft: false,
    earsDeaf: false,
    earsHearingAidRight: false,
    earsHearingAidLeft: false,
    earsVertigo: false,
    earsTinnitusRight: false,
    earsTinnitusLeft: false,
    earsCochlearImplant: false,
    earsCochlearTransplant: false,
    earsOther: false,
    earsOtherSpecify: '',
    hearingImpactFunctionSafety: '',
    hearingImpactExplain: '',
    hearingAffectsFunctionSafety: '',
    hearingAffectsFunctionSafetyExplain: '',
    
    // B1000 Vision
    b1000Vision: '', // 0-4 scale
    eyesNoProblem: false,
    eyesPERRLA: false,
    eyesPupilsUnequal: false,
    eyesGlasses: false,
    eyesContactsRight: false,
    eyesContactsLeft: false,
    eyesGlaucomaRight: false,
    eyesGlaucomaLeft: false,
    eyesCataractRight: false,
    eyesCataractLeft: false,
    eyesCataractSurgeryRightDate: '',
    eyesCataractSurgeryLeftDate: '',
    eyesBlurredVisionRight: false,
    eyesBlurredVisionLeft: false,
    eyesBlindRight: false,
    eyesBlindLeft: false,
    eyesProsthesisRight: false,
    eyesProsthesisLeft: false,
    eyesScleralIcterus: false,
    eyesDistortedVision: false,
    eyesInfections: false,
    eyesCataractsRight: false,
    eyesCataractsLeft: false,
    eyesOther: false,
    eyesOtherSpecify: '',
    visionImpactFunctionSafety: '',
    visionImpactExplain: '',
    visionAffectsFunctionSafety: '',
    visionAffectsFunctionSafetyExplain: '',
    
    // B1300 Health Literacy
    b1300HealthLiteracy: '', // 0-4, 7, 8
    educationalLevel: '',
    learningBarrierNoProblem: false,
    learningBarrierMentalHealth: false,
    learningBarrierMentalHealthDisability: false,
    learningBarrierPsychosocial: false,
    learningBarrierPhysical: false,
    learningBarrierFunctionalCognition: false,
    learningBarrierUnableToRead: false,
    learningBarrierUnableToWrite: false,
    educationalLevel: '',
    
    // Nose
    noseNoProblem: false,
    noseCongestion: false,
    noseEpistaxis: false,
    noseLossOfSmell: false,
    noseSinusProblem: false,
    noseOther: false,
    noseOtherSpecify: '',
    
    // Throat
    throatNoProblem: false,
    throatDifficultySwallowing: false,
    throatHoarseness: false,
    throatLesions: false,
    throatSoreThroat: false,
    throatOther: false,
    throatOtherSpecify: '',
    
    // Mouth
    mouthNoProblem: false,
    mouthMasses: false,
    mouthGingivitis: false,
    mouthUlcerations: false,
    mouthToothache: false,
    mouthLesions: false,
    mouthNoDentation: false,
    mouthDenturesUpper: false,
    mouthDenturesLower: false,
    mouthDenturesPartial: false,
    mouthTumors: false,
    mouthOther: false,
    mouthOtherSpecify: '',
    
    // Communication
    understandingVerbalContent: '',
    speechOralExpression: '',
    phoneAbilityAbleToDialMakeCall: false,
    phoneAbilityAbleToAnswerPhone: false,
    phoneAbilityMustUseAdaptivePhone: false,
    phoneAbilityNeedsHelper: false,
    phoneAbilityHelperMustMakeCall: false,
    phoneAbilityPatientDoesNotHavePhone: false,
    
    // Section C - Cognitive Patterns (Pages 7-8)
    // C0100
    c0100ShouldConductBIMS: '', // 0. No, 1. Yes
    
    // C0200 Repetition
    c0200RepetitionScore: '', // 0-3
    
    // C0300 Temporal Orientation
    c0300AYearScore: '', // 0-3
    c0300BMonthScore: '', // 0-2
    c0300CDayScore: '', // 0-1
    
    // C0400 Recall
    c0400ARecallSock: '', // 0-2
    c0400BRecallBlue: '', // 0-2
    c0400CRecallBed: '', // 0-2
    
    // C0500 BIMS Summary
    c0500BIMSSummaryScore: '', // 00-15 or 99
    
    // C1310 Signs and Symptoms of Delirium
    c1310AAcuteOnset: '', // 0. No, 1. Yes
    c1310BInattention: '', // 0, 1, 2
    c1310CDisorganizedThinking: '', // 0, 1, 2
    c1310DAlteredConsciousness: '', // 0, 1, 2
    
    // M1700 Cognitive Functioning
    m1700CognitiveFunctioning: '', // 0-4
    
    // M1710 When Confused
    m1710WhenConfused: '', // 0-4, NA
    
    // M1720 When Anxious
    m1720WhenAnxious: '', // 0-3, NA
    
    // Neurological Status
    neurologicalNoProblem: false,
    neurologicalDisorderType: '',
    historyTraumaticBrainInjury: false,
    traumaticBrainInjuryDate: '',
    traumaticBrainInjuryType: '',
    historyHeadaches: false,
    headachesLastDate: '',
    headachesType: '',
    historySeizures: false,
    seizuresLastDate: '',
    seizuresType: '',
    tremorsAtRest: false,
    tremorsWithVoluntaryMovement: false,
    tremorsContinuous: false,
    spasms: false,
    spasmsLocation: '',
    dominantSideRight: false,
    dominantSideLeft: false,
    hemiplegiaRight: false,
    hemiplegiaLeft: false,
    paraplegia: false,
    quadriplegiaTetraplegia: false,
    neurologicalAffectsFunctionSafety: '',
    neurologicalAffectsFunctionSafetyExplain: '',
    
    // Section D - Mood (Page 9)
    // D0150 PHQ-2 to PHQ-9
    d0150ALittleInterestPresence: '', // 0, 1, 9
    d0150ALittleInterestFrequency: '', // 0-3
    d0150BFeelingDownPresence: '', // 0, 1, 9
    d0150BFeelingDownFrequency: '', // 0-3
    d0150CTroubleSleepingPresence: '', // 0, 1, 9
    d0150CTroubleSleepingFrequency: '', // 0-3
    d0150DFeelingTiredPresence: '', // 0, 1, 9
    d0150DFeelingTiredFrequency: '', // 0-3
    d0150EPoorAppetitePresence: '', // 0, 1, 9
    d0150EPoorAppetiteFrequency: '', // 0-3
    d0150FFeelingBadPresence: '', // 0, 1, 9
    d0150FFeelingBadFrequency: '', // 0-3
    d0150GTroubleConcentratingPresence: '', // 0, 1, 9
    d0150GTroubleConcentratingFrequency: '', // 0-3
    d0150HMovingSpeakingSlowlyPresence: '', // 0, 1, 9
    d0150HMovingSpeakingSlowlyFrequency: '', // 0-3
    d0150IThoughtsBetterOffDeadPresence: '', // 0, 1, 9
    d0150IThoughtsBetterOffDeadFrequency: '', // 0-3
    
    // D0160 Total Severity Score
    d0160TotalSeverityScore: '', // 00-27 or 99
    
    // D0700 Social Isolation
    d0700SocialIsolation: '', // 0-4, 7, 8
    
    // Section E - Behavior (Pages 9-10)
    // M1740 Cognitive, Behavioral, and Psychiatric Symptoms
    m1740MemoryDeficit: false,
    m1740ImpairedDecisionMaking: false,
    m1740VerbalDisruption: false,
    m1740PhysicalAggression: false,
    m1740DisruptiveInfantileBehavior: false,
    m1740DelusionalHallucinatoryParanoid: false,
    m1740NoneOfAbove: false,
    
    // M1745 Frequency of Disruptive Behavior
    m1745DisruptiveBehaviorFrequency: '', // 0-5
    
    // Mental Status
    suddenAcuteChangeMentalStatus: '', // No, Yes
    changeCoincidedWithSomethingElse: '', // No, Yes
    changeCoincidedExplain: '',
    mentalStatusChangesReportedByPatient: false,
    mentalStatusChangesReportedByCaregiver: false,
    mentalStatusChangesReportedByRepresentative: false,
    mentalStatusChangesReportedByOther: false,
    mentalStatusChangesReportedByOtherSpecify: '',
    spiritualImplicationsImpactCare: false,
    culturalImplicationsImpactCare: false,
    spiritualCulturalExplain: '',
    spiritualResource: '',
    
    maritalStatusSingle: false,
    maritalStatusMarried: false,
    maritalStatusDivorced: false,
    maritalStatusWidower: false,
    maritalStatusPhone: '',
    
    // Feelings/Emotions
    feelingsNA: false,
    feelingsAngry: false,
    feelingsFear: false,
    feelingsSadness: false,
    feelingsDiscouraged: false,
    feelingsLonely: false,
    feelingsDepressed: false,
    feelingsHelpless: false,
    feelingsContent: false,
    feelingsHappy: false,
    feelingsHopeful: false,
    feelingsMotivated: false,
    feelingsOther: false,
    feelingsOtherSpecify: '',
    
    // Inability to Cope
    inabilityToCopeLackOfMotivation: false,
    inabilityToCopeUnrealisticExpectations: false,
    inabilityToCopeDenialOfProblems: false,
    
    // Evidence of Abuse/Neglect/Exploitation
    evidenceAbuse: false,
    evidenceNeglect: false,
    evidenceExploitation: false,
    evidencePotential: false,
    evidenceActual: false,
    evidenceVerbal: false,
    evidenceEmotional: false,
    evidencePhysical: false,
    evidenceFinancial: false,
    evidenceNA: false,
    
    mswReferralMade: '', // Yes, No
    otherIntervention: '',
    psychosocialBarriersAffectCare: '', // Yes, No
    psychosocialBarriersExplain: '',
    
    // This continues for all remaining sections...
    // Due to length, I'll include a summary of remaining sections
    
    // Section F - Preferences for Customary Routine Activities (Pages 10-12)
    // M1100 Patient Living Situation
    m1100PatientLivingSituation: '', // 01-15
    
    // M2102 Types and Sources of Assistance
    m2102fSupervisionSafety: '', // 0-4
    
    // Care Preferences/Personal Goals
    carePreferencesCommunicatedBy: '',
    carePreferencesInvolveHomeHealth: '', // Yes, No
    carePreferencesList: '',
    personalGoalsCommunicatedBy: '',
    personalGoalsDiscussed: '', // No, Yes
    personalGoalsRealistic: false,
    personalGoalsNeedModification: false,
    personalGoalsActionsIdentified: false,
    personalGoalsWrittenMeasurable: false,
    personalGoalsInformedAgreed: false,
    personalGoalsList: '',
    rocNoGoalChanges: false,
    rocGoalsChanged: false,
    
    // Strengths/Limitations
    patientStrengths: '',
    patientLimitations: '',
    limitationsAffectSafetyProgress: '', // No, Yes
    limitationsAffectSafetyProgressExplain: '',
    indicationsForHomeHealthAides: '', // No, Yes, Refused
    aidesOrderObtained: '', // No, Yes
    aidesReasonForNeed: '',
    
    // Living Arrangements/Supportive Assistance
    safetyBleedingPrecautions: false,
    safetyO2Precautions: false,
    safetySeizurePrecautions: false,
    safetyFallPrecautions: false,
    safetyAspirationPrecautions: false,
    safetySiderailsUp: false,
    safetyElevateHeadOfBed: false,
    safety24HrSupervision: false,
    safetyClearPathways: false,
    safetyLockWCWithTransfers: false,
    safetyInfectionControlMeasures: false,
    safetyWalkerCane: false,
    safetyOther: false,
    safetyOtherSpecify: '',
    needForFallRiskPlan: '', // Yes, No
    safetyPlansIndicated: '', // No, Yes
    safetyComments: '',
    
    // Instructions/Materials Provided
    instructionsRightsResponsibilities: false,
    instructionsHIPAA: false,
    instructionsAgencyPhoneNumber: false,
    instructionsBasicHomeSafety: false,
    instructionsMedicationRegimen: false,
    instructionsStateHotlineNumber: false,
    instructionsOASISPrivacyNotice: false,
    instructionsWhenToContactPhysician: false,
    instructionsDisease: false,
    instructionsDiseaseSpecify: '',
    instructionsAdminContactInfo: false,
    instructionsAdvanceDirectives: false,
    instructionsDNR: false,
    instructionsEmergencyPlanning: false,
    instructionsStandardPrecautions: false,
    instructionsCopyToRepresentative: false,
    instructionsOther: false,
    instructionsOtherSpecify: '',
    
    // Emergency Preparedness
    emergencyPriorityCodeAssigned: false,
    emergencyContactNumbersObtained: false,
    emergencyHHAPlansDiscussed: false,
    emergencyPatientSpecificOptionsDiscussed: false,
    emergencyIndividualizedPlanDiscussed: false,
    emergencyEducationalMaterialsProvided: false,
    emergencyEvacuationRoutesList: false,
    emergencyWrittenMaterialsGivenTo: '',
    emergencyComments: '',
    
    // Section G - Functional Status (Pages 13-14)
    // M1800 Grooming
    m1800Grooming: '', // 0-3
    
    // M1810 Dress Upper Body
    m1810DressUpperBody: '', // 0-3
    
    // M1820 Dress Lower Body
    m1820DressLowerBody: '', // 0-3
    
    // M1830 Bathing
    m1830Bathing: '', // 0-6
    
    // M1840 Toilet Transferring
    m1840ToiletTransferring: '', // 0-4
    
    // M1845 Toileting Hygiene
    m1845ToiletingHygiene: '', // 0-3
    
    // M1850 Transferring
    m1850Transferring: '', // 0-5
    
    // M1860 Ambulation/Locomotion
    m1860AmbulationLocomotion: '', // 0-6
    
    // Activities Permitted
    activitiesNoRestrictions: false,
    activitiesBathroomPrivileges: false,
    activitiesUpAsTolerated: false,
    activitiesTransferBedChair: false,
    activitiesExercisesPrescribed: false,
    activitiesCompleteBedrest: false,
    activitiesPartialWeightBearing: false,
    activitiesIndependentInHome: false,
    activitiesCrutches: false,
    activitiesCane: false,
    activitiesWheelchair: false,
    activitiesWalker: false,
    activitiesOther1: '',
    activitiesOther2: '',
    activitiesOther3: '',
    activitiesPlanComments: '',
    
    // Section GG - Functional Abilities and Goals (Pages 14-17)
    // GG0100 Prior Functioning
    gg0100ASelfCare: '', // 1-3, 8, 9
    gg0100BIndoorMobility: '', // 1-3, 8, 9
    gg0100CStairs: '', // 1-3, 8, 9
    gg0100DFunctionalCognition: '', // 1-3, 8, 9
    
    // GG0110 Prior Device Use
    gg0110AManualWheelchair: false,
    gg0110BMotorizedWheelchairScooter: false,
    gg0110CMechanicalLift: false,
    gg0110DWalker: false,
    gg0110EOrthoticsProsthetics: false,
    gg0110ZNoneOfAbove: false,
    
    // GG0130 Self-Care (SOC/ROC Performance and Discharge Goal)
    gg0130AEatingSOCROC: '',
    gg0130AEatingDischargeGoal: '',
    gg0130BOralHygieneSOCROC: '',
    gg0130BOralHygieneDischargeGoal: '',
    gg0130CToiletingHygieneSOCROC: '',
    gg0130CToiletingHygieneDischargeGoal: '',
    gg0130EShowerBatheSOCROC: '',
    gg0130EShowerBatheDischargeGoal: '',
    gg0130FUpperBodyDressingSOCROC: '',
    gg0130FUpperBodyDressingDischargeGoal: '',
    gg0130GLowerBodyDressingSOCROC: '',
    gg0130GLowerBodyDressingDischargeGoal: '',
    gg0130HFootwearSOCROC: '',
    gg0130HFootwearDischargeGoal: '',
    
    // GG0170 Mobility
    gg0170ARollLeftRightSOCROC: '',
    gg0170ARollLeftRightDischargeGoal: '',
    gg0170BSitToLyingSOCROC: '',
    gg0170BSitToLyingDischargeGoal: '',
    gg0170CLyingToSittingSOCROC: '',
    gg0170CLyingToSittingDischargeGoal: '',
    gg0170DSitToStandSOCROC: '',
    gg0170DSitToStandDischargeGoal: '',
    gg0170EChairBedTransferSOCROC: '',
    gg0170EChairBedTransferDischargeGoal: '',
    gg0170FToiletTransferSOCROC: '',
    gg0170FToiletTransferDischargeGoal: '',
    gg0170GCarTransferSOCROC: '',
    gg0170GCarTransferDischargeGoal: '',
    gg0170IWalk10FeetSOCROC: '',
    gg0170IWalk10FeetDischargeGoal: '',
    gg0170JWalk50Feet2TurnsSOCROC: '',
    gg0170JWalk50Feet2TurnsDischargeGoal: '',
    gg0170KWalk150FeetSOCROC: '',
    gg0170KWalk150FeetDischargeGoal: '',
    gg0170LWalk10FeetUnevenSOCROC: '',
    gg0170LWalk10FeetUnevenDischargeGoal: '',
    gg0170M1StepCurbSOCROC: '',
    gg0170M1StepCurbDischargeGoal: '',
    gg0170N4StepsSOCROC: '',
    gg0170N4StepsDischargeGoal: '',
    gg0170O12StepsSOCROC: '',
    gg0170O12StepsDischargeGoal: '',
    gg0170PPickingUpObjectSOCROC: '',
    gg0170PPickingUpObjectDischargeGoal: '',
    gg0170QUsesWheelchair: '', // 0. No, 1. Yes
    gg0170RWheel50Feet2TurnsSOCROC: '',
    gg0170RWheel50Feet2TurnsDischargeGoal: '',
    gg0170RR1WheelchairType: '', // 1. Manual, 2. Motorized
    gg0170SWheel150FeetSOCROC: '',
    gg0170SWheel150FeetDischargeGoal: '',
    gg0170SS1WheelchairType: '', // 1. Manual, 2. Motorized
    
    // Functional Limitations
    functionalLimitationAmputation: false,
    functionalLimitationBowelBladder: false,
    functionalLimitationContracture: false,
    functionalLimitationHearing: false,
    functionalLimitationEndurance: false,
    functionalLimitationAmbulation: false,
    functionalLimitationLegallyBlind: false,
    functionalLimitationDyspneaMinimalExertion: false,
    functionalLimitationSpeech: false,
    functionalLimitationOther1: '',
    functionalLimitationOther2: '',
    priorTransferAbility: '',
    priorSocialActivityLevel: '',
    
    // Musculoskeletal
    musculoskeletalNoProblem: false,
    pastProblemsJointsMusclesBones: '', // No, Yes
    pastProblemsWhatHappened: '',
    pastProblemsTreatmentReceived: '',
    pastProblemsResidualProblems: '', // No, Yes
    pastProblemsResidualWhatHappened: '',
    hasAmputation: '', // No, Yes
    amputationBelowKneeRight: false,
    amputationBelowKneeLeft: false,
    amputationAboveKneeRight: false,
    amputationAboveKneeLeft: false,
    amputationUpperExtremityRight: false,
    amputationUpperExtremityLeft: false,
    amputationOther: '',
    postureExaggeratedForwardCurve: false,
    postureRoundedUpperBack: false,
    postureSShaped: false,
    postureNAPatientCantStand: false,
    postureLimitsActivities: '', // No, Yes
    postureConditionExplain: '',
    painAssociatedWithJoints: false,
    painAssociatedWithMuscles: false,
    painAssociatedWithBones: false,
    hasTingling: false,
    hasNumbness: false,
    hasSwelling: false,
    hasContractures: false,
    hasWeaknessUE: false,
    hasWeaknessLE: false,
    hasAtrophy: false,
    hasDecreasedROM: false,
    motorChanges: '', // No, Yes
    motorChangesFine: false,
    motorChangesGross: false,
    handGripsEqual: false,
    handGripsUnequal: false,
    handGripStrongRight: false,
    handGripWeakRight: false,
    handGripStrongLeft: false,
    handGripWeakLeft: false,
    
    // Fall Risk Assessment (MAHC-10)
    fallRiskAge65Plus: false,
    fallRiskDiagnosis3OrMore: false,
    fallRiskPriorHistoryFalls: false,
    fallRiskIncontinence: false,
    fallRiskVisualImpairment: false,
    fallRiskImpairedFunctionalMobility: false,
    fallRiskEnvironmentalHazards: false,
    fallRiskPolyPharmacy: false,
    fallRiskPainAffectingFunction: false,
    fallRiskCognitiveImpairment: false,
    fallRiskTotalScore: '',
    
    // Section H - Bladder and Bowel (Pages 18-19)
    // Urinary Elimination
    urinaryNoProblem: false,
    urinaryDisorderType: '',
    urinaryUrgency: false,
    urinaryFrequency: false,
    urinaryBurning: false,
    urinaryPain: false,
    urinaryHesitancy: false,
    urinaryIncreasedAtNight: false,
    urinaryDecreased: false,
    urinaryColorYellowStraw: false,
    urinaryColorAmber: false,
    urinaryColorBrownGray: false,
    urinaryColorPinkRedTinged: false,
    urinaryColorOther: '',
    urinaryClarityClear: false,
    urinaryClarityCloudy: false,
    urinaryClaritySediment: false,
    urinaryClarityMucous: false,
    urinaryOdor: '', // No, Yes
    urinaryIncontinenceWhen: '',
    urinaryIncontinenceProducts: '',
    
    // Urinary Catheter
    urinaryCatheterNA: false,
    urinaryCatheterType: '',
    urinaryCatheterDateLastChanged: '',
    urinaryCatheterChangedThisVisit: false,
    urinaryCatheterChangedThisVisitSize: '',
    urinaryCatheterInsertedThisVisit: false,
    urinaryCatheterInsertedThisVisitSize: '',
    urinaryCatheterSingleBalloon: false,
    urinaryCatheterDoubleBalloon: false,
    urinaryCatheterSingleBalloonInflatedWith: '',
    urinaryCatheterSecondBalloonInflatedWith: '',
    urinaryCatheterWithoutDifficulty: false,
    urinaryCatheterWithDifficulty: false,
    urinaryCatheterWithDifficultyExplain: '',
    urinaryCatheterIrrigationSolutionType: '',
    urinaryCatheterIrrigationAmount: '',
    urinaryCatheterIrrigationFrequency: '',
    urinaryCatheterPatientToleratedWell: '', // No, Yes
    urinaryCatheterSuprapubic: false,
    urostomySiteDescription: '',
    ostomyCareBy: '',
    
    // M1600 UTI
    m1600UTI: '', // 0. No, 1. Yes, NA, UK
    
    // M1610 Urinary Incontinence or Catheter
    m1610UrinaryIncontinenceCatheter: '', // 0, 1, 2
    
    // Bowel Elimination
    bowelNoProblem: false,
    bowelDisorderType: '',
    bowelFrequencyOfStools: '',
    bowelRegimenProgram: '',
    bowelLaxativeEnemaFrequency: '',
    bowelOther: '',
    bowelConstipation: false,
    bowelDiarrhea: false,
    bowelHemorrhoids: false,
    bowelLastBM: '',
    bowelInvoluntaryIncontinenceDetails: '',
    bowelSoundsRU: '',
    bowelSoundsLU: '',
    bowelSoundsRL: '',
    bowelSoundsLL: '',
    bowelIncontinenceProducts: '',
    
    // Abdomen
    abdomenNoProblem: false,
    abdomenTenderness: false,
    abdomenPain: false,
    hasIleostomy: false,
    hasColostomy: false,
    ostomySiteDescription: '',
    abdomenDistention: false,
    abdomenHard: false,
    abdomenSoft: false,
    abdominalGirth: '',
    bowelOstomyCareBy: '',
    abdomenOther: '',
    
    // M1620 Bowel Incontinence Frequency
    m1620BowelIncontinenceFrequency: '', // 0-5, NA, UK
    
    // Genitalia
    genitaliaNoProblem: false,
    genitaliaNotAssessed: false,
    genitaliaOther: '',
    genitaliaDischarge: '',
    genitaliaLesions: false,
    genitaliaBlisters: false,
    genitaliaMasses: false,
    genitaliaCysts: false,
    genitaliaInflammation: false,
    genitaliaSurgicalAlterationFemaleToMale: false,
    genitaliaSurgicalAlterationMaleToFemale: false,
    prostateProblemBPH: false,
    prostateProblemTURP: false,
    prostateProblemTURPDate: '',
    selfTesticularExam: false,
    selfTesticularExamFrequency: '',
    selfTesticularExamDateLast: '',
    menopause: false,
    hysterectomy: false,
    hysterectomyDate: '',
    dateLastPAP: '',
    papResults: '',
    breastSelfExam: false,
    breastSelfExamFrequency: '',
    breastSelfExamDateLast: '',
    nippleDischargeRight: false,
    nippleDischargeRightDate: '',
    nippleDischargeLeft: false,
    nippleDischargeLeftDate: '',
    
    // M1630 Ostomy for Bowel Elimination
    m1630OstomyForBowel: '', // 0, 1, 2
    
    eliminationDisorderAffectsFunctionSafety: '', // No, Yes
    eliminationDisorderAffectsFunctionSafetyExplain: '',
    
    // Section I - Active Diagnoses (Pages 20-21)
    // M1021 Primary Diagnosis
    m1021PrimaryDiagnosis: '',
    m1021PrimaryDiagnosisICD10: '',
    
    // M1023 Other Diagnoses
    m1023DiagnosisB: '',
    m1023DiagnosisBICD10: '',
    m1023DiagnosisC: '',
    m1023DiagnosisCICD10: '',
    m1023DiagnosisD: '',
    m1023DiagnosisDICD10: '',
    m1023DiagnosisE: '',
    m1023DiagnosisEICD10: '',
    m1023DiagnosisF: '',
    m1023DiagnosisFICD10: '',
    m1023DiagnosisG: '',
    m1023DiagnosisGICD10: '',
    m1023DiagnosisH: '',
    m1023DiagnosisHICD10: '',
    m1023DiagnosisI: '',
    m1023DiagnosisIICD10: '',
    m1023DiagnosisJ: '',
    m1023DiagnosisJICD10: '',
    m1023DiagnosisK: '',
    m1023DiagnosisKICD10: '',
    m1023DiagnosisL: '',
    m1023DiagnosisLICD10: '',
    // Continue m through y...
    
    // Pertinent Surgical Procedures
    surgicalProceduresNA: false,
    surgicalProcedure1: '',
    surgicalProcedure1Date: '',
    surgicalProcedure2: '',
    surgicalProcedure2Date: '',
    surgicalProcedure3: '',
    surgicalProcedure3Date: '',
    
    // M1028 Comorbidities
    m1028PVD_PAD: false,
    m1028DM: false,
    m1028NoneOfAbove: false,
    
    // Endocrine/Hematology
    endocrineNoProblem: false,
    diabetesType1: false,
    diabetesType2: false,
    diabetesOther: '',
    diabetesDateOfOnset: '',
    diabeticDiet: false,
    diabetesAdministeredByPatient: false,
    diabetesAdministeredByCaregiver: false,
    diabetesAdministeredByNurse: false,
    diabetesAdministeredByFamily: false,
    diabetesAdministeredByOther: '',
    diabetesOralMedication: false,
    diabetesInjectableMedication: false,
    diabetesMedicationStartDate: '',
    symptomsHyperglycemiaIncreasedUrination: false,
    symptomsHyperglycemiaIncreasedThirst: false,
    symptomsHypoglycemiaSweats: false,
    symptomsHypoglycemiaIncreasedHunger: false,
    symptomsHypoglycemiaWeak: false,
    symptomsHypoglycemiaFaint: false,
    symptomsHypoglycemiaStupor: false,
    a1cPercent: '',
    a1cPatientReported: false,
    a1cLabSlip: false,
    a1cDate: '',
    bloodSugarFBS: false,
    bloodSugarBeforeMeal: false,
    bloodSugarAfterMeal: false,
    bloodSugarRandom: false,
    bloodSugarHS: false,
    bloodSugarRanges: '',
    bloodSugarDate: '',
    bloodSugarReportedByPatient: false,
    bloodSugarReportedByCaregiver: false,
    bloodSugarReportedByFamily: false,
    bloodSugarMonitoredByPatient: false,
    bloodSugarMonitoredByCaregiver: false,
    bloodSugarMonitoredByFamily: false,
    bloodSugarMonitoredByNurse: false,
    bloodSugarMonitoredByOther: '',
    bloodSugarMonitoringFrequency: '',
    diseaseManagementProblems: '',
    otherEndocrineHematologyIssues: '',
    glucometerCompetency: '',
    
    // Section J - Health Conditions (Pages 22-24)
    // M1033 Risk for Hospitalization
    m1033HistoryFalls: false,
    m1033MultipleHospitalizations: false,
    m1033UnintentionalWeightLoss: false,
    m1033MultipleEDVisits: false,
    m1033DeclineMentalEmotionalBehavioral: false,
    m1033DifficultyComplyingMedicalInstructions: false,
    m1033Taking5OrMoreMedications: false,
    m1033ReportsExhaustion: false,
    m1033OtherRisks: false,
    m1033NoneOfAbove: false,
    
    // Pain Assessment
    isExperiencingPain: '', // No, Yes
    painNonVerbalsDiaphoresis: false,
    painNonVerbalsGrimacing: false,
    painNonVerbalsMoaning: false,
    painNonVerbalsCrying: false,
    painNonVerbalsGuarding: false,
    painNonVerbalsIrritability: false,
    painNonVerbalsAnger: false,
    painNonVerbalsTense: false,
    painNonVerbalsRestlessness: false,
    painNonVerbalsVitalSignsChange: false,
    painNonVerbalsOther: '',
    painSelfAssessment: false,
    painImplications: '',
    painUnableToCommunicate: false,
    painTolerableLevel: '',
    painAssessmentUsed: '',
    painAssessmentWongBaker: false,
    painAssessmentPAINAD: false,
    
    // Pain Sites
    painSite1Location: '',
    painSite1Onset: '',
    painSite1PresentLevel: '',
    painSite1WorstLevel: '',
    painSite1BestLevel: '',
    painSite1Description: '',
    painSite2Location: '',
    painSite2Onset: '',
    painSite2PresentLevel: '',
    painSite2WorstLevel: '',
    painSite2BestLevel: '',
    painSite2Description: '',
    painSite3Location: '',
    painSite3Onset: '',
    painSite3PresentLevel: '',
    painSite3WorstLevel: '',
    painSite3BestLevel: '',
    painSite3Description: '',
    
    // PAINAD Scale
    painadBreathing: '', // 0, 1, 2
    painadNegativeVocalization: '', // 0, 1, 2
    painadFacialExpression: '', // 0, 1, 2
    painadBodyLanguage: '', // 0, 1, 2
    painadConsolability: '', // 0, 1, 2
    painadTotal: '',
    
    // J0510-J0530 Pain Effects
    j0510PainEffectOnSleep: '', // 0-4, 8
    j0520PainInterferenceTherapy: '', // 0-4, 8
    j0530PainInterferenceDayToDay: '', // 1-4, 8
    
    // Pain Activities Affected
    painAffectsFunctionalCognition: false,
    painAffectsTransfers: false,
    painAffectsHygiene: false,
    painAffectsAmbulation: false,
    painAffectsDressingUpper: false,
    painAffectsDressingLower: false,
    painAffectsUndressingUpper: false,
    painAffectsUndressingLower: false,
    painAffectsStairsAscend: false,
    painAffectsStairsDescend: false,
    painAffectsEating: false,
    painAffectsToileting: false,
    painAffectsAppetite: false,
    painAffectsPositionalChanges: false,
    painAffectsOther: '',
    
    painAffectsFunctionSafety: '', // Yes, No
    painAffectsFunctionSafetyExplain: '',
    painWorsensWithMovement: false,
    painWorsensWithAmbulation: false,
    painWorsensWithImmobility: false,
    painWorsensWithOther: '',
    painPatternExists: '', // No, Yes
    painPatternExplain: '',
    painBetterWithHeat: false,
    painBetterWithIce: false,
    painBetterWithMassage: false,
    painBetterWithRepositioning: false,
    painBetterWithRest: false,
    painBetterWithRelaxation: false,
    painBetterWithMedication: false,
    painBetterWithDiversion: false,
    painBetterWithOther: '',
    breakthroughMedicationFrequency: '',
    painRadiates: '', // No, Occasionally, Continuously, Intermittent
    currentPainControlAdequate: '', // No, Yes
    
    // Pain Medications Classification
    painMedAnalgesics: false,
    painMedCorticosteroid: false,
    painMedAntianxiety: false,
    painMedDMARD: false,
    painMedAnticonvulsant: false,
    painMedLocalAnesthetics: false,
    painMedAntidepressant: false,
    painMedNarcotic: false,
    painMedAntimigraine: false,
    painMedNSAIDS: false,
    painMedBiologic: false,
    painMedSalicylate: false,
    painComment: '',
    
    // Cardiopulmonary (Page 24)
    cardiopulmonaryNoProblem: false,
    cardiopulmonaryDisorderType: '',
    breathSoundsAnteriorLeft: '',
    breathSoundsAnteriorRight: '',
    breathSoundsPosteriorRightUpper: '',
    breathSoundsPosteriorLeftUpper: '',
    breathSoundsPosteriorRightLower: '',
    breathSoundsPosteriorLeftLower: '',
    laboredBreathing: false,
    nonSmoker: false,
    everSmokedInPast: '', // Yes, No
    dateLastSmoked: '',
    smokerFrequencyDaily: false,
    smokerFrequencyOccasional: false,
    smokerFrequencyVeryOccasional: false,
    smokerDailyAmount: '',
    respiratoryOxygenIntermittent: false,
    respiratoryOxygenContinuous: false,
    respiratoryVentilatorContinuous: false,
    respiratoryVentilatorAtNight: false,
    positiveAirwayPressureContinuous: false,
    positiveAirwayPressureBiLevel: false,
    oxygenLPM: '',
    oxygenViaCannula: false,
    oxygenViaMask: false,
    oxygenViaTrach: false,
    oxygenSaturation: '',
    trachSizeType: '',
    trachManagedByPatient: false,
    trachManagedByRN: false,
    trachManagedByCaregiver: false,
    trachManagedByFamily: false,
    intermittentTreatments: '', // No, Yes
    intermittentTreatmentsExplain: '',
    cough: '', // No, Yes
    coughProductive: false,
    coughNonProductive: false,
    coughDescription: '',
    positioningForBreathing: '', // No, Yes
    positioningForBreathingDescribe: '',
    
    heartSoundsRegular: false,
    heartSoundsIrregular: false,
    hasPacemaker: false,
    pacemakerDate: '',
    pacemakerLastDateChecked: '',
    nailBedsColor: '',
    
    // Circulation/Edema
    edemaPedalRightNA: false,
    edemaPedalRightNonPitting: false,
    edemaPedalRightPitting1: false,
    edemaPedalRightPitting2: false,
    edemaPedalRightPitting3: false,
    edemaPedalRightPitting4: false,
    edemaPedalRightCapillaryRefillLessThan3: false,
    edemaPedalRightCapillaryRefillMoreThan3: false,
    edemaPedalLeftNA: false,
    edemaPedalLeftNonPitting: false,
    edemaPedalLeftPitting1: false,
    edemaPedalLeftPitting2: false,
    edemaPedalLeftPitting3: false,
    edemaPedalLeftPitting4: false,
    edemaPedalLeftCapillaryRefillLessThan3: false,
    edemaPedalLeftCapillaryRefillMoreThan3: false,
    extremityCrampLocation: '',
    painAtRest: '',
    dependentEdema: '',
    cardiopulmonaryDiseaseManagementProblems: '',
    
    // M1400 Dyspnea
    m1400Dyspnea: '', // 0-4
    shortnessOfBreathAssessed: false,
    shortnessOfBreathReported: false,
    shortnessOfBreathExplain: '',
    sobAffectsFunctionSafety: '', // No, Yes
    sobAffectsFunctionSafetyExplain: '',
    
    // Section K - Swallowing/Nutritional Status (Pages 25-26)
    // M1060 Height and Weight
    m1060HeightInches: '',
    m1060WeightPounds: '',
    unableToWeighThisVisit: false,
    weightWithin30DaysFromDocumentation: '',
    weightPatientCaregiverReported: '',
    weightChangesGain: false,
    weightChangesLoss: false,
    weightChangesPounds: '',
    weightChangesTimeframe: '', // week, month, year
    weightChangesIntentional: false,
    weightChangesUnintentional: false,
    generalAppearanceUnderweight: false,
    generalAppearanceAverage: false,
    generalAppearanceOverweight: false,
    generalAppearanceObese: false,
    
    // Nutritional Status
    nutritionalNoProblem: false,
    dietGeneralNAS: false,
    dietNPO: false,
    dietControlledCarbohydrate: false,
    dietRenal: false,
    dietOther: false,
    dietOtherSpecify: '',
    nutritionalIncreaseFluids: false,
    nutritionalIncreaseFluidsAmount: '',
    nutritionalRestrictFluids: false,
    nutritionalRestrictFluidsAmount: '',
    appetiteGood: false,
    appetiteFair: false,
    appetitePoor: false,
    hasNausea: false,
    hasVomiting: false,
    vomitingFrequency: '',
    vomitingAmount: '',
    hasHeartburn: false,
    nutritionalOther: '',
    foodEnvironmentalAllergiesNA: false,
    foodEnvironmentalAllergies: '',
    alcoholUse: '', // No, Yes
    alcoholUseFrequency: '', // Daily, Occasional, Very Occasional
    alcoholUseDailyAmount: '',
    
    // Nutritional Screening
    nutritionalScreeningIllnessChangedFood: false,
    nutritionalScreeningFewerThan2Meals: false,
    nutritionalScreeningFewFruitsVegetables: false,
    nutritionalScreening3OrMoreDrinks: false,
    nutritionalScreeningToothMouthProblems: false,
    nutritionalScreeningNotEnoughMoney: false,
    nutritionalScreeningEatsAlone: false,
    nutritionalScreening3OrMoreDrugs: false,
    nutritionalScreeningWeightChange: false,
    nutritionalScreeningNotAbleToShopCookFeed: false,
    nutritionalScreeningTotalScore: '',
    nutritionalAtRiskIntervention: '',
    nutritionalSafetyRisk: '',
    
    // Ability to Prepare Light Meals
    abilityToPrepareLightMeals: '', // 0-3
    
    // Enteral Feedings
    enteralFeedingsNA: false,
    enteralNasogastric: false,
    enteralGastrostomy: false,
    enteralJejunostomy: false,
    enteralOther: '',
    enteralPumpType: '',
    enteralFeedingsBolus: false,
    enteralFeedingsContinuous: false,
    enteralFeedingsTypeAmountRate: '',
    enteralFlushProtocol: '',
    enteralPerformedByPatient: false,
    enteralPerformedByCaregiver: false,
    enteralPerformedByFamily: false,
    enteralPerformedByOther: '',
    enteralDressingSiteCare: '',
    enteralInterventionsInstructionsComments: '',
    
    // K0520 Nutritional Approaches
    k0520OnAdmissionParenteralIV: false,
    k0520OnAdmissionFeedingTube: false,
    k0520OnAdmissionMechanicallyAlteredDiet: false,
    k0520OnAdmissionTherapeuticDiet: false,
    k0520OnAdmissionNoneOfAbove: false,
    
    // M1870 Feeding or Eating
    m1870FeedingOrEating: '', // 0-5
    
    additionalCommentsNutrition: '',
    
    // Section M - Skin Conditions (Pages 27-30)
    // Integumentary Status
    integumentaryNoProblem: false,
    skinTurgorGood: false,
    skinTurgorPoor: false,
    skinItch: false,
    skinRash: false,
    skinDry: false,
    skinScaling: false,
    skinRedness: false,
    skinBruises: false,
    skinEcchymosis: false,
    skinPallor: false,
    skinJaundice: false,
    skinWeeping: false,
    skinOther: '',
    
    // Wound Care
    woundCareDoneThisVisit: '', // No, Yes
    woundCareLocations: '',
    soiledDressingRemovedByPatient: false,
    soiledDressingRemovedByCaregiver: false,
    soiledDressingRemovedByCaregiverName: '',
    soiledDressingRemovedByFamily: false,
    soiledDressingRemovedByRN: false,
    soiledDressingRemovedByPT: false,
    soiledDressingRemovedByOther: '',
    woundCareTechniqueSterile: false,
    woundCareTechniqueClean: false,
    handsWashedBefore: false,
    handsWashedAfter: false,
    woundCleanedWith: '',
    woundDressingApplied: '',
    woundIrrigatedWith: '',
    dressingSecuredWith: '',
    woundPackedWith: '',
    soiledDressingProperlyDisposed: false,
    patientToleratedProcedureWell: '', // No, Yes
    woundCareComments: '',
    
    // Diabetic Foot Exam
    diabeticFootExamNA: false,
    diabeticFootExamFrequency: '',
    diabeticFootExamDoneByPatient: false,
    diabeticFootExamDoneByCaregiverName: '',
    diabeticFootExamDoneByFamily: false,
    diabeticFootExamDoneByRN: false,
    diabeticFootExamDoneByOther: '',
    diabeticFootExamByClinicianThisVisit: '', // No, Yes
    diabeticFootIntegumentFindings: '',
    pedalPulsesPresentRight: false,
    pedalPulsesPresentLeft: false,
    pedalPulsesAbsentRight: false,
    pedalPulsesAbsentLeft: false,
    pedalPulsesComment: '',
    lossOfSenseWarmRight: false,
    lossOfSenseWarmLeft: false,
    lossOfSenseColdRight: false,
    lossOfSenseColdLeft: false,
    lossOfSenseComment: '',
    numbnessRight: false,
    numbnessLeft: false,
    tinglingRight: false,
    tinglingLeft: false,
    burningRight: false,
    burningLeft: false,
    legHairPresentRight: false,
    legHairPresentLeft: false,
    legHairAbsentRight: false,
    legHairAbsentLeft: false,
    diabeticFootComments: '',
    integumentaryAffectsFunctionSafety: '', // No, Yes
    integumentaryAffectsFunctionSafetyExplain: '',
    
    // Wound/Lesion Assessment (up to 5 wounds)
    // Wound 1
    wound1DateOriginallyReported: '',
    wound1Location: '',
    wound1TypeArterial: false,
    wound1TypeDiabeticFoot: false,
    wound1TypeMalignancy: false,
    wound1TypeMechanicalTrauma: false,
    wound1TypePressureUlcer: false,
    wound1TypeSurgical: false,
    wound1TypeDialysisAccess: false,
    wound1TypeVenousStasis: false,
    wound1TypeIV: false,
    wound1TypeOther: '',
    wound1SizeLength: '',
    wound1SizeWidth: '',
    wound1SizeDepth: '',
    wound1TunnelingLength: '',
    wound1TunnelingClock: '',
    wound1UnderminingCm: '',
    wound1UnderminingFrom: '',
    wound1UnderminingTo: '',
    wound1Stage: '',
    wound1Unstageable: false,
    wound1Unobservable: false,
    wound1DTI: false,
    wound1SeveritySkinOnly: false,
    wound1SeverityFattyTissue: false,
    wound1SeverityMuscle: false,
    wound1SeverityBone: false,
    wound1SeverityMuscleNecrosis: false,
    wound1SeverityBoneNecrosis: false,
    wound1SeverityOther: '',
    wound1Odor: '', // No, Yes
    wound1SurroundingSkinErythema: false,
    wound1SurroundingSkinInduration: false,
    wound1SurroundingSkinMaceration: false,
    wound1SurroundingSkinNormal: false,
    wound1SurroundingSkinOther: '',
    wound1Edema: '',
    wound1AppearanceSloughPercent: '',
    wound1AppearanceEscharPercent: '',
    wound1AppearanceGranulationPercent: '',
    wound1DrainageAmountNone: false,
    wound1DrainageAmountSmall: false,
    wound1DrainageAmountModerate: false,
    wound1DrainageAmountLarge: false,
    wound1DrainageColorClear: false,
    wound1DrainageColorTan: false,
    wound1DrainageColorSerosanguineous: false,
    wound1DrainageColorOther: '',
    wound1ConsistencyThin: false,
    wound1ConsistencyThick: false,
    wound1IncisionWellApproximated: false,
    wound1IncisionSeparation: false,
    wound1PlannedSecondaryIntention: false,
    wound1DialysisAccessPD: false,
    wound1DialysisAccessAVGraft: false,
    wound1DialysisAccessAVFistula: false,
    wound1IVSitePeripheral: false,
    wound1IVSitePICC: false,
    wound1IVSiteCentral: false,
    wound1IVLumens: '',
    wound1DateHealed: '',
    wound1Comments: '',
    
    // M1306 Unhealed Pressure Ulcer
    m1306UnhealedPressureUlcer: '', // 0. No, 1. Yes
    
    // M1311 Current Number at Each Stage
    m1311A1Stage2Number: '',
    m1311B1Stage3Number: '',
    m1311C1Stage4Number: '',
    m1311D1UnstageableNonRemovable: '',
    m1311E1UnstageableSloughEschar: '',
    m1311F1UnstageableDeepTissueInjury: '',
    
    // M1322 Stage 1 Pressure Injuries
    m1322Stage1Number: '', // 0, 1, 2, 3, 4 or more
    
    // M1324 Most Problematic Stageable
    m1324MostProblematicStage: '', // 1-4, NA
    
    // M1330 Stasis Ulcer
    m1330StasisUlcer: '', // 0-3
    
    // M1332 Number Observable
    m1332NumberObservable: '', // 1-4
    
    // M1334 Status Most Problematic
    m1334StatusMostProblematic: '', // 1-3
    
    // M1340 Surgical Wound
    m1340SurgicalWound: '', // 0-2
    
    // M1342 Status Most Problematic Surgical
    m1342StatusMostProblematic: '', // 0-3
    
    // Section N - Medications (Pages 30-31)
    // N0415 High-Risk Drug Classes
    n0415AntipsychoticTaking: false,
    n0415AntipsychoticIndicationNoted: false,
    n0415AnticoagulantTaking: false,
    n0415AnticoagulantIndicationNoted: false,
    n0415AntibioticTaking: false,
    n0415AntibioticIndicationNoted: false,
    n0415OpioidTaking: false,
    n0415OpioidIndicationNoted: false,
    n0415AntiplateletTaking: false,
    n0415AntiplateletIndicationNoted: false,
    n0415HypoglycemicTaking: false,
    n0415HypoglycemicIndicationNoted: false,
    n0415NoneOfAbove: false,
    
    // M2001 Drug Regimen Review
    m2001DrugRegimenReview: '', // 0, 1, 9
    m2001PotentialAdverseEffects: false,
    m2001DrugReactions: false,
    m2001IneffectiveDrugTherapy: false,
    m2001SignificantSideEffects: false,
    m2001SignificantDrugInteractions: false,
    m2001DuplicateDrugTherapy: false,
    m2001NonCompliance: false,
    m2001HighRiskDrugs: false,
    
    // M2003 Medication Follow-up
    m2003MedicationFollowUp: '', // 0. No, 1. Yes
    
    // M2010 High-Risk Drug Education
    m2010HighRiskDrugEducation: '', // 0. No, 1. Yes, NA
    m2010InstructedPatient: false,
    m2010InstructedCaregiver: false,
    m2010InstructedOther: '',
    m2010TeachingGuideGiven: false,
    
    // M2020 Management of Oral Medications
    m2020ManagementOralMedications: '', // 0-3, NA
    
    // M2030 Management of Injectable Medications
    m2030ManagementInjectableMedications: '', // 0-3, NA
    
    // Medications
    financialAbilityToPayForMedications: '', // Yes, No
    mswReferralMadeForMedications: '', // Yes, No
    mswReferralComment: '',
    medicationAllergyNone: false,
    medicationAllergyAspirin: false,
    medicationAllergyPenicillin: false,
    medicationAllergySulfa: false,
    medicationAllergyOther: false,
    medicationAllergyOtherSpecify: '',
    
    // IV/Infusion
    hasIV: '', // No, Yes
    ivTypes: '',
    ivNumberOfSites: '',
    ivSiteLocations: '',
    ivTotalNumberOfLumens: '',
    ivInsertionDates: '',
    ivLumensPatent: '', // Yes, No
    ivLumensPatentExplain: '',
    ivNANotFlushed: false,
    ivInjectionCapChangeFrequency: '',
    ivFlushSolutionFrequency: '',
    ivDressingChangeDuringVisit: '', // No, Yes
    ivDressingChangeFrequency: '',
    ivDressingChangeSterile: false,
    ivDressingChangeClean: false,
    ivSiteSkinCondition: '',
    ivOther: '',
    ivPerformedByPatient: false,
    ivPerformedByRN: false,
    ivPerformedByCaregiver: false,
    ivPerformedByFamily: false,
    ivPerformedByOther: '',
    
    // PICC Specific
    piccArmCircumference: '',
    piccExternalCatheterLength: '',
    piccXRayVerification: '', // No, Yes
    
    // IVAD Port Specific
    ivadReservoirSingle: false,
    ivadReservoirDouble: false,
    ivadHuberGaugeLength: '',
    ivadAccessed: '', // No, Yes
    ivadAccessedDate: '',
    
    // Epidural/Intrathecal
    epiduralIntrathecalAccess: '',
    epiduralSiteSkinCondition: '',
    
    infusionSolutionTypeVolumeRate: '',
    infusionPumpType: '',
    infusionAdministeredByPatient: false,
    infusionAdministeredByCaregiver: false,
    infusionAdministeredByNurse: false,
    infusionAdministeredByFamily: false,
    infusionAdministeredByOther: '',
    
    // Purpose of IV Access
    ivPurposeAntibioticTherapy: false,
    ivPurposePainControl: false,
    ivPurposeLabDraws: false,
    ivPurposeChemotherapy: false,
    ivPurposeMaintainVenousAccess: false,
    ivPurposeHydration: false,
    ivPurposeParenteralNutrition: false,
    ivPurposeOther: '',
    infusionCareProvidedDuringVisit: '', // No, Yes
    infusionInterventionsInstructionsComments: '',
    
    medicationAssistanceNeeded: '', // No, Yes
    medicationAssistanceWhoHelpsAndWhat: '',
    
    // Section O - Special Treatments, Procedures, and Programs (Pages 32-36)
    // O0110 Special Treatments
    o0110A1Chemotherapy: false,
    o0110A2IV: false,
    o0110A3Oral: false,
    o0110A10Other: false,
    o0110B1Radiation: false,
    o0110C1OxygenTherapy: false,
    o0110C2Continuous: false,
    o0110C3Intermittent: false,
    o0110C4HighConcentration: false,
    o0110D1Suctioning: false,
    o0110D2Scheduled: false,
    o0110D3AsNeeded: false,
    o0110E1TracheostomyCare: false,
    o0110F1InvasiveMechanicalVentilator: false,
    o0110G1NonInvasiveMechanicalVentilator: false,
    o0110G2BiPAP: false,
    o0110G3CPAP: false,
    o0110H1IVMedications: false,
    o0110H2Vasoactive: false,
    o0110H3Antibiotics: false,
    o0110H4Anticoagulation: false,
    o0110H10Other: false,
    o0110I1Transfusions: false,
    o0110J1Dialysis: false,
    o0110J2Hemodialysis: false,
    o0110J3PeritonealDialysis: false,
    o0110O1IVAccess: false,
    o0110O2Peripheral: false,
    o0110O3Midline: false,
    o0110O4Central: false,
    o0110Z1NoneOfAbove: false,
    
    // M2200 Therapy Need
    m2200TherapyNeed: '',
    m2200TherapyNeedNA: false,
    
    // Risk Factors/Hospital Admission/Emergency Room
    riskFactorsFollowedUpByDiscussion: false,
    riskFactorsFollowedUpByEducation: false,
    riskFactorsFollowedUpByTraining: false,
    riskFactorsLiteratureGivenToPatient: false,
    riskFactorsLiteratureGivenToRepresentative: false,
    riskFactorsLiteratureGivenToCaregiver: false,
    riskFactorsLiteratureGivenToFamilyMember: false,
    riskFactorsLiteratureGivenToOther: '',
    riskFactorsListNA: false,
    riskFactorsList: '',
    
    // Patient/Caregiver Education and Training
    educationWoundCareKnowledgeDeficit: '', // Yes, No, NA
    educationWoundCareIndividuals: '',
    educationDiabeticFootExamKnowledgeDeficit: '', // Yes, No, NA
    educationDiabeticFootExamIndividuals: '',
    educationInsulinAdminKnowledgeDeficit: '', // Yes, No, NA
    educationInsulinAdminIndividuals: '',
    educationGlucometerUseKnowledgeDeficit: '', // Yes, No, NA
    educationGlucometerUseIndividuals: '',
    educationNutritionalManagementKnowledgeDeficit: '', // Yes, No, NA
    educationNutritionalManagementIndividuals: '',
    educationMedicationAdminKnowledgeDeficit: '', // Yes, No, NA
    educationMedicationAdminOral: false,
    educationMedicationAdminInjected: false,
    educationMedicationAdminInfused: false,
    educationMedicationAdminInhaled: false,
    educationMedicationAdminTopical: false,
    educationMedicationAdminIndividuals: '',
    educationPainManagementKnowledgeDeficit: '', // Yes, No, NA
    educationPainManagementIndividuals: '',
    educationOxygenUseKnowledgeDeficit: '', // Yes, No, NA
    educationOxygenUseIndividuals: '',
    educationMedicalDevicesKnowledgeDeficit: '', // Yes, No, NA
    educationMedicalDevicesIndividuals: '',
    educationPressureReductionKnowledgeDeficit: '', // Yes, No, NA
    educationPressureReductionIndividuals: '',
    educationCatheterCareKnowledgeDeficit: '', // Yes, No, NA
    educationCatheterCareIndividuals: '',
    educationTrachCareKnowledgeDeficit: '', // Yes, No, NA
    educationTrachCareIndividuals: '',
    educationOstomyCareKnowledgeDeficit: '', // Yes, No, NA
    educationOstomyCareIndividuals: '',
    educationEmergencyPrepKnowledgeDeficit: '', // Yes, No, NA
    educationEmergencyPrepIndividuals: '',
    educationInfectionControlKnowledgeDeficit: '', // Yes, No, NA
    educationInfectionControlIndividuals: '',
    educationSSReportKnowledgeDeficit: '', // Yes, No, NA
    educationSSReportIndividuals: '',
    educationPatientRightsKnowledgeDeficit: '', // Yes, No, NA
    educationPatientRightsIndividuals: '',
    educationOtherCares: '',
    
    educationNeedsFurtherEducationTraining: '',
    educationEducatedThisVisitFor: '',
    educationAwareTrainingWillContinue: '',
    hasActionPlanForSymptomExacerbation: '', // No, Yes
    admissionPacketGivenTo: '',
    educationComments: '',
    
    // Rehabilitation Potential
    rehabReturnToIndependentLevel: false,
    rehabAbleToRemainWithPrimaryCaregiver: false,
    rehabAbleToRemainWithCommunityAgencies: false,
    rehabRestorativePotential: false,
    dischargeDiscussedWith: '',
    
    // Care Coordination
    careCoordinationPatient: false,
    careCoordinationCaregiver: false,
    careCoordinationRepresentative: false,
    careCoordinationFamilyInvolvement: false,
    
    // Checklist Items Completed at SOC/ROC
    checklistPrimaryDiagnosisIdentified: false,
    checklistAllSecondaryDiagnosesIdentified: false,
    checklistHomeboundStatusMedicalNecessity: false,
    checklistDrugRegimenReviewCompleted: false,
    checklistMedicationIssuesAddressed: false,
    checklistMedicationIssuesOutcomeDocumented: false,
    checklistMedicationIssuesOrderReceived: false,
    
    // Assessment Findings
    assessmentFindingsSensoryStatus: false,
    assessmentFindingsPain: false,
    assessmentFindingsEndocrineHematology: false,
    assessmentFindingsIntegumentary: false,
    assessmentFindingsCardiopulmonary: false,
    assessmentFindingsNutritional: false,
    assessmentFindingsUrinaryElimination: false,
    assessmentFindingsBowelElimination: false,
    assessmentFindingsNeuroEmotionalBehavioral: false,
    assessmentFindingsMusculoskeletal: false,
    assessmentFindingsSafetyIssues: false,
    assessmentFindingsPsychosocial: false,
    assessmentFindingsFunctionalLimitations: false,
    assessmentFindingsFallRisk: false,
    
    // Additional Areas Assessed
    additionalAreasCopingMechanisms: false,
    additionalAreasLevelOfComprehension: false,
    additionalAreasMotivation: false,
    additionalAreasIdentifiedStrengthsLimitations: false,
    additionalAreasNonPaidCaregiverAvailability: false,
    additionalAreasFamilySupport: false,
    additionalAreasFriendsAndCommunitySupport: false,
    additionalAreasLivingArrangements: false,
    additionalAreasCarePreferences: false,
    additionalAreasPersonalGoals: false,
    additionalAreasRiskForRehospitalization: false,
    additionalAreasAvoidableEDUse: false,
    additionalAreasInterventionsToAvoidRehospitalization: false,
    additionalAreasInterventionsToAvoidEDUse: false,
    additionalAreasCoordinationOfServices: false,
    additionalAreasEmergencyPrepPlan: false,
    
    // Additional Care Coordination
    additionalCareCoordinationFindingsReported: false,
    additionalCareCoordinationAdditionalFindings: false,
    additionalCareCoordinationMedicationIssues: false,
    additionalCareCoordinationVerificationDiagnoses: false,
    additionalCareCoordinationAdditionalDiagnosesList: '',
    additionalCareCoordinationVerificationRehabPotential: false,
    additionalCareCoordinationApprovalAdditionalInterventions: false,
    
    // Other Services Involved
    otherServicesInvolvedPT: false,
    otherServicesInvolvedOT: false,
    otherServicesInvolvedSLP: false,
    otherServicesInvolvedMSW: false,
    otherServicesInvolvedAide: false,
    otherServicesInvolvedOther: '',
    
    // MSW Referral
    mswReferralCommunityResources: false,
    mswReferralLivingWill: false,
    mswReferralCounselingNeeds: false,
    mswReferralUnsafeEnvironment: false,
    mswReferralOther: '',
    mswReferralDate: '',
    mswReferralRefused: false,
    mswReferralNA: false,
    
    careCoordinationComments: '',
    
    // DME/Medical Supplies
    dmeCompanyName: '',
    dmeCompanyPhone: '',
    oxygenCompanyName: '',
    oxygenCompanyPhone: '',
    communityOrganizationsServicesName: '',
    communityOrganizationsServicesPhone: '',
    communityOrganizationsServicesContact: '',
    communityOrganizationsServicesComments: '',
    
    // Current DME/Medical Supplies Checklist (extensive list)
    dmeNoneUsed: false,
    // Wound Care supplies
    dme2x2s: false,
    dme4x4s: false,
    dmeABDs: false,
    dmeCottonTippedApplicators: false,
    dmeSyringesSize: '',
    dmeTape: false,
    dmeDrainSponges: false,
    dmeHydrocolloids: false,
    dmeKerlixSize: '',
    dmeNuGauze: false,
    dmeSaline: false,
    dmeTransparentDressings: false,
    dmeWoundCleanser: false,
    dmeWoundGel: false,
    dmeWoundCareOther: '',
    // IV Supplies
    dmeAlcoholSwabs: false,
    dmeAngiocatheterSize: '',
    dmeBatteriesSize: '',
    dmeCentralLineDressing: false,
    dmeExtensionTubings: false,
    dmeInfusionPump: false,
    dmeInjectionCaps: false,
    dmeIVPole: false,
    dmeIVStartKit: false,
    dmeIVTubing: false,
    dmeIVSyringesSize: '',
    dmeIVOther: '',
    // Urinary/Ostomy
    dmeExternalCatheters: false,
    dmeOstomyPouchBrandSize: '',
    dmeOstomyWaferBrandSize: '',
    dmeSkinProtectant: false,
    dmeStomaAdhesiveTape: false,
    dmeUnderpads: false,
    dmeUrinaryBagPouch: false,
    dmeUrinaryOstomyOther: '',
    // Catheter Supplies
    dmeAceticAcid: false,
    dmeFrCatheterKit: '',
    dmeIrrigationTray: false,
    dmeCatheterSaline: false,
    dmeStraightCatheter: false,
    dmeCatheterOther: '',
    // Diabetic
    dmeChemstrips: false,
    dmeDiabeticSyringes: false,
    dmeDiabeticOther: '',
    // Miscellaneous
    dmeEnemaSupplies: false,
    dmeFeedingTubeType: '',
    dmeGlovesSize: '',
    dmeGlovesSterile: false,
    dmeGlovesNonSterile: false,
    dmeMedBox: false,
    dmeStapleRemovalKit: false,
    dmeSteriStrips: false,
    dmeSutureRemovalKit: false,
    dmeMiscOther: '',
    // Supplies/Equipment
    dmeAugmentativeCommunicationDevice: '',
    dmeBathBench: false,
    dmeBraceOrthotics: '',
    dmeCane: false,
    dmeCommode: false,
    dmeDressingAidHipKit: false,
    dmeEggcrate: false,
    dmeEnteralFeedingPump: false,
    dmeGrabBars: false,
    dmeHandheldShower: false,
    dmeHospitalBedSemiElectric: false,
    dmeHoyerLift: false,
    dmeKneeScooter: false,
    dmeMedicalAlert: false,
    dmeNebulizer: false,
    dmeOxygenConcentrator: false,
    dmePressureRelievingDevice: false,
    dmeProsthesisRUE: false,
    dmeProsthesisRLE: false,
    dmeProsthesisLUE: false,
    dmeProsthesisLLE: false,
    dmeProsthesisOther: '',
    dmeRaisedToiletSeat: false,
    dmeReacher: false,
    dmeSpecialMattressOverlay: false,
    dmeSuctionMachine: false,
    dmeTENSUnit: false,
    dmeTransferBoard: false,
    dmeTransferLift: false,
    dmeVentilator: false,
    dmeWalker: false,
    dmeWheelchair: false,
    dmeEquipmentOther: '',
    
    // Page 36 - Homebound and Assessment Summary
    confinedToHome: '', // Yes
    
    // Criteria One
    criteriaOneDependentOnAdaptiveDevices: false,
    criteriaOneCrutches: false,
    criteriaOneCanes: false,
    criteriaOneWalker: false,
    criteriaOneWheelchairManual: false,
    criteriaOneWheelchairMotorized: false,
    criteriaOneProstheticLimb: false,
    criteriaOneScooter: false,
    criteriaOneHelper: false,
    criteriaOneOther: '',
    criteriaOneNeedsSpecialTransportation: '',
    criteriaOneNeedsPhysicalAssist: '',
    criteriaOneLeavingMedicallyContraindicated: '',
    
    // Criteria Two
    criteriaTwoNormalInabilityToLeave: '',
    criteriaTwoRequiresConsiderableTaxingEffort: '',
    
    skilledCareProvidedYes: false,
    skilledCareProvidedExplain: '',
    planForNextVisit: '',
    homeboundAssessmentComments: '',
    
    // Physician Verbal Order
    physicianNameVerbalOrder: '',
    verbalOrderReceivedForHomeHealth: false,
    signatureTitlePersonReceivedVerbalOrder: '',
    verbalOrderReceivedDate: '',
    verbalOrderReceivedTime: '',
    physicianSignatureForVerbalOrder: '',
    physicianSignatureDate: '',
    physicianSignatureTime: '',
    
    // Final Signatures (comprehensive - typed, voice recording, handwritten)
    patientFamilyCaregiverRepSignature: { typed: '', audioURL: null, canvasDataURL: null },
    patientFamilyCaregiverRepSignatureDate: '',
    patientFamilyCaregiverRepSignatureTime: '',
    personCompletingFormSignature: { typed: '', audioURL: null, canvasDataURL: null },
    personCompletingFormTitle: '',
    agencyName: '',
    personCompletingFormDate: '',
    agencyPhoneNumber: '',
    personCompletingFormTime: '',
    // Additional signature fields
    clinicianSignature: { typed: '', audioURL: null, canvasDataURL: null },
    clinicianSignatureDate: '',
    clinicianNamePrint: '',
    clinicianCredentials: '',
    patientSignature: { typed: '', audioURL: null, canvasDataURL: null },
    patientSignatureDate: '',
    representativeRelationship: '',
    physicianCertificationSignature: { typed: '', audioURL: null, canvasDataURL: null },
    physicianCertificationDate: '',
    
    // ============= MISSING STATE FIELDS (Added to fix state management) =============
    accessElevator: false,
    accessRailings: false,
    accessRamp: false,
    accessSteps: false,
    accessStepsNumber: '',
    activitiesBRP: false,
    activitiesBedrest: false,
    activitiesExercisesInBed: false,
    activitiesIndependent: false,
    activitiesOther: false,
    activitiesOtherSpecify: '',
    activitiesPartialWeightBearingLeft: false,
    activitiesPartialWeightBearingRight: false,
    activitiesTransferBedToChairWithAssistance: false,
    activitiesUpAsToleratedWithAssistance: false,
    additionalDiagnoses: '',
    additionalWounds: '',
    advanceDirectivesDiscussed: false,
    alcoholDetails: '',
    appetite: '',
    assessmentSummary: '',
    bathroomBasement: false,
    bathroomFirstFloor: false,
    bathroomSecondFloor: false,
    bedroomBasement: false,
    bedroomFirstFloor: false,
    bedroomSecondFloor: false,
    bladderDysuria: false,
    bladderFrequency: false,
    bladderHematuria: false,
    bladderIncontinence: false,
    bladderInfection: false,
    bladderNoProblems: false,
    bladderNocturia: false,
    bladderRetention: false,
    bladderUrgency: false,
    bloodGlucoseTestingFrequency: '',
    bmiCalculated: '',
    bowelFlatulence: false,
    bowelIncontinence: false,
    bowelNoProblems: false,
    bowelOstomy: false,
    bowelRectalBleeding: false,
    c0100ShouldBIMSBeConducted: '',
    c0200RepetitionOfThreeWords: '',
    c0300AYear: '',
    c0300BMonth: '',
    c0300CDay: '',
    c0400ASock: '',
    c0400BBlue: '',
    c0400CBed: '',
    c0700ShortTermMemory: '',
    c0800LongTermMemory: '',
    c0900MemoryRecallCurrentSeason: false,
    c0900MemoryRecallInNursingHome: false,
    c0900MemoryRecallLocationRoom: false,
    c0900MemoryRecallNone: false,
    c0900MemoryRecallStaffNames: false,
    c1000CognitiveSkillsForDailyDecision: '',
    cardiovascularAICD: false,
    cardiovascularChestPain: false,
    cardiovascularDOE: false,
    cardiovascularEdema: false,
    cardiovascularNoProblems: false,
    cardiovascularOrthopnea: false,
    cardiovascularPND: false,
    cardiovascularPacemaker: false,
    cardiovascularPalpitations: false,
    catheterChangeFrequency: '',
    catheterDateInserted: '',
    catheterExternal: false,
    catheterIndwelling: false,
    catheterIntermittent: false,
    catheterSize: '',
    catheterSuprapubic: false,
    catheterType: '',
    copingAdequate: false,
    copingInadequate: false,
    copingInadequateExplain: '',
    d0150ALittleInterest: '',
    d0150BFeelingDown: '',
    d0150CTroubleSleeping: '',
    d0150DFeelingTired: '',
    d0150EPoorAppetite: '',
    d0150FFeelingBad: '',
    d0150GTroubleConcentrating: '',
    d0150HMovingSlowly: '',
    d0150IThoughtsOfHarm: '',
    diabetesManagementDiet: false,
    diabetesManagementInsulin: false,
    diabetesManagementOralMeds: false,
    dietDiabetic: false,
    dietLowFat: false,
    dietLowSodium: false,
    dietMechanicalSoft: false,
    dietPureed: false,
    dietRegular: false,
    disciplineDietitian: false,
    disciplineHHA: false,
    disciplineMSW: false,
    disciplineOT: false,
    disciplinePT: false,
    disciplineSLP: false,
    dmeBPMonitor: false,
    dmeBedside: false,
    dmeGlucometer: false,
    dmeHospitalBed: false,
    dmeOther: false,
    dmeOtherSpecify: '',
    dmeOxygen: false,
    dmeSuppliesNeeded: '',
    drugUse: '',
    drugUseDetails: '',
    dysphagiaChoking: false,
    dysphagiaCoughing: false,
    dysphagiaDrooling: false,
    dysphagiaLiquids: false,
    dysphagiaPills: false,
    dysphagiaPocketing: false,
    dysphagiaSolids: false,
    educationActivity: false,
    educationDME: false,
    educationDiet: false,
    educationDiseaseProcess: false,
    educationEmergencySigns: false,
    educationFallPrevention: false,
    educationInfectionControl: false,
    educationMedications: false,
    educationOther: false,
    educationOtherSpecify: '',
    educationPainManagement: false,
    educationProvidedToCaregiver: false,
    educationProvidedToFamily: false,
    educationProvidedToPatient: false,
    educationSafetyPrecautions: false,
    educationWoundCare: false,
    endocrineAnemia: false,
    endocrineBleedingDisorder: false,
    endocrineDiabetesType1: false,
    endocrineDiabetesType2: false,
    endocrineNoProblems: false,
    endocrineOther: false,
    endocrineOtherSpecify: '',
    endocrineThyroid: false,
    enteralFormulaName: '',
    enteralInsertionSiteCondition: '',
    enteralRateFrequency: '',
    enteralTubeDatePlaced: '',
    enteralTubeJejunostomy: false,
    enteralTubeNG: false,
    enteralTubeOther: false,
    enteralTubeOtherSpecify: '',
    enteralTubePEG: false,
    enteralTubeSize: '',
    fallRiskLevel: '',
    fluidRestriction: '',
    fluidRestrictionAmount: '',
    footLeftCallus: false,
    footLeftDry: false,
    footLeftIntact: false,
    footLeftUlcer: false,
    footRightCallus: false,
    footRightDry: false,
    footRightIntact: false,
    footRightUlcer: false,
    genitaliaEdema: false,
    genitaliaNoProblems: false,
    genitaliaOtherSpecify: '',
    gg0130AEating: '',
    gg0130AEatingGoal: '',
    gg0130BOralHygiene: '',
    gg0130BOralHygieneGoal: '',
    gg0130CToiletingHygiene: '',
    gg0130CToiletingHygieneGoal: '',
    gg0130E1ShowerBathe: '',
    gg0130E1ShowerBatheGoal: '',
    gg0130FUpperDressing: '',
    gg0130FUpperDressingGoal: '',
    gg0130GLowerDressing: '',
    gg0130GLowerDressingGoal: '',
    gg0130HFootwear: '',
    gg0130HFootwearGoal: '',
    gg0170ARoll: '',
    gg0170ARollGoal: '',
    gg0170BSitToLying: '',
    gg0170BSitToLyingGoal: '',
    gg0170CLyingToSitting: '',
    gg0170CLyingToSittingGoal: '',
    gg0170DSitToStand: '',
    gg0170DSitToStandGoal: '',
    gg0170EChairTransfer: '',
    gg0170EChairTransferGoal: '',
    gg0170FToiletTransfer: '',
    gg0170FToiletTransferGoal: '',
    gg0170GCarTransfer: '',
    gg0170GCarTransferGoal: '',
    gg0170IWalk10Feet: '',
    gg0170IWalk10FeetGoal: '',
    gg0170JWalk50Feet: '',
    gg0170JWalk50FeetGoal: '',
    gg0170KWalk150Feet: '',
    gg0170KWalk150FeetGoal: '',
    gg0170LWalkUneven: '',
    gg0170LWalkUnevenGoal: '',
    gg0170M1Step: '',
    gg0170M1StepGoal: '',
    gg0170N4Steps: '',
    gg0170N4StepsGoal: '',
    gg0170O12Steps: '',
    gg0170O12StepsGoal: '',
    gg0170PPickingUp: '',
    gg0170PPickingUpGoal: '',
    gg0170RRWheel50Feet: '',
    gg0170RRWheel50FeetGoal: '',
    gg0170SSWheel150Feet: '',
    gg0170SSWheel150FeetGoal: '',
    goalsExpectedOutcomes: '',
    hasCurrentPain: '',
    hazardClutter: false,
    hazardFirearms: false,
    hazardLooseRugs: false,
    hazardOther: false,
    hazardOtherSpecify: '',
    hazardOxygen: false,
    hazardPets: false,
    hazardPoorLighting: false,
    hazardSmoking: false,
    hipaaNoticeProvided: false,
    homeboundAmbulationDeficit: false,
    homeboundBedrest: false,
    homeboundCognitiveImpairment: false,
    homeboundJustification: '',
    homeboundMedicalRestriction: false,
    homeboundNeedsAssistanceOfAnother: false,
    homeboundNeedsAssistiveDevice: false,
    homeboundOther: false,
    homeboundOtherSpecify: '',
    homeboundPain: false,
    homeboundPsychiatricCondition: false,
    homeboundSOB: false,
    homeboundUnsafeToLeaveAlone: false,
    homeboundWeakness: false,
    ivAccessCentralLine: false,
    ivAccessMidline: false,
    ivAccessPICC: false,
    ivAccessPeripheral: false,
    ivAccessPort: false,
    ivDateInserted: '',
    ivLocation: '',
    ivMedicationsAdministered: '',
    ivSiteCondition: '',
    j0510PainEffectSleep: '',
    j0520PainInterference: '',
    j1800FallsSinceSOCROC: '',
    j1900ANoInjury: false,
    j1900BInjury: '',
    j1900CMajorInjury: '',
    k0520FeedingTube: false,
    k0520MechanicallyAlteredDiet: false,
    k0520NoneOfAbove: false,
    k0520ParenteralIV: false,
    k0520TherapeuticDiet: false,
    lastBMDate: '',
    locAlert: false,
    locComatose: false,
    locLethargic: false,
    locObtunded: false,
    locStuporous: false,
    m1005NoInpatientAdmission: '',
    m1021PrimaryDiagnosisCode: '',
    m1021SymptomControlRating: '',
    m1028DMType1Insulin: '',
    m1028DMType2Insulin: '',
    m1028DMType2NoInsulin: '',
    m1028PeripheralVascular: '',
    m10335PlusMedications: '',
    m1033Frailty: '',
    m1033OtherRisk: '',
    m1033OtherRiskSpecify: '',
    m1033RecentDecline: '',
    m1060Height: '',
    m1060HeightUK: '',
    m1060Weight: '',
    m1060WeightUK: '',
    m1100LivingSituation: '',
    m1311Stage2Present: false,
    m1311Stage2Worsened: false,
    m1311Stage3Present: false,
    m1311Stage3Worsened: false,
    m1311Stage4Present: false,
    m1311Stage4Worsened: false,
    m1311UnstageableDTIPresent: false,
    m1311UnstageableDTIWorsened: false,
    m1311UnstageableDressingPresent: false,
    m1311UnstageableDressingWorsened: false,
    m1311UnstageableSloughPresent: false,
    m1311UnstageableSloughWorsened: false,
    m1322Stage1PressureInjuries: '',
    m1342SurgicalWoundStatus: '',
    m1610UrinaryIncontinence: '',
    m1615WhenIncontinenceOccurs: '',
    m1620BowelIncontinence: '',
    m1630Ostomy: '',
    m1740Delusional: '',
    m1740DisruptiveBehavior: '',
    m1745FrequencyDisruptiveBehavior: '',
    m1860Ambulation: '',
    m2003MedicationFollowup: '',
    m2020ManagementOralMeds: '',
    m2030ManagementInjectableMeds: '',
    m2102aADL: '',
    m2102bIADL: '',
    m2102cMedication: '',
    m2102dMedicalProcedures: '',
    m2102eEquipment: '',
    m2102fSupervision: '',
    m2102gAdvocacy: '',
    mahc104PlusMeds: false,
    mahc10Age65Plus: false,
    mahc10AssistiveDevice: false,
    mahc10HighRiskMeds: false,
    mahc10HistoryFalls: false,
    mahc10NeuroDx: false,
    mahc10ToiletingAssistance: false,
    mahc10TotalScore: '',
    mahc10UnsafeHome: false,
    mahc10UnsteadyGait: false,
    mahc10VisionImpairment: false,
    medicationListComments: '',
    mentalStatusAffectAppropriate: false,
    mentalStatusAffectFlat: false,
    mentalStatusAgitated: false,
    mentalStatusAnxious: false,
    mentalStatusApathetic: false,
    mentalStatusDepressed: false,
    mentalStatusFearful: false,
    mentalStatusMoodLabile: false,
    mentalStatusNoProblem: false,
    mentalStatusOther: false,
    mentalStatusOtherSpecify: '',
    n0415AntibioticIndication: false,
    n0415AnticoagulantIndication: false,
    n0415AntiplateletIndication: false,
    n0415AntipsychoticIndication: false,
    n0415HypoglycemicIndication: false,
    n0415OpioidIndication: false,
    neuroAbnormalMovements: false,
    neuroNeuropathy: false,
    neuroNoProblem: false,
    neuroOther: false,
    neuroOtherSpecify: '',
    neuroParalysis: false,
    neuroParesthesia: false,
    neuroPertinentComments: '',
    neuroSeizureHistory: false,
    neuroSyncope: false,
    neuroTremors: false,
    neuroWeakness: false,
    o0110BiPAPCPAP: false,
    o0110Chemotherapy: false,
    o0110Dialysis: false,
    o0110Hospice: false,
    o0110IVMedications: false,
    o0110NoneOfAbove: false,
    o0110OxygenContinuous: false,
    o0110OxygenIntermittent: false,
    o0110Radiation: false,
    o0110Suctioning: false,
    o0110Tracheostomy: false,
    o0110TransfusionBlood: false,
    o0110Ventilator: false,
    orientedToPerson: false,
    orientedToPlace: false,
    orientedToSituation: false,
    orientedToTime: false,
    ostomyApplianceType: '',
    ostomyLocation: '',
    ostomyType: '',
    oxygenDeliveryMethod: '',
    oxygenFlowRate: '',
    painAggravatedBy: '',
    painFrequency: '',
    painIntensityAcceptable: '',
    painIntensityCurrent: '',
    painIntensityWorst: '',
    painLocations: '',
    painQualityAching: false,
    painQualityBurning: false,
    painQualityDull: false,
    painQualityOther: false,
    painQualityOtherSpecify: '',
    painQualityRadiating: false,
    painQualitySharp: false,
    painQualityThrobbing: false,
    painRelievedBy: '',
    patientRightsProvided: false,
    pedalPulsesLeft: '',
    pedalPulsesRight: '',
    phq2TotalScore: '',
    physicianContact: '',
    physicianContactDetails: '',
    planOfCare: '',
    preferredScheduleVisits: '',
    priorFunctionalCognition: '',
    priorIndoorMobility: '',
    priorSelfCare: '',
    priorStairs: '',
    psychosocialComments: '',
    psychosocialCounseling: false,
    psychosocialPsychiatrist: false,
    psychosocialSupportGroup: false,
    psychosocialTherapy: false,
    religiousCulturalPreferences: false,
    religiousCulturalPreferencesSpecify: '',
    residenceApartment: false,
    residenceAssistedLiving: false,
    residenceHouse: false,
    residenceMobileHome: false,
    residenceOther: false,
    residenceOtherSpecify: '',
    respiratoryCough: false,
    respiratoryDyspnea: false,
    respiratoryNoProblems: false,
    respiratoryOxygenUse: false,
    respiratorySputum: false,
    respiratoryWheezing: false,
    responseToTeaching: '',
    safetyAdequateHeating: false,
    safetyAdequateLighting: false,
    safetyCleanHome: false,
    safetyFireExtinguisher: false,
    safetyGrabBars: false,
    safetyRaisedToiletSeat: false,
    safetyRecommendations: '',
    safetyRunningWater: false,
    safetySecureRuGs: false,
    safetyShowerChair: false,
    safetySmokeDetectors: false,
    safetyWorkingPhone: false,
    sensationLeft: '',
    sensationRight: '',
    skilledNursingNeeds: '',
    skinBruising: false,
    skinDiscolored: false,
    skinEdema: false,
    skinFragile: false,
    skinIntact: false,
    skinTurgor: '',
    skinWound: false,
    specialConsiderations: '',
    stomaCondition: '',
    stressorEmployment: false,
    stressorFinancial: false,
    stressorGrief: false,
    stressorHealthRelated: false,
    stressorOther: false,
    stressorOtherSpecify: '',
    stressorRelationship: false,
    swallowingDifficulty: '',
    targetBloodGlucoseRange: '',
    tobaccoDetails: '',
    tobaccoUse: '',
    urinaryApplianceBriefs: false,
    urinaryApplianceCondomCatheter: false,
    urinaryApplianceNone: false,
    urinaryApplianceOther: false,
    urinaryApplianceOtherSpecify: '',
    urinaryAppliancePads: false,
    usualBowelPattern: '',
    weightChangeAmount: '',
    weightChangeLast30Days: '',
    weightChangeType: '',
    wound1BedDescription: '',
    wound1Depth: '',
    wound1Etiology: '',
    wound1Exudate: '',
    wound1Length: '',
    wound1Periwound: '',
    wound1Treatment: '',
    wound1Type: '',
    wound1Width: '',
  });

  const sections = [
    { name: 'Section A: Administrative', icon: User, description: 'Pages 1-6: Patient Info, Contacts, Caregivers, Advance Directives, History' },
    { name: 'Section B: Hearing/Vision', icon: Eye, description: 'Page 6: B0200 Hearing, B1000 Vision, B1300 Health Literacy' },
    { name: 'Section C: Cognitive', icon: Brain, description: 'Pages 7-8: BIMS, Delirium, M1700-M1720, Neurological' },
    { name: 'Section D: Mood', icon: Heart, description: 'Page 9: PHQ-2 to PHQ-9, D0160, D0700' },
    { name: 'Section E: Behavior', icon: Activity, description: 'Pages 9-10: M1740-M1745, Mental Status' },
    { name: 'Section F: Preferences', icon: Users, description: 'Pages 10-12: M1100, M2102, Care Preferences, Safety' },
    { name: 'Section G: Functional Status', icon: Activity, description: 'Pages 13-14: M1800-M1860, Activities Permitted' },
    { name: 'Section GG: Functional Abilities', icon: ClipboardList, description: 'Pages 14-17: GG0100-GG0170, Fall Risk (MAHC-10)' },
    { name: 'Section H: Bladder/Bowel', icon: Droplet, description: 'Pages 18-19: M1600-M1630, Urinary, Bowel, Genitalia' },
    { name: 'Section I: Diagnoses', icon: Stethoscope, description: 'Pages 20-21: M1021, M1023, M1028, Endocrine' },
    { name: 'Section J: Health Conditions', icon: AlertTriangle, description: 'Pages 22-24: M1033, Pain, Cardiopulmonary, M1400' },
    { name: 'Section K: Nutrition', icon: Thermometer, description: 'Pages 25-26: M1060, K0520, M1870, Enteral' },
    { name: 'Section M: Skin Conditions', icon: Shield, description: 'Pages 27-30: Wounds, M1306-M1342' },
    { name: 'Section N: Medications', icon: Pill, description: 'Pages 30-31: N0415, M2001-M2030, IV/Infusion' },
    { name: 'Section O: Special Treatments', icon: Stethoscope, description: 'Pages 32-35: O0110, M2200, Education, Care Coordination' },
    { name: 'Homebound/Summary', icon: Home, description: 'Page 36: Homebound Status, Signatures' },
  ];

  useEffect(() => {
    fetchPatients();
    if (formId) {
      fetchForm();
    }
  }, [formId]);

  // Auto-calculate BIMS Summary Score (C0500)
  useEffect(() => {
    const c0200 = parseInt(formData.c0200RepetitionOfThreeWords) || 0;
    const c0300A = parseInt(formData.c0300AYear) || 0;
    const c0300B = parseInt(formData.c0300BMonth) || 0;
    const c0300C = parseInt(formData.c0300CDay) || 0;
    const c0400A = parseInt(formData.c0400ASock) || 0;
    const c0400B = parseInt(formData.c0400BBlue) || 0;
    const c0400C = parseInt(formData.c0400CBed) || 0;
    
    const total = c0200 + c0300A + c0300B + c0300C + c0400A + c0400B + c0400C;
    if (formData.c0200RepetitionOfThreeWords || formData.c0300AYear) {
      setFormData(prev => ({ ...prev, c0500BIMSSummaryScore: total.toString().padStart(2, '0') }));
    }
  }, [formData.c0200RepetitionOfThreeWords, formData.c0300AYear, formData.c0300BMonth, formData.c0300CDay, formData.c0400ASock, formData.c0400BBlue, formData.c0400CBed]);

  // Auto-calculate PHQ-2 Score
  useEffect(() => {
    const a = parseInt(formData.d0150ALittleInterest) || 0;
    const b = parseInt(formData.d0150BFeelingDown) || 0;
    const phq2 = a + b;
    if (formData.d0150ALittleInterest || formData.d0150BFeelingDown) {
      setFormData(prev => ({ ...prev, phq2TotalScore: phq2.toString() }));
    }
  }, [formData.d0150ALittleInterest, formData.d0150BFeelingDown]);

  // Auto-calculate PHQ-9 Total Score (D0160)
  useEffect(() => {
    const scores = [
      formData.d0150ALittleInterest, formData.d0150BFeelingDown,
      formData.d0150CTroubleSleeping, formData.d0150DFeelingTired,
      formData.d0150EPoorAppetite, formData.d0150FFeelingBad,
      formData.d0150GTroubleConcentrating, formData.d0150HMovingSlowly,
      formData.d0150IThoughtsOfHarm
    ];
    const total = scores.reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    if (scores.some(s => s !== '' && s !== undefined)) {
      setFormData(prev => ({ ...prev, d0160TotalSeverityScore: total.toString().padStart(2, '0') }));
    }
  }, [formData.d0150ALittleInterest, formData.d0150BFeelingDown, formData.d0150CTroubleSleeping, formData.d0150DFeelingTired, formData.d0150EPoorAppetite, formData.d0150FFeelingBad, formData.d0150GTroubleConcentrating, formData.d0150HMovingSlowly, formData.d0150IThoughtsOfHarm]);

  // Auto-calculate Certification Period End Date (60 days from start)
  useEffect(() => {
    if (formData.certificationPeriodFrom) {
      const startDate = new Date(formData.certificationPeriodFrom);
      if (!isNaN(startDate.getTime())) {
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 59);
        const formattedEnd = `${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getDate().toString().padStart(2, '0')}/${endDate.getFullYear()}`;
        setFormData(prev => ({ ...prev, certificationPeriodTo: formattedEnd }));
      }
    }
  }, [formData.certificationPeriodFrom]);

  // Auto-calculate BMI
  useEffect(() => {
    const height = parseFloat(formData.m1060Height);
    const weight = parseFloat(formData.m1060Weight);
    if (height > 0 && weight > 0) {
      const bmi = (weight / ((height / 100) ** 2)).toFixed(1);
      setFormData(prev => ({ ...prev, bmiCalculated: bmi }));
    }
  }, [formData.m1060Height, formData.m1060Weight]);

  // Auto-calculate MAHC-10 Fall Risk Score
  useEffect(() => {
    const riskFactors = [
      formData.mahc10Age65Plus, formData.mahc10HistoryFalls, formData.mahc10VisionImpairment,
      formData.mahc10NeuroDx, formData.mahc10UnsafeHome, formData.mahc10UnsteadyGait,
      formData.mahc104PlusMeds, formData.mahc10HighRiskMeds, formData.mahc10AssistiveDevice,
      formData.mahc10ToiletingAssistance
    ];
    const score = riskFactors.filter(Boolean).length;
    if (riskFactors.some(r => r !== undefined)) {
      setFormData(prev => ({ ...prev, mahc10TotalScore: score.toString() }));
    }
  }, [formData.mahc10Age65Plus, formData.mahc10HistoryFalls, formData.mahc10VisionImpairment, formData.mahc10NeuroDx, formData.mahc10UnsafeHome, formData.mahc10UnsteadyGait, formData.mahc104PlusMeds, formData.mahc10HighRiskMeds, formData.mahc10AssistiveDevice, formData.mahc10ToiletingAssistance]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/soc-roc/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.assessment) {
        setFormData(prev => ({ ...prev, ...data.assessment.formData }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId 
        ? `${API_URL}/api/forms/soc-roc/${formId}`
        : `${API_URL}/api/forms/soc-roc`;
      
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      const payload = {
        patientId: formData.patientId,
        patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : '',
        assessmentType: formData.reasonForAssessment || 'SOC',
        assessmentDate: formData.assessmentDate || new Date().toISOString().split('T')[0],
        formData: formData
      };
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Assessment updated' : 'Assessment created');
        if (!formId && data.assessment?.id) {
          navigate(`/dashboard/forms/soc-roc-v2?id=${data.assessment.id}`);
        }
      } else {
        toast.error('Failed to save assessment');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  // Section A: Administrative Information (Pages 1-6)
  const renderSectionA = () => (
    <div className="space-y-8">
      {/* Page 1 Header */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <div className="text-center mb-4">
          <p className="text-xs text-gray-600">◉ = Dash is a valid response. See the OASIS Guidance Manual for specific item.</p>
          <p className="text-xs text-gray-600">Follow OASIS items in sequence unless otherwise directed.</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-sm font-medium">REASON FOR ASSESSMENT:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.reasonForAssessment === 'SOC'} onChange={() => handleInputChange('reasonForAssessment', 'SOC')} className="w-4 h-4" />
                <span className="text-sm">Start of Care</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.reasonForAssessment === 'ROC'} onChange={() => handleInputChange('reasonForAssessment', 'ROC')} className="w-4 h-4" />
                <span className="text-sm">Resumption of Care</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold">SOC/ROC OASIS COMPREHENSIVE NURSING ASSESSMENT</h2>
          <p className="text-sm">WITH PLAN OF CARE INFORMATION</p>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <VoiceDictationInput label="DATE:" value={formData.assessmentDate} onChange={(e) => handleInputChange('assessmentDate', e.target.value)} placeholder="MM/DD/YYYY" />
          <VoiceDictationInput label="TIME IN:" value={formData.timeIn} onChange={(e) => handleInputChange('timeIn', e.target.value)} placeholder="HH:MM" />
          <VoiceDictationInput label="TIME OUT:" value={formData.timeOut} onChange={(e) => handleInputChange('timeOut', e.target.value)} placeholder="HH:MM" />
        </div>
        
        <p className="text-xs text-gray-500 mt-4">This Patient Tracking Information must be filled out at start of care and per organizational policy. It is to be maintained as part of the clinical record.</p>
      </div>

      {/* Section A Administrative Information */}
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section A Administrative Information</h3>
        
        {/* M0018 */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M0018. National Provider Identifier (NPI) for the attending physician who has signed the plan of care</Label>
          <VoiceDictationInput value={formData.m0018NPI} onChange={(e) => handleInputChange('m0018NPI', e.target.value)} placeholder="NPI Number" className="mt-2" />
          
          <div className="grid grid-cols-4 gap-2 mt-3">
            <VoiceDictationInput label="Physician/NPP Name: (Last)" value={formData.physicianLastName} onChange={(e) => handleInputChange('physicianLastName', e.target.value)} />
            <VoiceDictationInput label="(First)" value={formData.physicianFirstName} onChange={(e) => handleInputChange('physicianFirstName', e.target.value)} />
            <VoiceDictationInput label="(MI)" value={formData.physicianMI} onChange={(e) => handleInputChange('physicianMI', e.target.value)} />
            <VoiceDictationInput label="(Suffix)" value={formData.physicianSuffix} onChange={(e) => handleInputChange('physicianSuffix', e.target.value)} />
          </div>
          
          <div className="mt-3">
            <VoiceDictationInput label="Physician/NPP Address: (Street/Suite No.)" value={formData.physicianAddress} onChange={(e) => handleInputChange('physicianAddress', e.target.value)} />
          </div>
          
          <div className="flex items-center gap-2 mt-2">
            <input type="checkbox" checked={formData.physicianUK} onChange={(e) => handleInputChange('physicianUK', e.target.checked)} className="w-4 h-4" />
            <Label className="text-sm">UK - Unknown or Not Available</Label>
          </div>
          
          <div className="grid grid-cols-3 gap-2 mt-3">
            <VoiceDictationInput label="Physician/NPP Phone:" value={formData.physicianPhone} onChange={(e) => handleInputChange('physicianPhone', e.target.value)} />
            <VoiceDictationInput label="Physician/NPP Fax:" value={formData.physicianFax} onChange={(e) => handleInputChange('physicianFax', e.target.value)} />
            <VoiceDictationInput label="Physician/NPP Email:" value={formData.physicianEmail} onChange={(e) => handleInputChange('physicianEmail', e.target.value)} />
          </div>
          
          <div className="grid grid-cols-3 gap-2 mt-3">
            <VoiceDictationInput label="City:" value={formData.physicianCity} onChange={(e) => handleInputChange('physicianCity', e.target.value)} />
            <VoiceDictationInput label="State:" value={formData.physicianState} onChange={(e) => handleInputChange('physicianState', e.target.value)} />
            <VoiceDictationInput label="ZIP Code:" value={formData.physicianZipCode} onChange={(e) => handleInputChange('physicianZipCode', e.target.value)} />
          </div>
        </div>

        {/* M0010-M0064 */}
        <div className="bg-white p-4 rounded border mb-4">
          <div className="grid grid-cols-3 gap-4">
            <VoiceDictationInput label="M0010. CMS Certification Number" value={formData.m0010CMSCertificationNumber} onChange={(e) => handleInputChange('m0010CMSCertificationNumber', e.target.value)} />
            <VoiceDictationInput label="M0014. Branch State" value={formData.m0014BranchState} onChange={(e) => handleInputChange('m0014BranchState', e.target.value)} />
            <VoiceDictationInput label="M0016. Branch ID Number" value={formData.m0016BranchIDNumber} onChange={(e) => handleInputChange('m0016BranchIDNumber', e.target.value)} />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <VoiceDictationInput label="M0020. Patient ID Number" value={formData.m0020PatientIDNumber} onChange={(e) => handleInputChange('m0020PatientIDNumber', e.target.value)} />
            <VoiceDictationInput label="Medical Record Number if different from Patient ID Number:" value={formData.medicalRecordNumber} onChange={(e) => handleInputChange('medicalRecordNumber', e.target.value)} />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <VoiceDictationInput label="M0030. Start of Care Date (Month/Day/Year)" value={formData.m0030StartOfCareDate} onChange={(e) => handleInputChange('m0030StartOfCareDate', e.target.value)} placeholder="MM/DD/YYYY" />
            <div>
              <VoiceDictationInput label="M0032. Resumption of Care Date (Month/Day/Year)" value={formData.m0032ResumptionOfCareDate} onChange={(e) => handleInputChange('m0032ResumptionOfCareDate', e.target.value)} placeholder="MM/DD/YYYY" />
              <div className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={formData.m0032NA} onChange={(e) => handleInputChange('m0032NA', e.target.checked)} className="w-4 h-4" />
                <Label className="text-xs">NA - Not Applicable</Label>
              </div>
            </div>
          </div>
          
          <div className="mt-4">
            <Label className="font-bold text-sm">M0040. Patient Name</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <VoiceDictationInput label="(First)" value={formData.m0040PatientFirstName} onChange={(e) => handleInputChange('m0040PatientFirstName', e.target.value)} />
              <VoiceDictationInput label="(MI)" value={formData.m0040PatientMI} onChange={(e) => handleInputChange('m0040PatientMI', e.target.value)} />
              <VoiceDictationInput label="(Last)" value={formData.m0040PatientLastName} onChange={(e) => handleInputChange('m0040PatientLastName', e.target.value)} />
              <VoiceDictationInput label="(Suffix)" value={formData.m0040PatientSuffix} onChange={(e) => handleInputChange('m0040PatientSuffix', e.target.value)} />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-4">
            <VoiceDictationInput label="M0050. Patient State of Residence" value={formData.m0050PatientStateOfResidence} onChange={(e) => handleInputChange('m0050PatientStateOfResidence', e.target.value)} />
            <VoiceDictationInput label="M0060. Patient ZIP Code" value={formData.m0060PatientZipCode} onChange={(e) => handleInputChange('m0060PatientZipCode', e.target.value)} />
            <div>
              <VoiceDictationInput label="M0064. Social Security Number" value={formData.m0064SocialSecurityNumber} onChange={(e) => handleInputChange('m0064SocialSecurityNumber', e.target.value)} />
              <div className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={formData.m0064UK} onChange={(e) => handleInputChange('m0064UK', e.target.checked)} className="w-4 h-4" />
                <Label className="text-xs">UK - Unknown or Not Available</Label>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Preparedness & Priority Code */}
        <div className="bg-yellow-50 p-4 rounded border mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <Label className="font-bold">EMERGENCY PREPAREDNESS ★★★ PRIORITY CODE ★★★</Label>
          </div>
          <p className="text-xs text-gray-600 mt-1">See page 3 for Emergency Contact, Representative and Advance Directives information.</p>
          <VoiceDictationInput label="Priority Code" value={formData.emergencyPreparednessCode} onChange={(e) => handleInputChange('emergencyPreparednessCode', e.target.value)} className="mt-2" />
        </div>

        {/* Page 2 - Section A Continued */}
        <div className="bg-white p-4 rounded border mb-4">
          <h4 className="font-bold text-sm mb-3 border-b pb-2">Section A Administrative Information (Continued) - Page 2</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-bold text-sm">M0063. Medicare Number</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={formData.m0063NANoMedicare} onChange={(e) => handleInputChange('m0063NANoMedicare', e.target.checked)} className="w-4 h-4" />
                <Label className="text-xs">NA - No Medicare</Label>
              </div>
              <VoiceDictationInput label="Claim #:" value={formData.m0063ClaimNumber} onChange={(e) => handleInputChange('m0063ClaimNumber', e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="font-bold text-sm">M0065. Medicaid Number</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={formData.m0065NANoMedicaid} onChange={(e) => handleInputChange('m0065NANoMedicaid', e.target.checked)} className="w-4 h-4" />
                <Label className="text-xs">NA - No Medicaid</Label>
              </div>
              <VoiceDictationInput label="Claim #:" value={formData.m0065ClaimNumber} onChange={(e) => handleInputChange('m0065ClaimNumber', e.target.value)} className="mt-2" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="font-bold text-sm">M0069. Gender (Enter Code)</Label>
              <p className="text-xs text-gray-500">1. Male  2. Female</p>
              <VoiceDictationInput value={formData.m0069Gender} onChange={(e) => handleInputChange('m0069Gender', e.target.value)} placeholder="Enter 1 or 2" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">Answer M0069 based on how the patient self-identifies.</p>
            </div>
            <VoiceDictationInput label="M0066. Birth Date (Month/Day/Year)" value={formData.m0066BirthDate} onChange={(e) => handleInputChange('m0066BirthDate', e.target.value)} placeholder="MM/DD/YYYY" />
          </div>
          
          <p className="text-xs text-gray-500 mt-2">If the patient does not self-identify, referral information (including hospital or physician office clinical data), or observation and physical assessment may be used. Based on the resources mentioned above, enter a response for patient's gender.</p>
          <VoiceDictationInput label="If the patient does self-identify but response given is not Male or Female, patient self-identifies as:" value={formData.patientSelfIdentifiesAs} onChange={(e) => handleInputChange('patientSelfIdentifiesAs', e.target.value)} className="mt-2" />
          <p className="text-xs text-gray-500 mt-1">Note: M0069 will still need to be coded, based on the assessment sources listed above.</p>
        </div>

        {/* A1005 Ethnicity */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">A1005. Ethnicity</Label>
          <p className="text-sm mb-2">Are you of Hispanic, Latino/a, or Spanish origin? ↓ Check all that apply</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityA} onChange={(e) => handleInputChange('a1005EthnicityA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">A. No, not of Hispanic, Latino/a, or Spanish origin</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityB} onChange={(e) => handleInputChange('a1005EthnicityB', e.target.checked)} className="w-4 h-4" /><span className="text-sm">B. Yes, Mexican, Mexican American, Chicano/a</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityC} onChange={(e) => handleInputChange('a1005EthnicityC', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C. Yes, Puerto Rican</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityD} onChange={(e) => handleInputChange('a1005EthnicityD', e.target.checked)} className="w-4 h-4" /><span className="text-sm">D. Yes, Cuban</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityE} onChange={(e) => handleInputChange('a1005EthnicityE', e.target.checked)} className="w-4 h-4" /><span className="text-sm">E. Yes, another Hispanic, Latino, or Spanish origin</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityX} onChange={(e) => handleInputChange('a1005EthnicityX', e.target.checked)} className="w-4 h-4" /><span className="text-sm">X. Patient unable to respond</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1005EthnicityY} onChange={(e) => handleInputChange('a1005EthnicityY', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Y. Patient declines to respond</span></label>
          </div>
        </div>

        {/* A1010 Race */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">A1010. Race</Label>
          <p className="text-sm mb-2">What is your race? ↓ Check all that apply</p>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceA} onChange={(e) => handleInputChange('a1010RaceA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">A. White</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceB} onChange={(e) => handleInputChange('a1010RaceB', e.target.checked)} className="w-4 h-4" /><span className="text-sm">B. Black or African American</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceC} onChange={(e) => handleInputChange('a1010RaceC', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C. American Indian or Alaska Native</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceD} onChange={(e) => handleInputChange('a1010RaceD', e.target.checked)} className="w-4 h-4" /><span className="text-sm">D. Asian Indian</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceE} onChange={(e) => handleInputChange('a1010RaceE', e.target.checked)} className="w-4 h-4" /><span className="text-sm">E. Chinese</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceF} onChange={(e) => handleInputChange('a1010RaceF', e.target.checked)} className="w-4 h-4" /><span className="text-sm">F. Filipino</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceG} onChange={(e) => handleInputChange('a1010RaceG', e.target.checked)} className="w-4 h-4" /><span className="text-sm">G. Japanese</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceH} onChange={(e) => handleInputChange('a1010RaceH', e.target.checked)} className="w-4 h-4" /><span className="text-sm">H. Korean</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceI} onChange={(e) => handleInputChange('a1010RaceI', e.target.checked)} className="w-4 h-4" /><span className="text-sm">I. Vietnamese</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceJ} onChange={(e) => handleInputChange('a1010RaceJ', e.target.checked)} className="w-4 h-4" /><span className="text-sm">J. Other Asian</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceK} onChange={(e) => handleInputChange('a1010RaceK', e.target.checked)} className="w-4 h-4" /><span className="text-sm">K. Native Hawaiian</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceL} onChange={(e) => handleInputChange('a1010RaceL', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L. Guamanian or Chamorro</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceM} onChange={(e) => handleInputChange('a1010RaceM', e.target.checked)} className="w-4 h-4" /><span className="text-sm">M. Samoan</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceN} onChange={(e) => handleInputChange('a1010RaceN', e.target.checked)} className="w-4 h-4" /><span className="text-sm">N. Other Pacific Islander</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceX} onChange={(e) => handleInputChange('a1010RaceX', e.target.checked)} className="w-4 h-4" /><span className="text-sm">X. Patient unable to respond</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceY} onChange={(e) => handleInputChange('a1010RaceY', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Y. Patient declines to respond</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1010RaceZ} onChange={(e) => handleInputChange('a1010RaceZ', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Z. None of the above</span></label>
          </div>
        </div>

        {/* M0150 Current Payment Source */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M0150. Current Payment Source for Home Care ↓ Check all that apply</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource0} onChange={(e) => handleInputChange('m0150PaymentSource0', e.target.checked)} className="w-4 h-4" /><span className="text-sm">0. None; no charge for current services</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource1} onChange={(e) => handleInputChange('m0150PaymentSource1', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1. Medicare (traditional fee-for-service)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource2} onChange={(e) => handleInputChange('m0150PaymentSource2', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2. Medicare (HMO/managed care/Advantage plan)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource3} onChange={(e) => handleInputChange('m0150PaymentSource3', e.target.checked)} className="w-4 h-4" /><span className="text-sm">3. Medicaid (traditional fee-for-service)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource4} onChange={(e) => handleInputChange('m0150PaymentSource4', e.target.checked)} className="w-4 h-4" /><span className="text-sm">4. Medicaid (HMO/managed care)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource5} onChange={(e) => handleInputChange('m0150PaymentSource5', e.target.checked)} className="w-4 h-4" /><span className="text-sm">5. Workers' compensation</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource6} onChange={(e) => handleInputChange('m0150PaymentSource6', e.target.checked)} className="w-4 h-4" /><span className="text-sm">6. Title programs (for example, Title III, V, XX)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource7} onChange={(e) => handleInputChange('m0150PaymentSource7', e.target.checked)} className="w-4 h-4" /><span className="text-sm">7. Other government (for example, TriCare, VA)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource8} onChange={(e) => handleInputChange('m0150PaymentSource8', e.target.checked)} className="w-4 h-4" /><span className="text-sm">8. Private insurance</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource9} onChange={(e) => handleInputChange('m0150PaymentSource9', e.target.checked)} className="w-4 h-4" /><span className="text-sm">9. Private HMO/managed care</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource10} onChange={(e) => handleInputChange('m0150PaymentSource10', e.target.checked)} className="w-4 h-4" /><span className="text-sm">10. Self-pay</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSource11} onChange={(e) => handleInputChange('m0150PaymentSource11', e.target.checked)} className="w-4 h-4" /><span className="text-sm">11. Other (specify)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m0150PaymentSourceUK} onChange={(e) => handleInputChange('m0150PaymentSourceUK', e.target.checked)} className="w-4 h-4" /><span className="text-sm">UK Unknown</span></label>
          </div>
          <VoiceDictationInput label="If Current Payment Source is coded 11, specify:" value={formData.m0150PaymentSource11Specify} onChange={(e) => handleInputChange('m0150PaymentSource11Specify', e.target.value)} className="mt-2" />
          <VoiceDictationInput label="ADDITIONAL COMMENTS" value={formData.additionalComments} onChange={(e) => handleInputChange('additionalComments', e.target.value)} rows={3} className="mt-3" />
          <p className="text-xs text-gray-500 mt-2 text-center font-bold">End of Patient Tracking Information</p>
        </div>

        {/* Page 3 - Patient Contacts/Caregivers */}
        <div className="bg-white p-4 rounded border mb-4">
          <h4 className="font-bold text-sm mb-3 border-b pb-2">Section A Administrative Information (Continued) - Page 3</h4>
          <h5 className="font-bold text-sm mb-2">PATIENT CONTACTS/CAREGIVERS</h5>
          
          <div className="mb-4">
            <Label className="text-sm">Present during this visit:</Label>
            <div className="flex flex-wrap gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.presentDuringVisitFamily} onChange={(e) => handleInputChange('presentDuringVisitFamily', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Family member(s)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.presentDuringVisitRepresentative} onChange={(e) => handleInputChange('presentDuringVisitRepresentative', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Representative</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.presentDuringVisitCaregivers} onChange={(e) => handleInputChange('presentDuringVisitCaregivers', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Caregiver(s)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.presentDuringVisitOther} onChange={(e) => handleInputChange('presentDuringVisitOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
              {formData.presentDuringVisitOther && <VoiceDictationInput value={formData.presentDuringVisitOtherSpecify} onChange={(e) => handleInputChange('presentDuringVisitOtherSpecify', e.target.value)} className="w-40" />}
            </div>
          </div>
          
          <div className="mb-4">
            <Label className="text-sm">ROC Assessment: Contact information confirmed with:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.rocContactInfoConfirmedWithPatient} onChange={(e) => handleInputChange('rocContactInfoConfirmedWithPatient', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Patient</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.rocChangesDocumented} onChange={(e) => handleInputChange('rocChangesDocumented', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Changes documented</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.rocNoChanges} onChange={(e) => handleInputChange('rocNoChanges', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No changes</span></label>
            </div>
          </div>
          
          <div className="mb-4">
            <Label className="text-sm">Does the patient have a representative?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="hasRep" checked={formData.doesPatientHaveRepresentative === 'No'} onChange={() => handleInputChange('doesPatientHaveRepresentative', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="hasRep" checked={formData.doesPatientHaveRepresentative === 'Yes'} onChange={() => handleInputChange('doesPatientHaveRepresentative', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.doesPatientHaveRepresentative === 'Yes' && (
              <div className="ml-4 mt-2">
                <Label className="text-sm">If yes, is the person:</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.representativeIsCourtDeclared} onChange={(e) => handleInputChange('representativeIsCourtDeclared', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Court declared</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.representativeIsPatientSelected} onChange={(e) => handleInputChange('representativeIsPatientSelected', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Patient selected</span></label>
                </div>
              </div>
            )}
          </div>

          {/* Representative and Emergency Contact side by side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Representative */}
            <div className="bg-gray-50 p-3 rounded">
              <h6 className="font-bold text-sm mb-2">Representative</h6>
              <VoiceDictationInput label="Representative Name:" value={formData.representativeName} onChange={(e) => handleInputChange('representativeName', e.target.value)} className="mb-2" />
              <Label className="text-sm">Relationship:</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.representativeRelationshipFamily} onChange={(e) => handleInputChange('representativeRelationshipFamily', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Family</span></label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.representativeRelationshipFriend} onChange={(e) => handleInputChange('representativeRelationshipFriend', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Friend</span></label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.representativeRelationshipOther} onChange={(e) => handleInputChange('representativeRelationshipOther', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Other:</span></label>
              </div>
              {formData.representativeRelationshipOther && <VoiceDictationInput value={formData.representativeRelationshipOtherSpecify} onChange={(e) => handleInputChange('representativeRelationshipOtherSpecify', e.target.value)} placeholder="Specify" className="mb-2" />}
              <VoiceDictationInput label="Address:" value={formData.representativeAddress} onChange={(e) => handleInputChange('representativeAddress', e.target.value)} className="mb-2" />
              <div className="grid grid-cols-3 gap-1 mb-2">
                <VoiceDictationInput label="City:" value={formData.representativeCity} onChange={(e) => handleInputChange('representativeCity', e.target.value)} />
                <VoiceDictationInput label="State:" value={formData.representativeState} onChange={(e) => handleInputChange('representativeState', e.target.value)} />
                <VoiceDictationInput label="ZIP:" value={formData.representativeZipCode} onChange={(e) => handleInputChange('representativeZipCode', e.target.value)} />
              </div>
              <VoiceDictationInput label="Phone:" value={formData.representativePhone} onChange={(e) => handleInputChange('representativePhone', e.target.value)} className="mb-2" />
              <VoiceDictationInput label="Email:" value={formData.representativeEmail} onChange={(e) => handleInputChange('representativeEmail', e.target.value)} />
            </div>
            
            {/* Emergency Contact */}
            <div className="bg-gray-50 p-3 rounded">
              <h6 className="font-bold text-sm mb-2">Emergency Contact</h6>
              <div className="flex flex-wrap gap-2 mb-2">
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.emergencyContactIsRepresentative} onChange={(e) => handleInputChange('emergencyContactIsRepresentative', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Representative</span></label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.emergencyContactIsCaregiver} onChange={(e) => handleInputChange('emergencyContactIsCaregiver', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Caregiver</span></label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.emergencyContactIsOther} onChange={(e) => handleInputChange('emergencyContactIsOther', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Other, if "Other"</span></label>
              </div>
              <VoiceDictationInput label="Emergency Contact Name:" value={formData.emergencyContactName} onChange={(e) => handleInputChange('emergencyContactName', e.target.value)} className="mb-2" />
              <Label className="text-sm">Relationship:</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.emergencyContactRelationshipFamily} onChange={(e) => handleInputChange('emergencyContactRelationshipFamily', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Family</span></label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.emergencyContactRelationshipFriend} onChange={(e) => handleInputChange('emergencyContactRelationshipFriend', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Friend</span></label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={formData.emergencyContactRelationshipOther} onChange={(e) => handleInputChange('emergencyContactRelationshipOther', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Other:</span></label>
              </div>
              {formData.emergencyContactRelationshipOther && <VoiceDictationInput value={formData.emergencyContactRelationshipOtherSpecify} onChange={(e) => handleInputChange('emergencyContactRelationshipOtherSpecify', e.target.value)} placeholder="Specify" className="mb-2" />}
              <VoiceDictationInput label="Address:" value={formData.emergencyContactAddress} onChange={(e) => handleInputChange('emergencyContactAddress', e.target.value)} className="mb-2" />
              <div className="grid grid-cols-3 gap-1 mb-2">
                <VoiceDictationInput label="City:" value={formData.emergencyContactCity} onChange={(e) => handleInputChange('emergencyContactCity', e.target.value)} />
                <VoiceDictationInput label="State:" value={formData.emergencyContactState} onChange={(e) => handleInputChange('emergencyContactState', e.target.value)} />
                <VoiceDictationInput label="ZIP:" value={formData.emergencyContactZipCode} onChange={(e) => handleInputChange('emergencyContactZipCode', e.target.value)} />
              </div>
              <VoiceDictationInput label="Phone:" value={formData.emergencyContactPhone} onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)} className="mb-2" />
              <VoiceDictationInput label="Email:" value={formData.emergencyContactEmail} onChange={(e) => handleInputChange('emergencyContactEmail', e.target.value)} />
            </div>
          </div>

          {/* Primary Caregivers */}
          <div className="mt-4">
            <Label className="text-sm font-bold">Primary caregiver(s) other than patient:</Label>
            <div className="flex gap-4 mt-1 mb-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.primaryCaregiverNA} onChange={(e) => handleInputChange('primaryCaregiverNA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">N/A</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.primaryCaregiverNoneAvailable} onChange={(e) => handleInputChange('primaryCaregiverNoneAvailable', e.target.checked)} className="w-4 h-4" /><span className="text-sm">None available</span></label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Caregiver 1 */}
              <div className="bg-gray-50 p-3 rounded">
                <VoiceDictationInput label="Caregiver Name:" value={formData.caregiver1Name} onChange={(e) => handleInputChange('caregiver1Name', e.target.value)} className="mb-2" />
                <Label className="text-sm">Relationship:</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.caregiver1RelationshipFamily} onChange={(e) => handleInputChange('caregiver1RelationshipFamily', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Family</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.caregiver1RelationshipFriend} onChange={(e) => handleInputChange('caregiver1RelationshipFriend', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Friend</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.caregiver1RelationshipOther} onChange={(e) => handleInputChange('caregiver1RelationshipOther', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Other:</span></label>
                </div>
                <VoiceDictationInput label="Address:" value={formData.caregiver1Address} onChange={(e) => handleInputChange('caregiver1Address', e.target.value)} className="mb-2" />
                <div className="grid grid-cols-3 gap-1 mb-2">
                  <VoiceDictationInput label="City:" value={formData.caregiver1City} onChange={(e) => handleInputChange('caregiver1City', e.target.value)} />
                  <VoiceDictationInput label="State:" value={formData.caregiver1State} onChange={(e) => handleInputChange('caregiver1State', e.target.value)} />
                  <VoiceDictationInput label="ZIP:" value={formData.caregiver1ZipCode} onChange={(e) => handleInputChange('caregiver1ZipCode', e.target.value)} />
                </div>
                <VoiceDictationInput label="Phone:" value={formData.caregiver1Phone} onChange={(e) => handleInputChange('caregiver1Phone', e.target.value)} className="mb-2" />
                <VoiceDictationInput label="Email:" value={formData.caregiver1Email} onChange={(e) => handleInputChange('caregiver1Email', e.target.value)} />
              </div>
              
              {/* Caregiver 2 */}
              <div className="bg-gray-50 p-3 rounded">
                <VoiceDictationInput label="Caregiver Name:" value={formData.caregiver2Name} onChange={(e) => handleInputChange('caregiver2Name', e.target.value)} className="mb-2" />
                <Label className="text-sm">Relationship:</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.caregiver2RelationshipFamily} onChange={(e) => handleInputChange('caregiver2RelationshipFamily', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Family</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.caregiver2RelationshipFriend} onChange={(e) => handleInputChange('caregiver2RelationshipFriend', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Friend</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.caregiver2RelationshipOther} onChange={(e) => handleInputChange('caregiver2RelationshipOther', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Other:</span></label>
                </div>
                <VoiceDictationInput label="Address:" value={formData.caregiver2Address} onChange={(e) => handleInputChange('caregiver2Address', e.target.value)} className="mb-2" />
                <div className="grid grid-cols-3 gap-1 mb-2">
                  <VoiceDictationInput label="City:" value={formData.caregiver2City} onChange={(e) => handleInputChange('caregiver2City', e.target.value)} />
                  <VoiceDictationInput label="State:" value={formData.caregiver2State} onChange={(e) => handleInputChange('caregiver2State', e.target.value)} />
                  <VoiceDictationInput label="ZIP:" value={formData.caregiver2ZipCode} onChange={(e) => handleInputChange('caregiver2ZipCode', e.target.value)} />
                </div>
                <VoiceDictationInput label="Phone:" value={formData.caregiver2Phone} onChange={(e) => handleInputChange('caregiver2Phone', e.target.value)} className="mb-2" />
                <VoiceDictationInput label="Email:" value={formData.caregiver2Email} onChange={(e) => handleInputChange('caregiver2Email', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Paid Service */}
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <Label className="text-sm font-bold">Paid service other than home health staff:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="paidService" checked={formData.paidServiceOtherThanHomeHealth === 'No'} onChange={() => handleInputChange('paidServiceOtherThanHomeHealth', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="paidService" checked={formData.paidServiceOtherThanHomeHealth === 'Yes'} onChange={() => handleInputChange('paidServiceOtherThanHomeHealth', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.paidServiceOtherThanHomeHealth === 'Yes' && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                <VoiceDictationInput label="Company name:" value={formData.paidServiceCompanyName} onChange={(e) => handleInputChange('paidServiceCompanyName', e.target.value)} />
                <VoiceDictationInput label="Phone number:" value={formData.paidServicePhone} onChange={(e) => handleInputChange('paidServicePhone', e.target.value)} />
                <VoiceDictationInput label="Contact name:" value={formData.paidServiceContactName} onChange={(e) => handleInputChange('paidServiceContactName', e.target.value)} />
              </div>
            )}
          </div>

          {/* Critical Situation Contact */}
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <Label className="text-sm">If the caregiver(s) are not available, is there anyone who could be contacted in a critical situation?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="criticalContact" checked={formData.criticalSituationContactAvailable === 'No'} onChange={() => handleInputChange('criticalSituationContactAvailable', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="criticalContact" checked={formData.criticalSituationContactAvailable === 'Yes'} onChange={() => handleInputChange('criticalSituationContactAvailable', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.criticalSituationContactAvailable === 'Yes' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <VoiceDictationInput label="Name:" value={formData.criticalSituationContactName} onChange={(e) => handleInputChange('criticalSituationContactName', e.target.value)} />
                <VoiceDictationInput label="Phone number:" value={formData.criticalSituationContactPhone} onChange={(e) => handleInputChange('criticalSituationContactPhone', e.target.value)} />
              </div>
            )}
          </div>
        </div>

        {/* Supportive Assistance */}
        <div className="bg-white p-4 rounded border mb-4">
          <h5 className="font-bold text-sm mb-3">SUPPORTIVE ASSISTANCE</h5>
          <Label className="text-sm">Prior to this admission, how often did the patient receive assistance with their ADLs/IADLs, from any caregiver(s)?</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            {['None received', 'At least daily', 'One to two times per week', 'Three or more times per week', 'Less often than weekly', 'Unknown'].map(opt => (
              <label key={opt} className="flex items-center gap-2">
                <input type="radio" name="priorAssistance" checked={formData.priorAssistanceFrequency === opt} onChange={() => handleInputChange('priorAssistanceFrequency', opt)} className="w-4 h-4" />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Type(s) of assistance provided:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeNone} onChange={(e) => handleInputChange('assistanceTypeNone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No assistance</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeMeals} onChange={(e) => handleInputChange('assistanceTypeMeals', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Meals</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeADLs} onChange={(e) => handleInputChange('assistanceTypeADLs', e.target.checked)} className="w-4 h-4" /><span className="text-sm">ADLs</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeTransportation} onChange={(e) => handleInputChange('assistanceTypeTransportation', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Transportation</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeSupervisionSupport} onChange={(e) => handleInputChange('assistanceTypeSupervisionSupport', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Supervision/Support</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeMedications} onChange={(e) => handleInputChange('assistanceTypeMedications', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Medications</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeHomeMaintenance} onChange={(e) => handleInputChange('assistanceTypeHomeMaintenance', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Home Maintenance</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.assistanceTypeOther} onChange={(e) => handleInputChange('assistanceTypeOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.assistanceTypeOther && <VoiceDictationInput value={formData.assistanceTypeOtherSpecify} onChange={(e) => handleInputChange('assistanceTypeOtherSpecify', e.target.value)} className="mt-2" />}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <Label className="text-sm">Caregiver(s) willing to assist?</Label>
              <div className="flex gap-3 mt-1">
                {['Yes', 'No', 'Unknown'].map(opt => (
                  <label key={opt} className="flex items-center gap-2">
                    <input type="radio" name="caregiverWilling" checked={formData.caregiverWillingToAssist === opt} onChange={() => handleInputChange('caregiverWillingToAssist', opt)} className="w-4 h-4" />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
              {(formData.caregiverWillingToAssist === 'No' || formData.caregiverWillingToAssist === 'Unknown') && (
                <VoiceDictationInput label="If no or unknown, explain:" value={formData.caregiverWillingToAssistExplain} onChange={(e) => handleInputChange('caregiverWillingToAssistExplain', e.target.value)} className="mt-2" />
              )}
            </div>
            <div>
              <Label className="text-sm">Does the caregiver need training to assist the patient?</Label>
              <div className="flex gap-3 mt-1">
                {['Yes', 'No', 'Unknown'].map(opt => (
                  <label key={opt} className="flex items-center gap-2">
                    <input type="radio" name="caregiverTraining" checked={formData.caregiverNeedsTraining === opt} onChange={() => handleInputChange('caregiverNeedsTraining', opt)} className="w-4 h-4" />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
              {(formData.caregiverNeedsTraining === 'No' || formData.caregiverNeedsTraining === 'Unknown') && (
                <VoiceDictationInput label="If no or unknown, explain:" value={formData.caregiverNeedsTrainingExplain} onChange={(e) => handleInputChange('caregiverNeedsTrainingExplain', e.target.value)} className="mt-2" />
              )}
            </div>
          </div>
          
          {/* Caregiver Availability Schedule */}
          <div className="mt-4">
            <Label className="text-sm font-bold">List below the hours and days a caregiver is available to provide cares.</Label>
            <label className="flex items-center gap-2 mt-1 mb-2">
              <input type="checkbox" checked={formData.caregiverNoSetSchedule} onChange={(e) => handleInputChange('caregiverNoSetSchedule', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">There is no set schedule for availability</span>
            </label>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2"></th>
                    <th className="border p-2">AM HOURS</th>
                    <th className="border p-2">PM HOURS</th>
                    <th className="border p-2">NIGHTS</th>
                  </tr>
                </thead>
                <tbody>
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                    <tr key={day}>
                      <td className="border p-2 font-medium">{day.toUpperCase()}</td>
                      <td className="border p-1"><VoiceDictationInput value={formData[`caregiverAvail${day}AM`]} onChange={(e) => handleInputChange(`caregiverAvail${day}AM`, e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData[`caregiverAvail${day}PM`]} onChange={(e) => handleInputChange(`caregiverAvail${day}PM`, e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData[`caregiverAvail${day}Night`]} onChange={(e) => handleInputChange(`caregiverAvail${day}Night`, e.target.value)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Advance Directives */}
        <div className="bg-white p-4 rounded border mb-4">
          <h5 className="font-bold text-sm mb-3">ADVANCE DIRECTIVES</h5>
          
          <div className="mb-3">
            <Label className="text-sm">Does the patient have a Living Will?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="livingWill" checked={formData.hasLivingWill === 'No'} onChange={() => handleInputChange('hasLivingWill', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="livingWill" checked={formData.hasLivingWill === 'Yes'} onChange={() => handleInputChange('hasLivingWill', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
          </div>
          
          <div className="mb-3">
            <Label className="text-sm">Discussed and literature provided during this visit to the:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectivesDiscussedWithPatient} onChange={(e) => handleInputChange('advanceDirectivesDiscussedWithPatient', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Patient</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectivesDiscussedWithFamily} onChange={(e) => handleInputChange('advanceDirectivesDiscussedWithFamily', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Family member</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectivesDiscussedWithRepresentative} onChange={(e) => handleInputChange('advanceDirectivesDiscussedWithRepresentative', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Representative</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectivesDiscussedWithCaregiver} onChange={(e) => handleInputChange('advanceDirectivesDiscussedWithCaregiver', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Caregiver</span></label>
            </div>
          </div>
          
          <div className="mb-3">
            <Label className="text-sm">Does the patient have an order for the following Advance Directives?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="advDirectiveOrders" checked={formData.hasAdvanceDirectiveOrders === 'No'} onChange={() => handleInputChange('hasAdvanceDirectiveOrders', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="advDirectiveOrders" checked={formData.hasAdvanceDirectiveOrders === 'Yes'} onChange={() => handleInputChange('hasAdvanceDirectiveOrders', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.hasAdvanceDirectiveOrders === 'Yes' && (
              <div className="ml-4 mt-2">
                <Label className="text-xs">If yes, check all that apply:</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectiveNoCPR} onChange={(e) => handleInputChange('advanceDirectiveNoCPR', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Cardiopulmonary Resuscitation (CPR)</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectiveDNI} onChange={(e) => handleInputChange('advanceDirectiveDNI', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Do Not Intubate (DNI)</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectiveDNR} onChange={(e) => handleInputChange('advanceDirectiveDNR', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Do Not Resuscitate (DNR)</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.advanceDirectiveNoArtificialNutrition} onChange={(e) => handleInputChange('advanceDirectiveNoArtificialNutrition', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Artificial Nutrition and Hydration</span></label>
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <VoiceDictationInput label="Medical/Durable Power of Attorney Name:" value={formData.medicalPowerOfAttorneyName} onChange={(e) => handleInputChange('medicalPowerOfAttorneyName', e.target.value)} />
              <VoiceDictationInput label="Phone #:" value={formData.medicalPowerOfAttorneyPhone} onChange={(e) => handleInputChange('medicalPowerOfAttorneyPhone', e.target.value)} className="mt-2" />
            </div>
            <div>
              <VoiceDictationInput label="Financial Power of Attorney Name:" value={formData.financialPowerOfAttorneyName} onChange={(e) => handleInputChange('financialPowerOfAttorneyName', e.target.value)} />
              <VoiceDictationInput label="Phone #:" value={formData.financialPowerOfAttorneyPhone} onChange={(e) => handleInputChange('financialPowerOfAttorneyPhone', e.target.value)} className="mt-2" />
            </div>
          </div>
          
          <VoiceDictationInput label="State specific form(s):" value={formData.stateSpecificForms} onChange={(e) => handleInputChange('stateSpecificForms', e.target.value)} className="mt-3" />
          
          <div className="mt-3">
            <Label className="text-sm">Copies on file with:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.copiesOnFileWithPCP} onChange={(e) => handleInputChange('copiesOnFileWithPCP', e.target.checked)} className="w-4 h-4" /><span className="text-sm">PCP</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.copiesOnFileWithOther} onChange={(e) => handleInputChange('copiesOnFileWithOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
              {formData.copiesOnFileWithOther && <VoiceDictationInput value={formData.copiesOnFileWithOtherSpecify} onChange={(e) => handleInputChange('copiesOnFileWithOtherSpecify', e.target.value)} className="w-40" />}
            </div>
          </div>
          
          <VoiceDictationInput label="Comments:" value={formData.advanceDirectivesComments} onChange={(e) => handleInputChange('advanceDirectivesComments', e.target.value)} rows={2} className="mt-3" />
        </div>

        {/* Page 4 - Section A Continued */}
        <div className="bg-white p-4 rounded border mb-4">
          <h4 className="font-bold text-sm mb-3 border-b pb-2">Section A Administrative Information (Continued) - Page 4</h4>
          
          {/* A1110 Language */}
          <div className="mb-4">
            <Label className="font-bold text-sm">A1110. Language (Enter Code)</Label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <VoiceDictationInput label="A. What is your preferred language?" value={formData.a1110PreferredLanguage} onChange={(e) => handleInputChange('a1110PreferredLanguage', e.target.value)} />
              <div>
                <Label className="text-sm">B. Do you need or want an interpreter to communicate with a doctor or health care staff?</Label>
                <p className="text-xs text-gray-500">0. No  1. Yes  9. Unable to determine</p>
                <VoiceDictationInput value={formData.a1110NeedsInterpreter} onChange={(e) => handleInputChange('a1110NeedsInterpreter', e.target.value)} placeholder="Enter 0, 1, or 9" />
              </div>
            </div>
          </div>
          
          {/* Language Barriers */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <Label className="font-bold text-sm">LANGUAGE BARRIER(S)</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.languageBarrierNoProblem} onChange={(e) => handleInputChange('languageBarrierNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.languageBarrierNeedsInterpreter} onChange={(e) => handleInputChange('languageBarrierNeedsInterpreter', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Needs interpreter</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.languageBarrierSignLanguage} onChange={(e) => handleInputChange('languageBarrierSignLanguage', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Sign language (type):</span></label>
            </div>
            {formData.languageBarrierSignLanguage && <VoiceDictationInput value={formData.languageBarrierSignLanguageType} onChange={(e) => handleInputChange('languageBarrierSignLanguageType', e.target.value)} className="mt-2 w-48" />}
            <div className="mt-2">
              <Label className="text-sm">Aphasic:</Label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.languageBarrierAphasicReceptive} onChange={(e) => handleInputChange('languageBarrierAphasicReceptive', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Receptive</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.languageBarrierAphasicExpressive} onChange={(e) => handleInputChange('languageBarrierAphasicExpressive', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Expressive</span></label>
              </div>
            </div>
          </div>
          
          {/* M0080 and M0090 */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="font-bold text-sm">M0080. Discipline of Person Completing Assessment (Enter Code)</Label>
              <p className="text-xs text-gray-500">1. RN  2. PT  3. SLP/ST  4. OT</p>
              <VoiceDictationInput value={formData.m0080Discipline} onChange={(e) => handleInputChange('m0080Discipline', e.target.value)} placeholder="Enter 1, 2, 3, or 4" className="mt-1" />
            </div>
            <div>
              <Label className="font-bold text-sm">M0090. Date Assessment Completed (Month/Day/Year)</Label>
              <p className="text-xs text-gray-500">Complete M0090 using the date of the day information was last collected.</p>
              <VoiceDictationInput value={formData.m0090DateAssessmentCompleted} onChange={(e) => handleInputChange('m0090DateAssessmentCompleted', e.target.value)} placeholder="MM/DD/YYYY" className="mt-1" />
            </div>
          </div>
          
          {/* M0100 Reason for Assessment */}
          <div className="mb-4">
            <Label className="font-bold text-sm">M0100. This Assessment is Currently Being Completed for the Following Reason (Enter Code)</Label>
            <p className="text-sm mt-1">Start/Resumption of Care:</p>
            <p className="text-xs text-gray-500">1. Start of care - further visits planned</p>
            <p className="text-xs text-gray-500">3. Resumption of care (after inpatient stay)</p>
            <VoiceDictationInput value={formData.m0100ReasonForAssessment} onChange={(e) => handleInputChange('m0100ReasonForAssessment', e.target.value)} placeholder="Enter 1 or 3" className="mt-1" />
            <p className="text-xs text-gray-500 mt-1">When ROC, review patient tracking information and complete M0032.</p>
          </div>
          
          {/* M0102 and M0104 */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="font-bold text-sm">M0102. Date of Physician-ordered Start of Care (Resumption of Care)</Label>
              <p className="text-xs text-gray-500">If the physician indicated a specific start of care (resumption of care) date when the patient was referred for home health services, record the date specified.</p>
              <VoiceDictationInput value={formData.m0102PhysicianOrderedSOCROCDate} onChange={(e) => handleInputChange('m0102PhysicianOrderedSOCROCDate', e.target.value)} placeholder="MM/DD/YYYY" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">→ Skip to M0110, Episode Timing, if date entered</p>
              <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={formData.m0102NA} onChange={(e) => handleInputChange('m0102NA', e.target.checked)} className="w-4 h-4" /><span className="text-xs">NA – No specific SOC/ROC date ordered by physician</span></label>
              <VoiceDictationInput label="If SOC/ROC was not initiated on ordered SOC/ROC date, explain circumstances:" value={formData.m0102NotInitiatedExplain} onChange={(e) => handleInputChange('m0102NotInitiatedExplain', e.target.value)} rows={2} className="mt-2" />
            </div>
            <div>
              <Label className="font-bold text-sm">M0104. Date of Referral</Label>
              <p className="text-xs text-gray-500">Indicate the date that the written or verbal referral for initiation or resumption of care was received by the HHA.</p>
              <VoiceDictationInput value={formData.m0104DateOfReferral} onChange={(e) => handleInputChange('m0104DateOfReferral', e.target.value)} placeholder="MM/DD/YYYY" className="mt-1" />
              <VoiceDictationInput label="If SOC/ROC was not initiated within 2 days of the referral date/discharge date, explain circumstances:" value={formData.m0104NotInitiatedExplain} onChange={(e) => handleInputChange('m0104NotInitiatedExplain', e.target.value)} rows={2} className="mt-2" />
            </div>
          </div>
          
          {/* M0110 Episode Timing */}
          <div className="mb-4">
            <Label className="font-bold text-sm">M0110. Episode Timing (Enter Code)</Label>
            <p className="text-xs text-gray-500">Is the Medicare home health payment episode, for which this assessment will define a case mix group, an "early" episode or a "later" episode in the patient's current sequence of adjacent Medicare home health payment episodes?</p>
            <p className="text-xs text-gray-500 mt-1">1. Early  2. Later  UK Unknown  NA Not Applicable: No Medicare case mix group to be defined by this assessment.</p>
            <VoiceDictationInput value={formData.m0110EpisodeTiming} onChange={(e) => handleInputChange('m0110EpisodeTiming', e.target.value)} placeholder="Enter 1, 2, UK, or NA" className="mt-1" />
          </div>
          
          {/* A1250 Transportation */}
          <div className="mb-4">
            <Label className="font-bold text-sm">A1250. Transportation (NACHC©)</Label>
            <p className="text-sm">Has lack of transportation kept you from medical appointments, meetings, work, or from getting things needed for daily living?</p>
            <p className="text-xs text-gray-500">↓ Check all that apply</p>
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1250TransportationA} onChange={(e) => handleInputChange('a1250TransportationA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">A. Yes, it has kept me from medical appointments or from getting my medications</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1250TransportationB} onChange={(e) => handleInputChange('a1250TransportationB', e.target.checked)} className="w-4 h-4" /><span className="text-sm">B. Yes, it has kept me from non-medical meetings, appointments, work, or from getting things that I need</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1250TransportationC} onChange={(e) => handleInputChange('a1250TransportationC', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C. No</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1250TransportationX} onChange={(e) => handleInputChange('a1250TransportationX', e.target.checked)} className="w-4 h-4" /><span className="text-sm">X. Patient unable to respond</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.a1250TransportationY} onChange={(e) => handleInputChange('a1250TransportationY', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Y. Patient declines to respond</span></label>
            </div>
            <p className="text-xs text-gray-400 mt-2 italic">Adapted from: NACHC© 2019. National Association of Community Health Centers, Inc., Association of Asian Pacific Community Health Organizations, Oregon Primary Care Association. PRAPARE and its resources are proprietary information of NACHC and its partners, intended for use by NACHC, its partners, and authorized recipients. Do not publish, copy, or distribute this information in part or whole without written consent from NACHC.</p>
          </div>
        </div>

        {/* Page 5 - Patient History */}
        <div className="bg-white p-4 rounded border mb-4">
          <h4 className="font-bold text-sm mb-3 border-b pb-2">Section A Administrative Information (Continued) - Page 5</h4>
          <h5 className="font-bold text-sm mb-3">PATIENT HISTORY</h5>
          
          <VoiceDictationInput label="PRIMARY REASON FOR HOME HEALTH ADMISSION: (review Face-to-Face)" value={formData.primaryReasonForHomeHealthAdmission} onChange={(e) => handleInputChange('primaryReasonForHomeHealthAdmission', e.target.value)} rows={3} className="mb-4" />
          
          <div className="mb-4">
            <Label className="font-bold text-sm">PERTINENT HISTORY AND/OR PREVIOUS OUTCOMES:</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyHypertension} onChange={(e) => handleInputChange('historyHypertension', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hypertension</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyHypotension} onChange={(e) => handleInputChange('historyHypotension', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hypotension</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyCardiac} onChange={(e) => handleInputChange('historyCardiac', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cardiac</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyRespiratory} onChange={(e) => handleInputChange('historyRespiratory', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Respiratory</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyOsteoporosis} onChange={(e) => handleInputChange('historyOsteoporosis', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Osteoporosis</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyFractures} onChange={(e) => handleInputChange('historyFractures', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Fractures</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyCancer} onChange={(e) => handleInputChange('historyCancer', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cancer (site:</span></label>
              {formData.historyCancer && <VoiceDictationInput value={formData.historyCancerSite} onChange={(e) => handleInputChange('historyCancerSite', e.target.value)} placeholder="site" className="w-24" />}
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyInfection} onChange={(e) => handleInputChange('historyInfection', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Infection</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyImmunosuppressed} onChange={(e) => handleInputChange('historyImmunosuppressed', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Immunosuppressed</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyOpenWound} onChange={(e) => handleInputChange('historyOpenWound', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Open Wound etiology:</span></label>
              {formData.historyOpenWound && <VoiceDictationInput value={formData.historyOpenWoundEtiology} onChange={(e) => handleInputChange('historyOpenWoundEtiology', e.target.value)} placeholder="etiology" className="w-32" />}
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyFallsWithoutInjury} onChange={(e) => handleInputChange('historyFallsWithoutInjury', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Falls without injury</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyFallsWithInjury} onChange={(e) => handleInputChange('historyFallsWithInjury', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Falls with injury</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyHospitalizations} onChange={(e) => handleInputChange('historyHospitalizations', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hospitalizations</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyERVisits} onChange={(e) => handleInputChange('historyERVisits', e.target.checked)} className="w-4 h-4" /><span className="text-sm">ER Visits</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.historyRecentSurgeries} onChange={(e) => handleInputChange('historyRecentSurgeries', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Recent Surgeries</span></label>
            </div>
          </div>
          
          <VoiceDictationInput label="Pertinent details:" value={formData.pertinentDetails} onChange={(e) => handleInputChange('pertinentDetails', e.target.value)} rows={2} className="mb-3" />
          
          <div className="mb-4">
            <Label className="text-sm">Surgery/Procedure(s) expected in future:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="surgeryExpected" checked={formData.surgeryProcedureExpectedInFuture === 'No'} onChange={() => handleInputChange('surgeryProcedureExpectedInFuture', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="surgeryExpected" checked={formData.surgeryProcedureExpectedInFuture === 'Yes'} onChange={() => handleInputChange('surgeryProcedureExpectedInFuture', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.surgeryProcedureExpectedInFuture === 'Yes' && (
              <VoiceDictationInput label="If yes, explain:" value={formData.surgeryProcedureExpectedExplain} onChange={(e) => handleInputChange('surgeryProcedureExpectedExplain', e.target.value)} className="mt-2" />
            )}
          </div>
          
          {/* Vital Signs */}
          <div className="bg-gray-50 p-3 rounded mb-4">
            <h6 className="font-bold text-sm mb-3">VITAL SIGNS:</h6>
            
            {/* Blood Pressure */}
            <div className="mb-4">
              <Label className="font-bold text-sm">Blood Pressure:</Label>
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2"></th>
                      <th className="border p-2">Left</th>
                      <th className="border p-2">Right</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2 font-medium">Sitting/Lying</td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureLeftSittingLying} onChange={(e) => handleInputChange('bloodPressureLeftSittingLying', e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureRightSittingLying} onChange={(e) => handleInputChange('bloodPressureRightSittingLying', e.target.value)} /></td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-medium">Standing</td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureLeftStanding} onChange={(e) => handleInputChange('bloodPressureLeftStanding', e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureRightStanding} onChange={(e) => handleInputChange('bloodPressureRightStanding', e.target.value)} /></td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-medium">At rest</td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureLeftAtRest} onChange={(e) => handleInputChange('bloodPressureLeftAtRest', e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureRightAtRest} onChange={(e) => handleInputChange('bloodPressureRightAtRest', e.target.value)} /></td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-medium">With activity</td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureLeftWithActivity} onChange={(e) => handleInputChange('bloodPressureLeftWithActivity', e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureRightWithActivity} onChange={(e) => handleInputChange('bloodPressureRightWithActivity', e.target.value)} /></td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-medium">Post activity</td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureLeftPostActivity} onChange={(e) => handleInputChange('bloodPressureLeftPostActivity', e.target.value)} /></td>
                      <td className="border p-1"><VoiceDictationInput value={formData.bloodPressureRightPostActivity} onChange={(e) => handleInputChange('bloodPressureRightPostActivity', e.target.value)} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bloodPressureObserved} onChange={(e) => handleInputChange('bloodPressureObserved', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Observed</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bloodPressureReported} onChange={(e) => handleInputChange('bloodPressureReported', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Reported</span></label>
              </div>
            </div>
            
            {/* Temperature */}
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <VoiceDictationInput label="Temperature:" value={formData.temperature} onChange={(e) => handleInputChange('temperature', e.target.value)} />
                <div className="flex flex-wrap gap-2 mt-1">
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.temperatureMethodOral} onChange={(e) => handleInputChange('temperatureMethodOral', e.target.checked)} className="w-3 h-3" /><span className="text-xs">F Oral</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.temperatureMethodTemporalForehead} onChange={(e) => handleInputChange('temperatureMethodTemporalForehead', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Temporal/Forehead</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.temperatureMethodRectal} onChange={(e) => handleInputChange('temperatureMethodRectal', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Rectal</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.temperatureMethodAxillary} onChange={(e) => handleInputChange('temperatureMethodAxillary', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Axillary</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.temperatureMethodTympanic} onChange={(e) => handleInputChange('temperatureMethodTympanic', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Tympanic</span></label>
                </div>
              </div>
              <div>
                <VoiceDictationInput label="Pulse:" value={formData.pulse} onChange={(e) => handleInputChange('pulse', e.target.value)} />
                <div className="flex flex-wrap gap-2 mt-1">
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.pulseApical} onChange={(e) => handleInputChange('pulseApical', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Apical</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.pulseBrachial} onChange={(e) => handleInputChange('pulseBrachial', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Brachial</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.pulseRadial} onChange={(e) => handleInputChange('pulseRadial', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Radial</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.pulseCarotid} onChange={(e) => handleInputChange('pulseCarotid', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Carotid</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.pulseRegular} onChange={(e) => handleInputChange('pulseRegular', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Regular</span></label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={formData.pulseIrregular} onChange={(e) => handleInputChange('pulseIrregular', e.target.checked)} className="w-3 h-3" /><span className="text-xs">Irregular</span></label>
                </div>
              </div>
            </div>
            
            {/* Pulse Oximetry */}
            <div className="grid grid-cols-3 gap-4 mb-3">
              <VoiceDictationInput label="Pulse Oximetry: at rest %" value={formData.pulseOximetryAtRest} onChange={(e) => handleInputChange('pulseOximetryAtRest', e.target.value)} />
              <VoiceDictationInput label="after activity %" value={formData.pulseOximetryAfterActivity} onChange={(e) => handleInputChange('pulseOximetryAfterActivity', e.target.value)} />
              <VoiceDictationInput label="(specify activity):" value={formData.pulseOximetryActivitySpecify} onChange={(e) => handleInputChange('pulseOximetryActivitySpecify', e.target.value)} />
            </div>
            
            {/* Respirations */}
            <div className="mb-3">
              <VoiceDictationInput label="Respirations:" value={formData.respirations} onChange={(e) => handleInputChange('respirations', e.target.value)} className="w-32" />
              <div className="flex flex-wrap gap-3 mt-1">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respirationsRegular} onChange={(e) => handleInputChange('respirationsRegular', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Regular</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respirationsIrregular} onChange={(e) => handleInputChange('respirationsIrregular', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Irregular</span></label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2"><span className="text-sm">Apnea periods</span></label>
                  <VoiceDictationInput value={formData.respirationsApneaPeriods} onChange={(e) => handleInputChange('respirationsApneaPeriods', e.target.value)} placeholder="sec." className="w-20" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Immunizations */}
          <div className="bg-gray-50 p-3 rounded mb-4">
            <h6 className="font-bold text-sm mb-2">IMMUNIZATIONS:</h6>
            <Label className="text-sm">Within the past 12 months:</Label>
            <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={formData.immunizationInfluenza} onChange={(e) => handleInputChange('immunizationInfluenza', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Influenza (specifically this year's flu season)</span></label>
            
            <Label className="text-sm mt-2 block">According to immunization guidelines:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.immunizationPneumonia} onChange={(e) => handleInputChange('immunizationPneumonia', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pneumonia</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.immunizationTetanus} onChange={(e) => handleInputChange('immunizationTetanus', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Tetanus</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.immunizationShingles} onChange={(e) => handleInputChange('immunizationShingles', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Shingles</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.immunizationHepatitisC} onChange={(e) => handleInputChange('immunizationHepatitisC', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hepatitis C</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.immunizationOther} onChange={(e) => handleInputChange('immunizationOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
              {formData.immunizationOther && <VoiceDictationInput value={formData.immunizationOtherSpecify} onChange={(e) => handleInputChange('immunizationOtherSpecify', e.target.value)} className="w-32" />}
            </div>
            
            <VoiceDictationInput label="Needs:" value={formData.immunizationNeeds} onChange={(e) => handleInputChange('immunizationNeeds', e.target.value)} className="mt-2" />
            
            <div className="mt-3">
              <Label className="text-sm font-bold">Last COVID-19 Vaccination:</Label>
              <div className="flex flex-wrap gap-3 mt-1">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.covidVaccineInitialSeries} onChange={(e) => handleInputChange('covidVaccineInitialSeries', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Initial vaccine series</span></label>
                <Label className="text-sm">Booster:</Label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.covidVaccineBooster1} onChange={(e) => handleInputChange('covidVaccineBooster1', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1st</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.covidVaccineBooster2} onChange={(e) => handleInputChange('covidVaccineBooster2', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2nd</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.covidVaccineBooster3} onChange={(e) => handleInputChange('covidVaccineBooster3', e.target.checked)} className="w-4 h-4" /><span className="text-sm">3rd</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.covidVaccineBooster4} onChange={(e) => handleInputChange('covidVaccineBooster4', e.target.checked)} className="w-4 h-4" /><span className="text-sm">4th</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.covidVaccineBooster5} onChange={(e) => handleInputChange('covidVaccineBooster5', e.target.checked)} className="w-4 h-4" /><span className="text-sm">5th</span></label>
              </div>
            </div>
            
            <VoiceDictationInput label="Medical restrictions or personal preferences impacting immunizations:" value={formData.medicalRestrictionsImpactingImmunizations} onChange={(e) => handleInputChange('medicalRestrictionsImpactingImmunizations', e.target.value)} rows={2} className="mt-3" />
          </div>
          
          {/* M1000 Inpatient Facilities */}
          <div className="bg-white p-3 rounded border mb-4">
            <Label className="font-bold text-sm">M1000. From which of the following Inpatient Facilities was the patient discharged within the past 14 days?</Label>
            <p className="text-xs text-gray-500 mb-2">↓ Check all that apply</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility1} onChange={(e) => handleInputChange('m1000InpatientFacility1', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1. Long-term nursing facility (NF)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility2} onChange={(e) => handleInputChange('m1000InpatientFacility2', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2. Skilled nursing facility (SNF/TCU)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility3} onChange={(e) => handleInputChange('m1000InpatientFacility3', e.target.checked)} className="w-4 h-4" /><span className="text-sm">3. Short-stay acute hospital (IPPS)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility4} onChange={(e) => handleInputChange('m1000InpatientFacility4', e.target.checked)} className="w-4 h-4" /><span className="text-sm">4. Long-term care hospital (LTCH)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility5} onChange={(e) => handleInputChange('m1000InpatientFacility5', e.target.checked)} className="w-4 h-4" /><span className="text-sm">5. Inpatient rehabilitation hospital or unit (IRF)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility6} onChange={(e) => handleInputChange('m1000InpatientFacility6', e.target.checked)} className="w-4 h-4" /><span className="text-sm">6. Psychiatric hospital or unit</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacility7} onChange={(e) => handleInputChange('m1000InpatientFacility7', e.target.checked)} className="w-4 h-4" /><span className="text-sm">7. Other (specify)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1000InpatientFacilityNA} onChange={(e) => handleInputChange('m1000InpatientFacilityNA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">NA Patient was not discharged from an inpatient facility</span></label>
            </div>
            {formData.m1000InpatientFacility7 && <VoiceDictationInput value={formData.m1000InpatientFacility7Specify} onChange={(e) => handleInputChange('m1000InpatientFacility7Specify', e.target.value)} placeholder="Specify other" className="mt-2" />}
            <p className="text-xs text-gray-500 mt-2">→ Skip to B0200, Hearing at SOC, Skip to B1300, Health Literacy at ROC</p>
            
            <VoiceDictationInput label="Name of inpatient facility(ies):" value={formData.nameOfInpatientFacilities} onChange={(e) => handleInputChange('nameOfInpatientFacilities', e.target.value)} className="mt-3" />
            
            <div className="mt-3">
              <Label className="font-bold text-sm">M1005. Inpatient Discharge Date (most recent)</Label>
              <VoiceDictationInput value={formData.m1005InpatientDischargeDate} onChange={(e) => handleInputChange('m1005InpatientDischargeDate', e.target.value)} placeholder="Month/Day/Year" className="mt-1" />
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1005UK} onChange={(e) => handleInputChange('m1005UK', e.target.checked)} className="w-4 h-4" /><span className="text-xs">UK - Unknown or Not Available</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1005NoInpatientAdmission} onChange={(e) => handleInputChange('m1005NoInpatientAdmission', e.target.checked)} className="w-4 h-4" /><span className="text-xs">No inpatient admission. Note: Observation stays are NOT an inpatient stay.</span></label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Placeholder functions for remaining sections - these will be implemented
  const renderSectionB = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section B Hearing, Speech, and Vision</h3>
        
        {/* B0200 Hearing */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">B0200. Hearing (Enter Code)</Label>
          <p className="text-sm mb-2">Ability to hear (with hearing aid or hearing appliances if normally used)</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Adequate – no difficulty in normal conversation, social interaction, listening to TV</p>
            <p>1. Minimal difficulty - difficulty in some environments (e.g., when person speaks softly, or setting is noisy)</p>
            <p>2. Moderate difficulty - speaker has to increase volume and speak distinctly</p>
            <p>3. Highly impaired - absence of useful hearing</p>
          </div>
          <VoiceDictationInput value={formData.b0200Hearing} onChange={(e) => handleInputChange('b0200Hearing', e.target.value)} placeholder="Enter 0, 1, 2, or 3" className="w-32" />
          
          <div className="mt-3 p-3 bg-gray-50 rounded">
            <Label className="font-bold text-sm">EARS:</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsNoProblem} onChange={(e) => handleInputChange('earsNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsHOHRight} onChange={(e) => handleInputChange('earsHOHRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">HOH: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsHOHLeft} onChange={(e) => handleInputChange('earsHOHLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsDeaf} onChange={(e) => handleInputChange('earsDeaf', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Deaf</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsHearingAidRight} onChange={(e) => handleInputChange('earsHearingAidRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hearing aid: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsHearingAidLeft} onChange={(e) => handleInputChange('earsHearingAidLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsVertigo} onChange={(e) => handleInputChange('earsVertigo', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Vertigo</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsTinnitusRight} onChange={(e) => handleInputChange('earsTinnitusRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Tinnitus: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsTinnitusLeft} onChange={(e) => handleInputChange('earsTinnitusLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsCochlearTransplant} onChange={(e) => handleInputChange('earsCochlearTransplant', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cochlear Transplant</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.earsOther} onChange={(e) => handleInputChange('earsOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other (specify):</span></label>
            </div>
            {formData.earsOther && <VoiceDictationInput value={formData.earsOtherSpecify} onChange={(e) => handleInputChange('earsOtherSpecify', e.target.value)} className="mt-2 w-64" />}
            
            <div className="mt-3">
              <Label className="text-sm">Does the hearing impairment interfere/impact their function/safety?</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2"><input type="radio" name="hearingImpact" checked={formData.hearingImpactFunctionSafety === 'No'} onChange={() => handleInputChange('hearingImpactFunctionSafety', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="hearingImpact" checked={formData.hearingImpactFunctionSafety === 'Yes'} onChange={() => handleInputChange('hearingImpactFunctionSafety', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
              </div>
              {formData.hearingImpactFunctionSafety === 'Yes' && <VoiceDictationInput label="If yes, explain:" value={formData.hearingImpactExplain} onChange={(e) => handleInputChange('hearingImpactExplain', e.target.value)} className="mt-2" />}
            </div>
          </div>
        </div>

        {/* B1000 Vision */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">B1000. Vision (Enter Code)</Label>
          <p className="text-sm mb-2">Ability to see in adequate light (with glasses or other visual appliances)</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Adequate – sees fine detail, such as regular print in newspapers/books</p>
            <p>1. Impaired – sees large print, but not regular print in newspapers/books</p>
            <p>2. Moderately impaired - limited vision; not able to see newspaper headlines but can identify objects</p>
            <p>3. Highly impaired – object identification in question, but eyes appear to follow objects</p>
            <p>4. Severely impaired – no vision or sees only light, colors or shapes; eyes do not appear to follow objects</p>
          </div>
          <VoiceDictationInput value={formData.b1000Vision} onChange={(e) => handleInputChange('b1000Vision', e.target.value)} placeholder="Enter 0, 1, 2, 3, or 4" className="w-32" />
          
          <div className="mt-3 p-3 bg-gray-50 rounded">
            <Label className="font-bold text-sm">EYES:</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesNoProblem} onChange={(e) => handleInputChange('eyesNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesPERRLA} onChange={(e) => handleInputChange('eyesPERRLA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">PERRLA</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesPupilsUnequal} onChange={(e) => handleInputChange('eyesPupilsUnequal', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pupils unequal</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesGlasses} onChange={(e) => handleInputChange('eyesGlasses', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Glasses</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesContactsRight} onChange={(e) => handleInputChange('eyesContactsRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Contacts: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesContactsLeft} onChange={(e) => handleInputChange('eyesContactsLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesGlaucomaRight} onChange={(e) => handleInputChange('eyesGlaucomaRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Glaucoma: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesGlaucomaLeft} onChange={(e) => handleInputChange('eyesGlaucomaLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesBlurredVisionRight} onChange={(e) => handleInputChange('eyesBlurredVisionRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Blurred vision: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesBlurredVisionLeft} onChange={(e) => handleInputChange('eyesBlurredVisionLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesCataractsRight} onChange={(e) => handleInputChange('eyesCataractsRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cataract(s): R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesCataractsLeft} onChange={(e) => handleInputChange('eyesCataractsLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesProsthesisRight} onChange={(e) => handleInputChange('eyesProsthesisRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Prosthesis: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesProsthesisLeft} onChange={(e) => handleInputChange('eyesProsthesisLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesScleralIcterus} onChange={(e) => handleInputChange('eyesScleralIcterus', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Scleral icterus/yellowing</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesDistortedVision} onChange={(e) => handleInputChange('eyesDistortedVision', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Distorted vision</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesInfections} onChange={(e) => handleInputChange('eyesInfections', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Infections:</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesBlindRight} onChange={(e) => handleInputChange('eyesBlindRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Blind: R</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesBlindLeft} onChange={(e) => handleInputChange('eyesBlindLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.eyesOther} onChange={(e) => handleInputChange('eyesOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.eyesOther && <VoiceDictationInput value={formData.eyesOtherSpecify} onChange={(e) => handleInputChange('eyesOtherSpecify', e.target.value)} className="mt-2 w-64" />}
            
            <div className="grid grid-cols-2 gap-4 mt-3">
              <VoiceDictationInput label="Cataract surgery: (Right) Date:" value={formData.eyesCataractSurgeryRightDate} onChange={(e) => handleInputChange('eyesCataractSurgeryRightDate', e.target.value)} />
              <VoiceDictationInput label="(Left) Date:" value={formData.eyesCataractSurgeryLeftDate} onChange={(e) => handleInputChange('eyesCataractSurgeryLeftDate', e.target.value)} />
            </div>
            
            <div className="mt-3">
              <Label className="text-sm">Does the impaired vision interfere/impact their function/safety?</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2"><input type="radio" name="visionImpact" checked={formData.visionImpactFunctionSafety === 'No'} onChange={() => handleInputChange('visionImpactFunctionSafety', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="visionImpact" checked={formData.visionImpactFunctionSafety === 'Yes'} onChange={() => handleInputChange('visionImpactFunctionSafety', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
              </div>
              {formData.visionImpactFunctionSafety === 'Yes' && <VoiceDictationInput label="If yes, explain:" value={formData.visionImpactExplain} onChange={(e) => handleInputChange('visionImpactExplain', e.target.value)} className="mt-2" />}
            </div>
          </div>
        </div>

        {/* NOSE */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">NOSE:</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.noseNoProblem} onChange={(e) => handleInputChange('noseNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.noseCongestion} onChange={(e) => handleInputChange('noseCongestion', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Congestion</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.noseEpistaxis} onChange={(e) => handleInputChange('noseEpistaxis', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Epistaxis</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.noseLossOfSmell} onChange={(e) => handleInputChange('noseLossOfSmell', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Loss of smell</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.noseSinusProblem} onChange={(e) => handleInputChange('noseSinusProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Sinus problem</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.noseOther} onChange={(e) => handleInputChange('noseOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other (specify):</span></label>
          </div>
          {formData.noseOther && <VoiceDictationInput value={formData.noseOtherSpecify} onChange={(e) => handleInputChange('noseOtherSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>

        {/* THROAT */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">THROAT:</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.throatNoProblem} onChange={(e) => handleInputChange('throatNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.throatDifficultySwallowing} onChange={(e) => handleInputChange('throatDifficultySwallowing', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Difficulty swallowing</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.throatHoarseness} onChange={(e) => handleInputChange('throatHoarseness', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hoarseness</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.throatLesions} onChange={(e) => handleInputChange('throatLesions', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Lesion(s)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.throatSoreThroat} onChange={(e) => handleInputChange('throatSoreThroat', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Sore throat</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.throatOther} onChange={(e) => handleInputChange('throatOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other (specify):</span></label>
          </div>
          {formData.throatOther && <VoiceDictationInput value={formData.throatOtherSpecify} onChange={(e) => handleInputChange('throatOtherSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>

        {/* MOUTH */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">MOUTH:</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthNoProblem} onChange={(e) => handleInputChange('mouthNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthMasses} onChange={(e) => handleInputChange('mouthMasses', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Mass(es)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthTumors} onChange={(e) => handleInputChange('mouthTumors', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Tumor(s)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthGingivitis} onChange={(e) => handleInputChange('mouthGingivitis', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Gingivitis</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthUlcerations} onChange={(e) => handleInputChange('mouthUlcerations', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Ulceration(s)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthToothache} onChange={(e) => handleInputChange('mouthToothache', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Toothache</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthLesions} onChange={(e) => handleInputChange('mouthLesions', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Lesion(s)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthNoDentation} onChange={(e) => handleInputChange('mouthNoDentation', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Dentation</span></label>
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            <Label className="text-sm">Dentures:</Label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthDenturesUpper} onChange={(e) => handleInputChange('mouthDenturesUpper', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Upper</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthDenturesLower} onChange={(e) => handleInputChange('mouthDenturesLower', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Lower</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthDenturesPartial} onChange={(e) => handleInputChange('mouthDenturesPartial', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Partial</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mouthOther} onChange={(e) => handleInputChange('mouthOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other (specify):</span></label>
          </div>
          {formData.mouthOther && <VoiceDictationInput value={formData.mouthOtherSpecify} onChange={(e) => handleInputChange('mouthOtherSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>

        {/* B1300 Health Literacy */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">B1300. Health Literacy (From Creative Commons©) (Enter Code)</Label>
          <p className="text-sm mb-2">How often do you need to have someone help you when you read instructions, pamphlets, or other written material from your doctor or pharmacy?</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Never</p>
            <p>1. Rarely</p>
            <p>2. Sometimes</p>
            <p>3. Often</p>
            <p>4. Always</p>
            <p>7. Patient declines to respond</p>
            <p>8. Patient unable to respond</p>
          </div>
          <VoiceDictationInput value={formData.b1300HealthLiteracy} onChange={(e) => handleInputChange('b1300HealthLiteracy', e.target.value)} placeholder="Enter 0-4, 7, or 8" className="w-32" />
          <p className="text-xs text-gray-400 mt-2 italic">The Single Item Literacy Screener is licensed under a Creative Commons Attribution Noncommercial 4.0 International License.</p>
          
          <div className="mt-3 p-3 bg-gray-50 rounded">
            <Label className="font-bold text-sm">LEARNING BARRIER(S):</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierNoProblem} onChange={(e) => handleInputChange('learningBarrierNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierMentalHealthDisability} onChange={(e) => handleInputChange('learningBarrierMentalHealthDisability', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Mental Health Disability</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierPsychosocial} onChange={(e) => handleInputChange('learningBarrierPsychosocial', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Psychosocial</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierPhysical} onChange={(e) => handleInputChange('learningBarrierPhysical', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Physical</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierFunctionalCognition} onChange={(e) => handleInputChange('learningBarrierFunctionalCognition', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Functional Cognition</span></label>
            </div>
            <div className="flex flex-wrap gap-3 mt-2">
              <Label className="text-sm">Unable to:</Label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierUnableToRead} onChange={(e) => handleInputChange('learningBarrierUnableToRead', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Read</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.learningBarrierUnableToWrite} onChange={(e) => handleInputChange('learningBarrierUnableToWrite', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Write</span></label>
            </div>
            <VoiceDictationInput label="Educational level:" value={formData.educationalLevel} onChange={(e) => handleInputChange('educationalLevel', e.target.value)} className="mt-2" />
            <p className="text-xs text-gray-500 mt-1">See page 4 for Language Barrier(s)</p>
          </div>
        </div>

        {/* COMMUNICATION */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">COMMUNICATION</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Understanding of verbal content in patient's own language (with hearing aid or device):</Label>
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2"><input type="radio" name="verbalUnderstanding" checked={formData.understandingVerbalContent === 'Understands'} onChange={() => handleInputChange('understandingVerbalContent', 'Understands')} className="w-4 h-4" /><span className="text-sm">Understands: clear comprehension without cues or repetitions</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="verbalUnderstanding" checked={formData.understandingVerbalContent === 'UsuallyUnderstands'} onChange={() => handleInputChange('understandingVerbalContent', 'UsuallyUnderstands')} className="w-4 h-4" /><span className="text-sm">Usually Understands: Requires cues at times</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="verbalUnderstanding" checked={formData.understandingVerbalContent === 'SometimesUnderstands'} onChange={() => handleInputChange('understandingVerbalContent', 'SometimesUnderstands')} className="w-4 h-4" /><span className="text-sm">Sometimes Understands: Frequently requires cues to understand</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="verbalUnderstanding" checked={formData.understandingVerbalContent === 'RarelyNeverUnderstands'} onChange={() => handleInputChange('understandingVerbalContent', 'RarelyNeverUnderstands')} className="w-4 h-4" /><span className="text-sm">Rarely/Never Understands</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="verbalUnderstanding" checked={formData.understandingVerbalContent === 'UnableToAssess'} onChange={() => handleInputChange('understandingVerbalContent', 'UnableToAssess')} className="w-4 h-4" /><span className="text-sm">Unable to assess understanding</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Speech and oral (verbal) expression of language (in patient's own language):</Label>
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2"><input type="radio" name="speechExpression" checked={formData.speechOralExpression === 'ExpressesClearly'} onChange={() => handleInputChange('speechOralExpression', 'ExpressesClearly')} className="w-4 h-4" /><span className="text-sm">Expresses complex ideas, feelings, and needs clearly</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="speechExpression" checked={formData.speechOralExpression === 'MinimalModDifficulty'} onChange={() => handleInputChange('speechOralExpression', 'MinimalModDifficulty')} className="w-4 h-4" /><span className="text-sm">Minimal to moderate difficulty in expressing needs. May speak in phrases or short sentences. Needs minimal or moderate prompting</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="speechExpression" checked={formData.speechOralExpression === 'UnableToExpress'} onChange={() => handleInputChange('speechOralExpression', 'UnableToExpress')} className="w-4 h-4" /><span className="text-sm">Unable to express basic needs. Speech nonsensical or unintelligible</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="speechExpression" checked={formData.speechOralExpression === 'NonResponsive'} onChange={() => handleInputChange('speechOralExpression', 'NonResponsive')} className="w-4 h-4" /><span className="text-sm">Patient nonresponsive or unable to speak</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Patient's current ability to use the telephone safely:</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.phoneAbilityAbleToDialMakeCall} onChange={(e) => handleInputChange('phoneAbilityAbleToDialMakeCall', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Able to dial (make call)</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.phoneAbilityAbleToAnswerPhone} onChange={(e) => handleInputChange('phoneAbilityAbleToAnswerPhone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Able to answer phone</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.phoneAbilityMustUseAdaptivePhone} onChange={(e) => handleInputChange('phoneAbilityMustUseAdaptivePhone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Must use adaptive phone to complete activity</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.phoneAbilityNeedsHelper} onChange={(e) => handleInputChange('phoneAbilityNeedsHelper', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Needs helper to complete activity</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.phoneAbilityHelperMustMakeCall} onChange={(e) => handleInputChange('phoneAbilityHelperMustMakeCall', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Helper must make call for patient</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.phoneAbilityPatientDoesNotHavePhone} onChange={(e) => handleInputChange('phoneAbilityPatientDoesNotHavePhone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Patient does not have a phone</span></label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionC = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section C Cognitive Patterns</h3>
        
        {/* BIMS Introduction */}
        <div className="bg-yellow-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">Brief Interview for Mental Status (BIMS)</Label>
          <p className="text-xs text-gray-600 mt-1">Attempt to conduct interview with all patients. If unable to complete, enter 99 in total score and complete Staff Assessment (C0700-C1000).</p>
        </div>

        {/* C0100 */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0100. Should Brief Interview for Mental Status Be Conducted?</Label>
          <p className="text-xs text-gray-600 mb-2">Attempt to conduct interview with all patients</p>
          <div className="flex gap-4">
            <label className="flex items-center gap-2"><input type="radio" name="c0100" checked={formData.c0100ShouldBIMSBeConducted === '0'} onChange={() => handleInputChange('c0100ShouldBIMSBeConducted', '0')} className="w-4 h-4" /><span className="text-sm">0. No (patient is rarely/never understood) → Skip to C0700, Staff Assessment of Mental Status</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="c0100" checked={formData.c0100ShouldBIMSBeConducted === '1'} onChange={() => handleInputChange('c0100ShouldBIMSBeConducted', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes → Continue to C0200, Repetition of Three Words</span></label>
          </div>
        </div>

        {/* C0200 Repetition of Three Words */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0200. Repetition of Three Words</Label>
          <p className="text-sm mb-2">Ask patient: "I am going to say three words for you to remember. Please repeat the words after I have said all three. The words are: sock, blue, bed. Now tell me the three words."</p>
          <p className="text-xs text-gray-600 mb-2">Number of words repeated after first attempt</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. None</p>
            <p>1. One</p>
            <p>2. Two</p>
            <p>3. Three</p>
          </div>
          <VoiceDictationInput value={formData.c0200RepetitionOfThreeWords} onChange={(e) => handleInputChange('c0200RepetitionOfThreeWords', e.target.value)} placeholder="Enter 0, 1, 2, or 3" className="w-32" />
          <p className="text-xs text-gray-500 mt-2">After the patient's first attempt, repeat the words using cues ("sock, something to wear; blue, a color; bed, a piece of furniture"). You may repeat the words up to two more times.</p>
        </div>

        {/* C0300 Temporal Orientation */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0300. Temporal Orientation (Orientation to year, month, and day)</Label>
          <p className="text-sm mb-2">Ask patient: "Please tell me what year it is right now."</p>
          
          <div className="mb-3">
            <Label className="text-sm font-medium">A. Able to report correct year</Label>
            <div className="text-xs text-gray-600 space-y-1 mb-1">
              <p>0. Missed by &gt; 5 years or no answer</p>
              <p>1. Missed by 2-5 years</p>
              <p>2. Missed by 1 year</p>
              <p>3. Correct</p>
            </div>
            <VoiceDictationInput value={formData.c0300AYear} onChange={(e) => handleInputChange('c0300AYear', e.target.value)} placeholder="Enter 0, 1, 2, or 3" className="w-32" />
          </div>
          
          <p className="text-sm mb-2">Ask patient: "What month are we in right now?"</p>
          <div className="mb-3">
            <Label className="text-sm font-medium">B. Able to report correct month</Label>
            <div className="text-xs text-gray-600 space-y-1 mb-1">
              <p>0. Missed by &gt; 1 month or no answer</p>
              <p>1. Missed by 6 days to 1 month</p>
              <p>2. Accurate within 5 days</p>
            </div>
            <VoiceDictationInput value={formData.c0300BMonth} onChange={(e) => handleInputChange('c0300BMonth', e.target.value)} placeholder="Enter 0, 1, or 2" className="w-32" />
          </div>
          
          <p className="text-sm mb-2">Ask patient: "What day of the week is today?"</p>
          <div className="mb-3">
            <Label className="text-sm font-medium">C. Able to report correct day of the week</Label>
            <div className="text-xs text-gray-600 space-y-1 mb-1">
              <p>0. Incorrect or no answer</p>
              <p>1. Correct</p>
            </div>
            <VoiceDictationInput value={formData.c0300CDay} onChange={(e) => handleInputChange('c0300CDay', e.target.value)} placeholder="Enter 0 or 1" className="w-32" />
          </div>
        </div>

        {/* C0400 Recall */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0400. Recall</Label>
          <p className="text-sm mb-2">Ask patient: "Let's go back to an earlier question. What were those three words that I asked you to repeat?"</p>
          <p className="text-xs text-gray-600 mb-2">If unable to remember a word, give cue (e.g., "something to wear") for each word not recalled.</p>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium">A. Able to recall "sock"</Label>
              <div className="text-xs text-gray-600 space-y-1 mb-1">
                <p>0. No - could not recall</p>
                <p>1. Yes, after cueing ("something to wear")</p>
                <p>2. Yes, no cue required</p>
              </div>
              <VoiceDictationInput value={formData.c0400ASock} onChange={(e) => handleInputChange('c0400ASock', e.target.value)} placeholder="0, 1, or 2" className="w-24" />
            </div>
            <div>
              <Label className="text-sm font-medium">B. Able to recall "blue"</Label>
              <div className="text-xs text-gray-600 space-y-1 mb-1">
                <p>0. No - could not recall</p>
                <p>1. Yes, after cueing ("a color")</p>
                <p>2. Yes, no cue required</p>
              </div>
              <VoiceDictationInput value={formData.c0400BBlue} onChange={(e) => handleInputChange('c0400BBlue', e.target.value)} placeholder="0, 1, or 2" className="w-24" />
            </div>
            <div>
              <Label className="text-sm font-medium">C. Able to recall "bed"</Label>
              <div className="text-xs text-gray-600 space-y-1 mb-1">
                <p>0. No - could not recall</p>
                <p>1. Yes, after cueing ("a piece of furniture")</p>
                <p>2. Yes, no cue required</p>
              </div>
              <VoiceDictationInput value={formData.c0400CBed} onChange={(e) => handleInputChange('c0400CBed', e.target.value)} placeholder="0, 1, or 2" className="w-24" />
            </div>
          </div>
        </div>

        {/* C0500 BIMS Summary Score */}
        <div className="bg-blue-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0500. BIMS Summary Score</Label>
          <p className="text-xs text-gray-600 mb-2">Add scores for questions C0200-C0400 and fill in total score (00-15)</p>
          <p className="text-xs text-gray-600 mb-2">Enter 99 if the patient was unable to complete the interview</p>
          <VoiceDictationInput value={formData.c0500BIMSSummaryScore} onChange={(e) => handleInputChange('c0500BIMSSummaryScore', e.target.value)} placeholder="00-15 or 99" className="w-32" />
          <div className="mt-2 text-xs text-gray-600">
            <p>Scores: 13-15 = Intact cognition | 8-12 = Moderately impaired | 0-7 = Severely impaired</p>
          </div>
        </div>

        {/* C0700-C1000 Staff Assessment */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">Staff Assessment of Mental Status (Complete only if unable to complete BIMS)</Label>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">C0700. Short-term Memory OK</Label>
            <p className="text-xs text-gray-600 mb-1">Seems or appears to recall after 5 minutes</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="radio" name="c0700" checked={formData.c0700ShortTermMemory === '0'} onChange={() => handleInputChange('c0700ShortTermMemory', '0')} className="w-4 h-4" /><span className="text-sm">0. Memory OK</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="c0700" checked={formData.c0700ShortTermMemory === '1'} onChange={() => handleInputChange('c0700ShortTermMemory', '1')} className="w-4 h-4" /><span className="text-sm">1. Memory problem</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">C0800. Long-term Memory OK</Label>
            <p className="text-xs text-gray-600 mb-1">Seems or appears to recall long past</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="radio" name="c0800" checked={formData.c0800LongTermMemory === '0'} onChange={() => handleInputChange('c0800LongTermMemory', '0')} className="w-4 h-4" /><span className="text-sm">0. Memory OK</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="c0800" checked={formData.c0800LongTermMemory === '1'} onChange={() => handleInputChange('c0800LongTermMemory', '1')} className="w-4 h-4" /><span className="text-sm">1. Memory problem</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">C0900. Memory/Recall Ability (Enter Code)</Label>
            <p className="text-xs text-gray-600 mb-1">↓ Check all that patient was normally able to recall</p>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.c0900MemoryRecallCurrentSeason} onChange={(e) => handleInputChange('c0900MemoryRecallCurrentSeason', e.target.checked)} className="w-4 h-4" /><span className="text-sm">A. Current season</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.c0900MemoryRecallLocationRoom} onChange={(e) => handleInputChange('c0900MemoryRecallLocationRoom', e.target.checked)} className="w-4 h-4" /><span className="text-sm">B. Location of own room</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.c0900MemoryRecallStaffNames} onChange={(e) => handleInputChange('c0900MemoryRecallStaffNames', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C. Staff names and faces</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.c0900MemoryRecallInNursingHome} onChange={(e) => handleInputChange('c0900MemoryRecallInNursingHome', e.target.checked)} className="w-4 h-4" /><span className="text-sm">D. That he or she is in a nursing home</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.c0900MemoryRecallNone} onChange={(e) => handleInputChange('c0900MemoryRecallNone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Z. None of above were recalled</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">C1000. Cognitive Skills for Daily Decision Making (Enter Code)</Label>
            <p className="text-xs text-gray-600 mb-1">Made decisions regarding tasks of daily life</p>
            <div className="text-xs text-gray-600 space-y-1 mb-2">
              <p>0. Independent - Decisions consistent/reasonable</p>
              <p>1. Modified independence - Some difficulty in new situations only</p>
              <p>2. Moderately impaired - Decisions poor; cues/supervision required</p>
              <p>3. Severely impaired - Never/rarely made decisions</p>
            </div>
            <VoiceDictationInput value={formData.c1000CognitiveSkillsForDailyDecision} onChange={(e) => handleInputChange('c1000CognitiveSkillsForDailyDecision', e.target.value)} placeholder="Enter 0, 1, 2, or 3" className="w-32" />
          </div>
        </div>

        {/* C1310 Signs and Symptoms of Delirium (CAM) */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C1310. Signs and Symptoms of Delirium (from CAM©)</Label>
          <p className="text-xs text-gray-600 mb-2">Code after completing Brief Interview for Mental Status or Staff Assessment, and reviewing medical record.</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border mt-2">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Sign/Symptom</th>
                  <th className="border p-2 text-center">Code</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">
                    <p className="font-medium">A. Acute Onset of Mental Status Change</p>
                    <p className="text-xs text-gray-500">Is there evidence of an acute change in mental status from the patient's baseline?</p>
                    <p className="text-xs text-gray-500">0. No | 1. Yes</p>
                  </td>
                  <td className="border p-2"><VoiceDictationInput value={formData.c1310AAcuteOnset} onChange={(e) => handleInputChange('c1310AAcuteOnset', e.target.value)} placeholder="0 or 1" className="w-20" /></td>
                </tr>
                <tr>
                  <td className="border p-2">
                    <p className="font-medium">B. Inattention</p>
                    <p className="text-xs text-gray-500">Did the patient have difficulty focusing attention, for example being easily distractible or having difficulty keeping track of what was being said?</p>
                    <p className="text-xs text-gray-500">0. Behavior not present | 1. Behavior continuously present, does not fluctuate | 2. Behavior present, fluctuates</p>
                  </td>
                  <td className="border p-2"><VoiceDictationInput value={formData.c1310BInattention} onChange={(e) => handleInputChange('c1310BInattention', e.target.value)} placeholder="0, 1, or 2" className="w-20" /></td>
                </tr>
                <tr>
                  <td className="border p-2">
                    <p className="font-medium">C. Disorganized Thinking</p>
                    <p className="text-xs text-gray-500">Was the patient's thinking disorganized or incoherent (rambling or irrelevant conversation, unclear or illogical flow of ideas, or unpredictable switching from subject to subject)?</p>
                    <p className="text-xs text-gray-500">0. Behavior not present | 1. Behavior continuously present, does not fluctuate | 2. Behavior present, fluctuates</p>
                  </td>
                  <td className="border p-2"><VoiceDictationInput value={formData.c1310CDisorganizedThinking} onChange={(e) => handleInputChange('c1310CDisorganizedThinking', e.target.value)} placeholder="0, 1, or 2" className="w-20" /></td>
                </tr>
                <tr>
                  <td className="border p-2">
                    <p className="font-medium">D. Altered Level of Consciousness</p>
                    <p className="text-xs text-gray-500">Did the patient have altered level of consciousness (e.g., vigilant - Loss of contact with environment, stupor - difficult to arouse, coma - unable to arouse)?</p>
                    <p className="text-xs text-gray-500">0. Behavior not present | 1. Behavior continuously present, does not fluctuate | 2. Behavior present, fluctuates</p>
                  </td>
                  <td className="border p-2"><VoiceDictationInput value={formData.c1310DAlteredConsciousness} onChange={(e) => handleInputChange('c1310DAlteredConsciousness', e.target.value)} placeholder="0, 1, or 2" className="w-20" /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-2 italic">Confusion Assessment Method. © 1988, 2003, Hospital Elder Life Program. All rights reserved. Adapted from: Inouye SK et al. Ann Intern Med. 1990; 113(12):941-8.</p>
        </div>

        {/* M1700-M1730 Cognitive/Mental Status */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1700. Cognitive Functioning (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Patient's current level of alertness, orientation, comprehension, concentration, and immediate memory for simple commands.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Alert/oriented, able to focus and shift attention, comprehends and recalls task directions independently</p>
            <p>1. Requires prompting (cueing, repetition, reminders) only under stressful or unfamiliar conditions</p>
            <p>2. Requires assistance and some direction in specific situations (e.g., on all tasks involving shifting of attention) or consistently requires low stimulus environment due to distractibility</p>
            <p>3. Requires considerable assistance in routine situations. Is not alert and oriented or is unable to shift attention and recall directions more than half the time</p>
            <p>4. Totally dependent due to disturbances such as constant disorientation, coma, persistent vegetative state, or delirium</p>
          </div>
          <VoiceDictationInput value={formData.m1700CognitiveFunctioning} onChange={(e) => handleInputChange('m1700CognitiveFunctioning', e.target.value)} placeholder="Enter 0, 1, 2, 3, or 4" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1710. When Confused (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Reporting the presence of confusion</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Never</p>
            <p>1. In new or complex situations only</p>
            <p>2. On awakening or at night only</p>
            <p>3. During the day and evening, but not constantly</p>
            <p>4. Constantly</p>
            <p>NA Patient nonresponsive</p>
          </div>
          <VoiceDictationInput value={formData.m1710WhenConfused} onChange={(e) => handleInputChange('m1710WhenConfused', e.target.value)} placeholder="Enter 0-4 or NA" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1720. When Anxious (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Reporting the frequency of anxiety</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. None of the time</p>
            <p>1. Less often than daily</p>
            <p>2. Daily, but not constantly</p>
            <p>3. All of the time</p>
            <p>NA Patient nonresponsive</p>
          </div>
          <VoiceDictationInput value={formData.m1720WhenAnxious} onChange={(e) => handleInputChange('m1720WhenAnxious', e.target.value)} placeholder="Enter 0-3 or NA" className="w-32" />
        </div>

        {/* Neurological Status */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">NEUROLOGICAL STATUS</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Level of Consciousness (LOC):</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.locAlert} onChange={(e) => handleInputChange('locAlert', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Alert</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.locLethargic} onChange={(e) => handleInputChange('locLethargic', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Lethargic</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.locObtunded} onChange={(e) => handleInputChange('locObtunded', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Obtunded</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.locStuporous} onChange={(e) => handleInputChange('locStuporous', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Stuporous</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.locComatose} onChange={(e) => handleInputChange('locComatose', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Comatose</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Orientation:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.orientedToPerson} onChange={(e) => handleInputChange('orientedToPerson', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Person</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.orientedToPlace} onChange={(e) => handleInputChange('orientedToPlace', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Place</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.orientedToTime} onChange={(e) => handleInputChange('orientedToTime', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Time</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.orientedToSituation} onChange={(e) => handleInputChange('orientedToSituation', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Situation</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Neurological:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroNoProblem} onChange={(e) => handleInputChange('neuroNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroSeizureHistory} onChange={(e) => handleInputChange('neuroSeizureHistory', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Seizure history</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroTremors} onChange={(e) => handleInputChange('neuroTremors', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Tremors</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroParalysis} onChange={(e) => handleInputChange('neuroParalysis', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Paralysis</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroParesthesia} onChange={(e) => handleInputChange('neuroParesthesia', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Paresthesia</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroWeakness} onChange={(e) => handleInputChange('neuroWeakness', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Weakness</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroSyncope} onChange={(e) => handleInputChange('neuroSyncope', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Syncope</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroAbnormalMovements} onChange={(e) => handleInputChange('neuroAbnormalMovements', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Abnormal movements</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroNeuropathy} onChange={(e) => handleInputChange('neuroNeuropathy', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Neuropathy</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.neuroOther} onChange={(e) => handleInputChange('neuroOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.neuroOther && <VoiceDictationInput value={formData.neuroOtherSpecify} onChange={(e) => handleInputChange('neuroOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <VoiceDictationInput label="Pertinent neurological comments:" value={formData.neuroPertinentComments} onChange={(e) => handleInputChange('neuroPertinentComments', e.target.value)} rows={2} className="mt-3" />
        </div>
      </div>
    </div>
  );

  const renderSectionD = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section D Mood</h3>
        
        {/* D0150 Patient Mood Interview (PHQ-2) */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">D0150. Patient Mood Interview (PHQ-2 to 9©)</Label>
          <p className="text-xs text-gray-600 mb-2">Say to patient: "Over the last 2 weeks, have you been bothered by any of the following problems?"</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border mt-2">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left"></th>
                  <th className="border p-2 text-center text-xs">0. Not at all</th>
                  <th className="border p-2 text-center text-xs">1. Several days</th>
                  <th className="border p-2 text-center text-xs">2. More than half the days</th>
                  <th className="border p-2 text-center text-xs">3. Nearly every day</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">A. Little interest or pleasure in doing things</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150A" checked={formData.d0150ALittleInterest === '0'} onChange={() => handleInputChange('d0150ALittleInterest', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150A" checked={formData.d0150ALittleInterest === '1'} onChange={() => handleInputChange('d0150ALittleInterest', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150A" checked={formData.d0150ALittleInterest === '2'} onChange={() => handleInputChange('d0150ALittleInterest', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150A" checked={formData.d0150ALittleInterest === '3'} onChange={() => handleInputChange('d0150ALittleInterest', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">B. Feeling down, depressed, or hopeless</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150B" checked={formData.d0150BFeelingDown === '0'} onChange={() => handleInputChange('d0150BFeelingDown', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150B" checked={formData.d0150BFeelingDown === '1'} onChange={() => handleInputChange('d0150BFeelingDown', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150B" checked={formData.d0150BFeelingDown === '2'} onChange={() => handleInputChange('d0150BFeelingDown', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150B" checked={formData.d0150BFeelingDown === '3'} onChange={() => handleInputChange('d0150BFeelingDown', '3')} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 rounded">
            <Label className="text-sm font-medium">PHQ-2 Total Severity Score (Add A + B):</Label>
            <VoiceDictationInput value={formData.phq2TotalScore} onChange={(e) => handleInputChange('phq2TotalScore', e.target.value)} placeholder="0-6" className="w-24 mt-1" />
            <p className="text-xs text-gray-500 mt-1">If score is 3 or more, complete PHQ-9 below</p>
          </div>
        </div>

        {/* PHQ-9 Continuation */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PHQ-9 Continuation (Complete if PHQ-2 score ≥ 3)</Label>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border mt-2">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left"></th>
                  <th className="border p-2 text-center text-xs">0. Not at all</th>
                  <th className="border p-2 text-center text-xs">1. Several days</th>
                  <th className="border p-2 text-center text-xs">2. More than half the days</th>
                  <th className="border p-2 text-center text-xs">3. Nearly every day</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">C. Trouble falling or staying asleep, or sleeping too much</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150C" checked={formData.d0150CTroubleSleeping === '0'} onChange={() => handleInputChange('d0150CTroubleSleeping', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150C" checked={formData.d0150CTroubleSleeping === '1'} onChange={() => handleInputChange('d0150CTroubleSleeping', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150C" checked={formData.d0150CTroubleSleeping === '2'} onChange={() => handleInputChange('d0150CTroubleSleeping', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150C" checked={formData.d0150CTroubleSleeping === '3'} onChange={() => handleInputChange('d0150CTroubleSleeping', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">D. Feeling tired or having little energy</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150D" checked={formData.d0150DFeelingTired === '0'} onChange={() => handleInputChange('d0150DFeelingTired', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150D" checked={formData.d0150DFeelingTired === '1'} onChange={() => handleInputChange('d0150DFeelingTired', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150D" checked={formData.d0150DFeelingTired === '2'} onChange={() => handleInputChange('d0150DFeelingTired', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150D" checked={formData.d0150DFeelingTired === '3'} onChange={() => handleInputChange('d0150DFeelingTired', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">E. Poor appetite or overeating</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150E" checked={formData.d0150EPoorAppetite === '0'} onChange={() => handleInputChange('d0150EPoorAppetite', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150E" checked={formData.d0150EPoorAppetite === '1'} onChange={() => handleInputChange('d0150EPoorAppetite', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150E" checked={formData.d0150EPoorAppetite === '2'} onChange={() => handleInputChange('d0150EPoorAppetite', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150E" checked={formData.d0150EPoorAppetite === '3'} onChange={() => handleInputChange('d0150EPoorAppetite', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">F. Feeling bad about yourself - or that you are a failure or have let yourself or your family down</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150F" checked={formData.d0150FFeelingBad === '0'} onChange={() => handleInputChange('d0150FFeelingBad', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150F" checked={formData.d0150FFeelingBad === '1'} onChange={() => handleInputChange('d0150FFeelingBad', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150F" checked={formData.d0150FFeelingBad === '2'} onChange={() => handleInputChange('d0150FFeelingBad', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150F" checked={formData.d0150FFeelingBad === '3'} onChange={() => handleInputChange('d0150FFeelingBad', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">G. Trouble concentrating on things, such as reading the newspaper or watching television</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150G" checked={formData.d0150GTroubleConcentrating === '0'} onChange={() => handleInputChange('d0150GTroubleConcentrating', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150G" checked={formData.d0150GTroubleConcentrating === '1'} onChange={() => handleInputChange('d0150GTroubleConcentrating', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150G" checked={formData.d0150GTroubleConcentrating === '2'} onChange={() => handleInputChange('d0150GTroubleConcentrating', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150G" checked={formData.d0150GTroubleConcentrating === '3'} onChange={() => handleInputChange('d0150GTroubleConcentrating', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">H. Moving or speaking so slowly that other people could have noticed. Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150H" checked={formData.d0150HMovingSlowly === '0'} onChange={() => handleInputChange('d0150HMovingSlowly', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150H" checked={formData.d0150HMovingSlowly === '1'} onChange={() => handleInputChange('d0150HMovingSlowly', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150H" checked={formData.d0150HMovingSlowly === '2'} onChange={() => handleInputChange('d0150HMovingSlowly', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150H" checked={formData.d0150HMovingSlowly === '3'} onChange={() => handleInputChange('d0150HMovingSlowly', '3')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">I. Thoughts that you would be better off dead, or of hurting yourself in some way</td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150I" checked={formData.d0150IThoughtsOfHarm === '0'} onChange={() => handleInputChange('d0150IThoughtsOfHarm', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150I" checked={formData.d0150IThoughtsOfHarm === '1'} onChange={() => handleInputChange('d0150IThoughtsOfHarm', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150I" checked={formData.d0150IThoughtsOfHarm === '2'} onChange={() => handleInputChange('d0150IThoughtsOfHarm', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="d0150I" checked={formData.d0150IThoughtsOfHarm === '3'} onChange={() => handleInputChange('d0150IThoughtsOfHarm', '3')} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
            <p className="text-xs text-yellow-700 font-medium">⚠️ If patient indicates thoughts of self-harm (Item I), immediately assess safety and notify physician.</p>
          </div>
        </div>

        {/* D0160 PHQ-9 Total Score */}
        <div className="bg-blue-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">D0160. Total Severity Score</Label>
          <p className="text-xs text-gray-600 mb-2">Add scores for all 9 items (A through I)</p>
          <VoiceDictationInput value={formData.d0160TotalSeverityScore} onChange={(e) => handleInputChange('d0160TotalSeverityScore', e.target.value)} placeholder="0-27" className="w-24" />
          <div className="mt-2 text-xs text-gray-600 space-y-1">
            <p>1-4: Minimal depression</p>
            <p>5-9: Mild depression</p>
            <p>10-14: Moderate depression</p>
            <p>15-19: Moderately severe depression</p>
            <p>20-27: Severe depression</p>
          </div>
          <p className="text-xs text-gray-400 mt-2 italic">Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues.</p>
        </div>

        {/* D0700 Social Isolation */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">D0700. Social Isolation (Enter Code)</Label>
          <p className="text-sm mb-2">How often do you feel lonely or isolated from those around you?</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Never</p>
            <p>1. Rarely</p>
            <p>2. Sometimes</p>
            <p>3. Often</p>
            <p>4. Always</p>
            <p>7. Patient declines to respond</p>
            <p>8. Patient unable to respond</p>
          </div>
          <VoiceDictationInput value={formData.d0700SocialIsolation} onChange={(e) => handleInputChange('d0700SocialIsolation', e.target.value)} placeholder="Enter 0-4, 7, or 8" className="w-32" />
        </div>
      </div>
    </div>
  );

  const renderSectionE = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section E Behavior</h3>
        
        {/* M1740 Cognitive, behavioral, and psychiatric symptoms */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1740. Cognitive, behavioral, and psychiatric symptoms that are demonstrated at least once a week</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all that apply</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740MemoryDeficit} onChange={(e) => handleInputChange('m1740MemoryDeficit', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1. Memory deficit: failure to recognize familiar persons/places, inability to recall events of past 24 hours, significant memory loss so that supervision is required</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740ImpairedDecisionMaking} onChange={(e) => handleInputChange('m1740ImpairedDecisionMaking', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2. Impaired decision-making: failure to perform usual ADLs or IADLs, inability to appropriately stop activities, jeopardizes safety through actions</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740VerbalDisruption} onChange={(e) => handleInputChange('m1740VerbalDisruption', e.target.checked)} className="w-4 h-4" /><span className="text-sm">3. Verbal disruption: yelling, threatening, excessive profanity, sexual references, etc.</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740PhysicalAggression} onChange={(e) => handleInputChange('m1740PhysicalAggression', e.target.checked)} className="w-4 h-4" /><span className="text-sm">4. Physical aggression: aggressive or combative to self and others (e.g., hits self, throws objects, punches, dangerous maneuvers with wheelchair or other objects)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740DisruptiveBehavior} onChange={(e) => handleInputChange('m1740DisruptiveBehavior', e.target.checked)} className="w-4 h-4" /><span className="text-sm">5. Disruptive, infantile, or socially inappropriate behavior (excludes verbal actions)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740Delusional} onChange={(e) => handleInputChange('m1740Delusional', e.target.checked)} className="w-4 h-4" /><span className="text-sm">6. Delusional, hallucinatory, or paranoid behavior</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1740NoneOfAbove} onChange={(e) => handleInputChange('m1740NoneOfAbove', e.target.checked)} className="w-4 h-4" /><span className="text-sm">7. None of the above behaviors demonstrated</span></label>
          </div>
        </div>

        {/* M1745 Frequency of Disruptive Behavior Symptoms */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1745. Frequency of Disruptive Behavior Symptoms (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">(Reported or observed)</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Never</p>
            <p>1. Less than once a month</p>
            <p>2. Once a month</p>
            <p>3. Several times each month</p>
            <p>4. Several times a week</p>
            <p>5. At least daily</p>
          </div>
          <VoiceDictationInput value={formData.m1745FrequencyDisruptiveBehavior} onChange={(e) => handleInputChange('m1745FrequencyDisruptiveBehavior', e.target.value)} placeholder="Enter 0-5" className="w-32" />
        </div>

        {/* Mental Status */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">MENTAL STATUS</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusNoProblem} onChange={(e) => handleInputChange('mentalStatusNoProblem', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problem</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusAnxious} onChange={(e) => handleInputChange('mentalStatusAnxious', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Anxious</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusFearful} onChange={(e) => handleInputChange('mentalStatusFearful', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Fearful</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusDepressed} onChange={(e) => handleInputChange('mentalStatusDepressed', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Depressed</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusApathetic} onChange={(e) => handleInputChange('mentalStatusApathetic', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Apathetic</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusAgitated} onChange={(e) => handleInputChange('mentalStatusAgitated', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Agitated</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusAffectFlat} onChange={(e) => handleInputChange('mentalStatusAffectFlat', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Affect: Flat</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusAffectAppropriate} onChange={(e) => handleInputChange('mentalStatusAffectAppropriate', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Appropriate</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusMoodLabile} onChange={(e) => handleInputChange('mentalStatusMoodLabile', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Mood Labile</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.mentalStatusOther} onChange={(e) => handleInputChange('mentalStatusOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
          </div>
          {formData.mentalStatusOther && <VoiceDictationInput value={formData.mentalStatusOtherSpecify} onChange={(e) => handleInputChange('mentalStatusOtherSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>

        {/* PSYCHOSOCIAL */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PSYCHOSOCIAL</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Is patient receiving or does patient have history of receiving psychosocial care, including:</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.psychosocialCounseling} onChange={(e) => handleInputChange('psychosocialCounseling', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Counseling</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.psychosocialTherapy} onChange={(e) => handleInputChange('psychosocialTherapy', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Therapy</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.psychosocialPsychiatrist} onChange={(e) => handleInputChange('psychosocialPsychiatrist', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Psychiatrist</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.psychosocialSupportGroup} onChange={(e) => handleInputChange('psychosocialSupportGroup', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Support group</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Stressors:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.stressorFinancial} onChange={(e) => handleInputChange('stressorFinancial', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Financial</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.stressorRelationship} onChange={(e) => handleInputChange('stressorRelationship', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Relationship</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.stressorEmployment} onChange={(e) => handleInputChange('stressorEmployment', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Employment</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.stressorHealthRelated} onChange={(e) => handleInputChange('stressorHealthRelated', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Health-related</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.stressorGrief} onChange={(e) => handleInputChange('stressorGrief', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Grief</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.stressorOther} onChange={(e) => handleInputChange('stressorOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.stressorOther && <VoiceDictationInput value={formData.stressorOtherSpecify} onChange={(e) => handleInputChange('stressorOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Coping mechanisms:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.copingAdequate} onChange={(e) => handleInputChange('copingAdequate', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Adequate</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.copingInadequate} onChange={(e) => handleInputChange('copingInadequate', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Inadequate</span></label>
            </div>
            {formData.copingInadequate && <VoiceDictationInput label="If inadequate, explain:" value={formData.copingInadequateExplain} onChange={(e) => handleInputChange('copingInadequateExplain', e.target.value)} className="mt-2" />}
          </div>
          
          <VoiceDictationInput label="Pertinent psychosocial comments:" value={formData.psychosocialComments} onChange={(e) => handleInputChange('psychosocialComments', e.target.value)} rows={2} className="mt-3" />
        </div>

        {/* Substance Use */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">SUBSTANCE USE HISTORY</Label>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <Label className="text-sm">Tobacco use:</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                <label className="flex items-center gap-2"><input type="radio" name="tobaccoUse" checked={formData.tobaccoUse === 'Never'} onChange={() => handleInputChange('tobaccoUse', 'Never')} className="w-4 h-4" /><span className="text-sm">Never</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="tobaccoUse" checked={formData.tobaccoUse === 'Current'} onChange={() => handleInputChange('tobaccoUse', 'Current')} className="w-4 h-4" /><span className="text-sm">Current</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="tobaccoUse" checked={formData.tobaccoUse === 'Former'} onChange={() => handleInputChange('tobaccoUse', 'Former')} className="w-4 h-4" /><span className="text-sm">Former</span></label>
              </div>
              {(formData.tobaccoUse === 'Current' || formData.tobaccoUse === 'Former') && (
                <VoiceDictationInput label="Type/Amount/Duration:" value={formData.tobaccoDetails} onChange={(e) => handleInputChange('tobaccoDetails', e.target.value)} className="mt-2" />
              )}
            </div>
            <div>
              <Label className="text-sm">Alcohol use:</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                <label className="flex items-center gap-2"><input type="radio" name="alcoholUse" checked={formData.alcoholUse === 'Never'} onChange={() => handleInputChange('alcoholUse', 'Never')} className="w-4 h-4" /><span className="text-sm">Never</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="alcoholUse" checked={formData.alcoholUse === 'Current'} onChange={() => handleInputChange('alcoholUse', 'Current')} className="w-4 h-4" /><span className="text-sm">Current</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="alcoholUse" checked={formData.alcoholUse === 'Former'} onChange={() => handleInputChange('alcoholUse', 'Former')} className="w-4 h-4" /><span className="text-sm">Former</span></label>
              </div>
              {(formData.alcoholUse === 'Current' || formData.alcoholUse === 'Former') && (
                <VoiceDictationInput label="Type/Amount/Duration:" value={formData.alcoholDetails} onChange={(e) => handleInputChange('alcoholDetails', e.target.value)} className="mt-2" />
              )}
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Drug use (illicit or non-prescribed):</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="drugUse" checked={formData.drugUse === 'No'} onChange={() => handleInputChange('drugUse', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="drugUse" checked={formData.drugUse === 'Yes'} onChange={() => handleInputChange('drugUse', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.drugUse === 'Yes' && <VoiceDictationInput label="If yes, specify:" value={formData.drugUseDetails} onChange={(e) => handleInputChange('drugUseDetails', e.target.value)} className="mt-2" />}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionF = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section F Preferences for Customary Routine and Activities</h3>
        
        {/* M1100 Patient Living Situation */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1100. Patient Living Situation</Label>
          <p className="text-xs text-gray-600 mb-2">Which of the following best describes the patient's residential circumstance and availability of assistance?</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Living Arrangement</th>
                  <th className="border p-2 text-center text-xs">Around the Clock</th>
                  <th className="border p-2 text-center text-xs">Regular Daytime</th>
                  <th className="border p-2 text-center text-xs">Regular Nighttime</th>
                  <th className="border p-2 text-center text-xs">Occasional/Short-Term</th>
                  <th className="border p-2 text-center text-xs">No Assistance Available</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">A. Patient lives alone</td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'A01'} onChange={() => handleInputChange('m1100LivingSituation', 'A01')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'A02'} onChange={() => handleInputChange('m1100LivingSituation', 'A02')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'A03'} onChange={() => handleInputChange('m1100LivingSituation', 'A03')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'A04'} onChange={() => handleInputChange('m1100LivingSituation', 'A04')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'A05'} onChange={() => handleInputChange('m1100LivingSituation', 'A05')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">B. Patient lives with other person(s) in the home</td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'B06'} onChange={() => handleInputChange('m1100LivingSituation', 'B06')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'B07'} onChange={() => handleInputChange('m1100LivingSituation', 'B07')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'B08'} onChange={() => handleInputChange('m1100LivingSituation', 'B08')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'B09'} onChange={() => handleInputChange('m1100LivingSituation', 'B09')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'B10'} onChange={() => handleInputChange('m1100LivingSituation', 'B10')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">C. Patient lives in congregate situation (e.g., assisted living)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'C11'} onChange={() => handleInputChange('m1100LivingSituation', 'C11')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'C12'} onChange={() => handleInputChange('m1100LivingSituation', 'C12')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'C13'} onChange={() => handleInputChange('m1100LivingSituation', 'C13')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'C14'} onChange={() => handleInputChange('m1100LivingSituation', 'C14')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m1100" checked={formData.m1100LivingSituation === 'C15'} onChange={() => handleInputChange('m1100LivingSituation', 'C15')} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* M2102 Types and Sources of Assistance */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M2102. Types and Sources of Assistance</Label>
          <p className="text-xs text-gray-600 mb-2">Determine the ability and willingness of non-agency caregivers (such as family members, friends, or privately hired helpers) to provide assistance for the following activities.</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Type of Assistance</th>
                  <th className="border p-2 text-center text-xs">0. No assistance needed</th>
                  <th className="border p-2 text-center text-xs">1. Non-agency caregiver(s) currently providing assistance</th>
                  <th className="border p-2 text-center text-xs">2. Non-agency caregiver(s) need training</th>
                  <th className="border p-2 text-center text-xs">3. Non-agency caregiver(s) not likely to provide assistance</th>
                  <th className="border p-2 text-center text-xs">4. Assistance needed, but no caregiver available</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">a. ADL assistance (e.g., bathing, dressing, toileting, transferring)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102a" checked={formData.m2102aADL === '0'} onChange={() => handleInputChange('m2102aADL', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102a" checked={formData.m2102aADL === '1'} onChange={() => handleInputChange('m2102aADL', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102a" checked={formData.m2102aADL === '2'} onChange={() => handleInputChange('m2102aADL', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102a" checked={formData.m2102aADL === '3'} onChange={() => handleInputChange('m2102aADL', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102a" checked={formData.m2102aADL === '4'} onChange={() => handleInputChange('m2102aADL', '4')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">b. IADL assistance (e.g., meals, housekeeping, laundry, telephone, shopping)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102b" checked={formData.m2102bIADL === '0'} onChange={() => handleInputChange('m2102bIADL', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102b" checked={formData.m2102bIADL === '1'} onChange={() => handleInputChange('m2102bIADL', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102b" checked={formData.m2102bIADL === '2'} onChange={() => handleInputChange('m2102bIADL', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102b" checked={formData.m2102bIADL === '3'} onChange={() => handleInputChange('m2102bIADL', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102b" checked={formData.m2102bIADL === '4'} onChange={() => handleInputChange('m2102bIADL', '4')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">c. Medication administration (e.g., oral, inhaled, or injectable)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102c" checked={formData.m2102cMedication === '0'} onChange={() => handleInputChange('m2102cMedication', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102c" checked={formData.m2102cMedication === '1'} onChange={() => handleInputChange('m2102cMedication', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102c" checked={formData.m2102cMedication === '2'} onChange={() => handleInputChange('m2102cMedication', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102c" checked={formData.m2102cMedication === '3'} onChange={() => handleInputChange('m2102cMedication', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102c" checked={formData.m2102cMedication === '4'} onChange={() => handleInputChange('m2102cMedication', '4')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">d. Medical procedures/treatments (e.g., wound care)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102d" checked={formData.m2102dMedicalProcedures === '0'} onChange={() => handleInputChange('m2102dMedicalProcedures', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102d" checked={formData.m2102dMedicalProcedures === '1'} onChange={() => handleInputChange('m2102dMedicalProcedures', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102d" checked={formData.m2102dMedicalProcedures === '2'} onChange={() => handleInputChange('m2102dMedicalProcedures', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102d" checked={formData.m2102dMedicalProcedures === '3'} onChange={() => handleInputChange('m2102dMedicalProcedures', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102d" checked={formData.m2102dMedicalProcedures === '4'} onChange={() => handleInputChange('m2102dMedicalProcedures', '4')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">e. Management of equipment (e.g., oxygen, IV, enteral/parenteral nutrition)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102e" checked={formData.m2102eEquipment === '0'} onChange={() => handleInputChange('m2102eEquipment', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102e" checked={formData.m2102eEquipment === '1'} onChange={() => handleInputChange('m2102eEquipment', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102e" checked={formData.m2102eEquipment === '2'} onChange={() => handleInputChange('m2102eEquipment', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102e" checked={formData.m2102eEquipment === '3'} onChange={() => handleInputChange('m2102eEquipment', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102e" checked={formData.m2102eEquipment === '4'} onChange={() => handleInputChange('m2102eEquipment', '4')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">f. Supervision and safety (e.g., due to cognitive impairment)</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102f" checked={formData.m2102fSupervision === '0'} onChange={() => handleInputChange('m2102fSupervision', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102f" checked={formData.m2102fSupervision === '1'} onChange={() => handleInputChange('m2102fSupervision', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102f" checked={formData.m2102fSupervision === '2'} onChange={() => handleInputChange('m2102fSupervision', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102f" checked={formData.m2102fSupervision === '3'} onChange={() => handleInputChange('m2102fSupervision', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102f" checked={formData.m2102fSupervision === '4'} onChange={() => handleInputChange('m2102fSupervision', '4')} /></td>
                </tr>
                <tr>
                  <td className="border p-2">g. Advocacy or facilitation of patient's participation in appropriate medical care</td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102g" checked={formData.m2102gAdvocacy === '0'} onChange={() => handleInputChange('m2102gAdvocacy', '0')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102g" checked={formData.m2102gAdvocacy === '1'} onChange={() => handleInputChange('m2102gAdvocacy', '1')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102g" checked={formData.m2102gAdvocacy === '2'} onChange={() => handleInputChange('m2102gAdvocacy', '2')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102g" checked={formData.m2102gAdvocacy === '3'} onChange={() => handleInputChange('m2102gAdvocacy', '3')} /></td>
                  <td className="border p-2 text-center"><input type="radio" name="m2102g" checked={formData.m2102gAdvocacy === '4'} onChange={() => handleInputChange('m2102gAdvocacy', '4')} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Care Preferences */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">CARE PREFERENCES</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Patient's preferred schedule for visits:</Label>
            <VoiceDictationInput value={formData.preferredScheduleVisits} onChange={(e) => handleInputChange('preferredScheduleVisits', e.target.value)} className="mt-1" placeholder="e.g., Mornings preferred, no weekends" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Special considerations or preferences:</Label>
            <VoiceDictationInput value={formData.specialConsiderations} onChange={(e) => handleInputChange('specialConsiderations', e.target.value)} rows={2} className="mt-1" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Religious/Cultural preferences affecting care:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="religiousCultural" checked={formData.religiousCulturalPreferences === 'None'} onChange={() => handleInputChange('religiousCulturalPreferences', 'None')} className="w-4 h-4" /><span className="text-sm">None identified</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="religiousCultural" checked={formData.religiousCulturalPreferences === 'Yes'} onChange={() => handleInputChange('religiousCulturalPreferences', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes (specify)</span></label>
            </div>
            {formData.religiousCulturalPreferences === 'Yes' && <VoiceDictationInput value={formData.religiousCulturalPreferencesSpecify} onChange={(e) => handleInputChange('religiousCulturalPreferencesSpecify', e.target.value)} className="mt-2" />}
          </div>
        </div>

        {/* Safety/Environment */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">SAFETY/ENVIRONMENT</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Type of residence:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.residenceHouse} onChange={(e) => handleInputChange('residenceHouse', e.target.checked)} className="w-4 h-4" /><span className="text-sm">House</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.residenceApartment} onChange={(e) => handleInputChange('residenceApartment', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Apartment</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.residenceMobileHome} onChange={(e) => handleInputChange('residenceMobileHome', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Mobile home</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.residenceAssistedLiving} onChange={(e) => handleInputChange('residenceAssistedLiving', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Assisted living</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.residenceOther} onChange={(e) => handleInputChange('residenceOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.residenceOther && <VoiceDictationInput value={formData.residenceOtherSpecify} onChange={(e) => handleInputChange('residenceOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Access to home:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.accessSteps} onChange={(e) => handleInputChange('accessSteps', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Steps</span></label>
              <VoiceDictationInput value={formData.accessStepsNumber} onChange={(e) => handleInputChange('accessStepsNumber', e.target.value)} placeholder="# of steps" className="w-24" />
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.accessRamp} onChange={(e) => handleInputChange('accessRamp', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Ramp</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.accessElevator} onChange={(e) => handleInputChange('accessElevator', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Elevator</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.accessRailings} onChange={(e) => handleInputChange('accessRailings', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Railings</span></label>
            </div>
          </div>

          <div className="mt-3">
            <Label className="text-sm">Bedroom located on:</Label>
            <div className="flex gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bedroomFirstFloor} onChange={(e) => handleInputChange('bedroomFirstFloor', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1st floor</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bedroomSecondFloor} onChange={(e) => handleInputChange('bedroomSecondFloor', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2nd floor</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bedroomBasement} onChange={(e) => handleInputChange('bedroomBasement', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Basement</span></label>
            </div>
          </div>

          <div className="mt-3">
            <Label className="text-sm">Bathroom located on:</Label>
            <div className="flex gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bathroomFirstFloor} onChange={(e) => handleInputChange('bathroomFirstFloor', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1st floor</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bathroomSecondFloor} onChange={(e) => handleInputChange('bathroomSecondFloor', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2nd floor</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bathroomBasement} onChange={(e) => handleInputChange('bathroomBasement', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Basement</span></label>
            </div>
          </div>
        </div>

        {/* Living Conditions/Safety Hazards */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">LIVING CONDITIONS/SAFETY HAZARDS</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all that apply</p>
          
          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyAdequateLighting} onChange={(e) => handleInputChange('safetyAdequateLighting', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Adequate lighting</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyAdequateHeating} onChange={(e) => handleInputChange('safetyAdequateHeating', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Adequate heating/cooling</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyRunningWater} onChange={(e) => handleInputChange('safetyRunningWater', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Running water</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyWorkingPhone} onChange={(e) => handleInputChange('safetyWorkingPhone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Working phone</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetySmokeDetectors} onChange={(e) => handleInputChange('safetySmokeDetectors', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Smoke detectors</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyFireExtinguisher} onChange={(e) => handleInputChange('safetyFireExtinguisher', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Fire extinguisher</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyCleanHome} onChange={(e) => handleInputChange('safetyCleanHome', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Clean home environment</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetySecureRuGs} onChange={(e) => handleInputChange('safetySecureRuGs', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Secured/removed throw rugs</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyClearPathways} onChange={(e) => handleInputChange('safetyClearPathways', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Clear pathways</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyGrabBars} onChange={(e) => handleInputChange('safetyGrabBars', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Grab bars in bathroom</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyShowerChair} onChange={(e) => handleInputChange('safetyShowerChair', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Shower chair/tub bench</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.safetyRaisedToiletSeat} onChange={(e) => handleInputChange('safetyRaisedToiletSeat', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Raised toilet seat</span></label>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Hazards identified:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardLooseRugs} onChange={(e) => handleInputChange('hazardLooseRugs', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Loose/throw rugs</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardClutter} onChange={(e) => handleInputChange('hazardClutter', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Clutter</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardPoorLighting} onChange={(e) => handleInputChange('hazardPoorLighting', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Poor lighting</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardPets} onChange={(e) => handleInputChange('hazardPets', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pets</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardFirearms} onChange={(e) => handleInputChange('hazardFirearms', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Unsecured firearms</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardOxygen} onChange={(e) => handleInputChange('hazardOxygen', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Oxygen in use</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardSmoking} onChange={(e) => handleInputChange('hazardSmoking', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Smoking in home</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.hazardOther} onChange={(e) => handleInputChange('hazardOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.hazardOther && <VoiceDictationInput value={formData.hazardOtherSpecify} onChange={(e) => handleInputChange('hazardOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <VoiceDictationInput label="Safety recommendations made:" value={formData.safetyRecommendations} onChange={(e) => handleInputChange('safetyRecommendations', e.target.value)} rows={2} className="mt-3" />
        </div>
      </div>
    </div>
  );

  const renderSectionG = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section G Functional Status</h3>
        
        {/* M1800-M1860 */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1800. Grooming (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to tend safely to personal hygiene needs (specifically: washing face and hands, hair care, shaving or make up, teeth or denture care, fingernail care)</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to groom self unaided, with or without the use of assistive devices or adapted methods</p>
            <p>1. Grooming utensils must be placed within reach before able to complete grooming activities</p>
            <p>2. Someone must assist the patient to groom self</p>
            <p>3. Patient depends entirely upon someone else for grooming needs</p>
          </div>
          <VoiceDictationInput value={formData.m1800Grooming} onChange={(e) => handleInputChange('m1800Grooming', e.target.value)} placeholder="Enter 0-3" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1810. Current Ability to Dress Upper Body (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Safely: with or without dressing aids, including undergarments, pullovers, front-opening shirts and blouses, managing zippers, buttons, snaps</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to get clothes out of closets and drawers, put them on and remove them from the upper body without assistance</p>
            <p>1. Able to dress upper body without assistance if clothing is laid out or handed to the patient</p>
            <p>2. Someone must help the patient put on upper body clothing</p>
            <p>3. Patient depends entirely upon another person to dress the upper body</p>
          </div>
          <VoiceDictationInput value={formData.m1810DressUpperBody} onChange={(e) => handleInputChange('m1810DressUpperBody', e.target.value)} placeholder="Enter 0-3" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1820. Current Ability to Dress Lower Body (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Safely: with or without dressing aids, including undergarments, slacks, socks or nylons, shoes</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to obtain, put on, and remove clothing and shoes without assistance</p>
            <p>1. Able to dress lower body without assistance if clothing and shoes are laid out or handed to the patient</p>
            <p>2. Someone must help the patient put on undergarments, slacks, socks or nylons, and shoes</p>
            <p>3. Patient depends entirely upon another person to dress lower body</p>
          </div>
          <VoiceDictationInput value={formData.m1820DressLowerBody} onChange={(e) => handleInputChange('m1820DressLowerBody', e.target.value)} placeholder="Enter 0-3" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1830. Bathing (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to wash entire body safely. Excludes grooming (washing face and hands only).</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to bathe self in shower or tub independently, including getting in and out of tub/shower</p>
            <p>1. With the use of devices, is able to bathe self in shower or tub independently</p>
            <p>2. Able to bathe in shower or tub with the intermittent assistance of another person</p>
            <p>3. Able to participate in bathing self in shower or tub, but requires presence of another person throughout the bath</p>
            <p>4. Unable to use the shower or tub, but able to bathe self independently with or without assistive devices</p>
            <p>5. Unable to use the shower or tub, but able to participate in bathing at the bedside, in chair or standing</p>
            <p>6. Unable to participate effectively in bathing and is bathed totally by another person</p>
          </div>
          <VoiceDictationInput value={formData.m1830Bathing} onChange={(e) => handleInputChange('m1830Bathing', e.target.value)} placeholder="Enter 0-6" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1840. Toilet Transferring (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to get to and from the toilet or bedside commode safely and transfer on and off toilet/commode.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to get to and from the toilet and transfer independently with or without a device</p>
            <p>1. When reminded, assisted, or supervised by another person, able to get to and from the toilet and transfer</p>
            <p>2. Unable to get to and from the toilet but is able to use a bedside commode (with or without assistance)</p>
            <p>3. Unable to get to and from the toilet or bedside commode but is able to use a bedpan/urinal independently</p>
            <p>4. Is totally dependent in toileting</p>
          </div>
          <VoiceDictationInput value={formData.m1840ToiletTransferring} onChange={(e) => handleInputChange('m1840ToiletTransferring', e.target.value)} placeholder="Enter 0-4" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1845. Toileting Hygiene (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to maintain perineal hygiene safely, adjust clothes and/or incontinence pads before and after using toilet, commode, bedpan, urinal.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to manage toileting hygiene and target clothing management without assistance</p>
            <p>1. Able to manage toileting hygiene and clothing management without assistance if supplies/equipment are laid out for the patient</p>
            <p>2. Someone must help the patient to maintain toileting hygiene and/or adjust clothing</p>
            <p>3. Patient depends entirely upon another person to maintain toileting hygiene</p>
          </div>
          <VoiceDictationInput value={formData.m1845ToiletingHygiene} onChange={(e) => handleInputChange('m1845ToiletingHygiene', e.target.value)} placeholder="Enter 0-3" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1850. Transferring (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to move safely from bed to chair, or ability to turn and position self in bed if patient is bedfast.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to independently transfer</p>
            <p>1. Able to transfer with minimal human assistance or with use of an assistive device</p>
            <p>2. Able to bear weight and pivot during the transfer process but unable to transfer self</p>
            <p>3. Unable to transfer self and is unable to bear weight or pivot when transferred by another person</p>
            <p>4. Bedfast, unable to transfer but is able to turn and position self in bed</p>
            <p>5. Bedfast, unable to transfer and is unable to turn and position self</p>
          </div>
          <VoiceDictationInput value={formData.m1850Transferring} onChange={(e) => handleInputChange('m1850Transferring', e.target.value)} placeholder="Enter 0-5" className="w-32" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1860. Ambulation/Locomotion (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to walk safely, once in a standing position, or use a wheelchair, once in a seated position, on a variety of surfaces.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to independently walk on even and uneven surfaces and negotiate stairs with or without railings</p>
            <p>1. With the use of a one-handed device, able to independently walk on even and uneven surfaces and negotiate stairs with or without railings</p>
            <p>2. Requires use of a two-handed device to walk alone on a level surface and/or requires human supervision or assistance to negotiate stairs or steps or uneven surfaces</p>
            <p>3. Able to walk only with the supervision or assistance of another person at all times</p>
            <p>4. Chairfast, unable to ambulate but is able to wheel self independently</p>
            <p>5. Chairfast, unable to ambulate and is unable to wheel self</p>
            <p>6. Bedfast, unable to ambulate or be up in a chair</p>
          </div>
          <VoiceDictationInput value={formData.m1860Ambulation} onChange={(e) => handleInputChange('m1860Ambulation', e.target.value)} placeholder="Enter 0-6" className="w-32" />
        </div>

        {/* Activities Permitted */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">ACTIVITIES PERMITTED</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all that apply</p>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesBedrest} onChange={(e) => handleInputChange('activitiesBedrest', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Bedrest</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesBRP} onChange={(e) => handleInputChange('activitiesBRP', e.target.checked)} className="w-4 h-4" /><span className="text-sm">BRP</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesUpAsToleratedWithAssistance} onChange={(e) => handleInputChange('activitiesUpAsToleratedWithAssistance', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Up as tolerated with assistance</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesTransferBedToChairWithAssistance} onChange={(e) => handleInputChange('activitiesTransferBedToChairWithAssistance', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Transfer bed to chair with assistance</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesExercisesInBed} onChange={(e) => handleInputChange('activitiesExercisesInBed', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Exercises in bed</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesPartialWeightBearingRight} onChange={(e) => handleInputChange('activitiesPartialWeightBearingRight', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Partial weight bearing: R</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesPartialWeightBearingLeft} onChange={(e) => handleInputChange('activitiesPartialWeightBearingLeft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">L</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesIndependent} onChange={(e) => handleInputChange('activitiesIndependent', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Independent</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesNoRestrictions} onChange={(e) => handleInputChange('activitiesNoRestrictions', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No restrictions</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.activitiesOther} onChange={(e) => handleInputChange('activitiesOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
          </div>
          {formData.activitiesOther && <VoiceDictationInput value={formData.activitiesOtherSpecify} onChange={(e) => handleInputChange('activitiesOtherSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>

        {/* Prior Functioning */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PRIOR FUNCTIONING: Everyday Activities</Label>
          <p className="text-xs text-gray-600 mb-2">Indicate the patient's usual ability with everyday activities prior to current illness, exacerbation, or injury.</p>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">Self-Care (4 weeks prior):</Label>
            <div className="text-xs text-gray-600 mb-2">
              <p>3. Independent | 2. Needed Some Help | 1. Dependent | 8. Unknown</p>
            </div>
            <VoiceDictationInput value={formData.priorSelfCare} onChange={(e) => handleInputChange('priorSelfCare', e.target.value)} placeholder="Enter 3, 2, 1, or 8" className="w-32" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">Indoor Mobility (4 weeks prior):</Label>
            <div className="text-xs text-gray-600 mb-2">
              <p>3. Independent | 2. Needed Some Help | 1. Dependent | 8. Unknown</p>
            </div>
            <VoiceDictationInput value={formData.priorIndoorMobility} onChange={(e) => handleInputChange('priorIndoorMobility', e.target.value)} placeholder="Enter 3, 2, 1, or 8" className="w-32" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">Stairs (4 weeks prior):</Label>
            <div className="text-xs text-gray-600 mb-2">
              <p>3. Independent | 2. Needed Some Help | 1. Dependent | 8. Unknown | 9. Not Applicable</p>
            </div>
            <VoiceDictationInput value={formData.priorStairs} onChange={(e) => handleInputChange('priorStairs', e.target.value)} placeholder="Enter 3, 2, 1, 8, or 9" className="w-32" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">Functional Cognition (4 weeks prior):</Label>
            <div className="text-xs text-gray-600 mb-2">
              <p>3. Independent | 2. Needed Some Help | 1. Dependent | 8. Unknown</p>
            </div>
            <VoiceDictationInput value={formData.priorFunctionalCognition} onChange={(e) => handleInputChange('priorFunctionalCognition', e.target.value)} placeholder="Enter 3, 2, 1, or 8" className="w-32" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionGG = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section GG Functional Abilities and Goals</h3>
        
        {/* GG0100 Prior Functioning */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GG0100. Prior Functioning: Everyday Activities</Label>
          <p className="text-xs text-gray-600 mb-2">Indicate the patient's usual ability with everyday activities prior to current illness.</p>
          <p className="text-xs text-gray-600 mb-2">Code: 3=Independent | 2=Needed Some Help | 1=Dependent | 8=Unknown</p>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <Label className="text-sm">A. Self-Care:</Label>
              <VoiceDictationInput value={formData.gg0100ASelfCare} onChange={(e) => handleInputChange('gg0100ASelfCare', e.target.value)} placeholder="3, 2, 1, or 8" className="w-24" />
            </div>
            <div>
              <Label className="text-sm">B. Indoor Mobility:</Label>
              <VoiceDictationInput value={formData.gg0100BIndoorMobility} onChange={(e) => handleInputChange('gg0100BIndoorMobility', e.target.value)} placeholder="3, 2, 1, or 8" className="w-24" />
            </div>
            <div>
              <Label className="text-sm">C. Stairs:</Label>
              <VoiceDictationInput value={formData.gg0100CStairs} onChange={(e) => handleInputChange('gg0100CStairs', e.target.value)} placeholder="3, 2, 1, 8, or 9" className="w-24" />
            </div>
            <div>
              <Label className="text-sm">D. Functional Cognition:</Label>
              <VoiceDictationInput value={formData.gg0100DFunctionalCognition} onChange={(e) => handleInputChange('gg0100DFunctionalCognition', e.target.value)} placeholder="3, 2, 1, or 8" className="w-24" />
            </div>
          </div>
        </div>

        {/* GG0130 Self-Care */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GG0130. Self-Care</Label>
          <p className="text-xs text-gray-600 mb-2">Code the patient's usual performance at SOC/ROC for each activity using the 6-point scale.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-3 p-2 bg-gray-50 rounded">
            <p><strong>06</strong> - Independent</p>
            <p><strong>05</strong> - Setup or clean-up assistance</p>
            <p><strong>04</strong> - Supervision or touching assistance</p>
            <p><strong>03</strong> - Partial/moderate assistance</p>
            <p><strong>02</strong> - Substantial/maximal assistance</p>
            <p><strong>01</strong> - Dependent</p>
            <p><strong>07</strong> - Patient refused | <strong>09</strong> - Not applicable | <strong>10</strong> - Not attempted due to environmental limitations | <strong>88</strong> - Not attempted due to medical condition</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Self-Care Activity</th>
                  <th className="border p-2 text-center">SOC/ROC Performance</th>
                  <th className="border p-2 text-center">Discharge Goal</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border p-2">A. Eating</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130AEating} onChange={(e) => handleInputChange('gg0130AEating', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130AEatingGoal} onChange={(e) => handleInputChange('gg0130AEatingGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">B. Oral hygiene</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130BOralHygiene} onChange={(e) => handleInputChange('gg0130BOralHygiene', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130BOralHygieneGoal} onChange={(e) => handleInputChange('gg0130BOralHygieneGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">C. Toileting hygiene</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130CToiletingHygiene} onChange={(e) => handleInputChange('gg0130CToiletingHygiene', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130CToiletingHygieneGoal} onChange={(e) => handleInputChange('gg0130CToiletingHygieneGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">E1. Shower/bathe self</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130E1ShowerBathe} onChange={(e) => handleInputChange('gg0130E1ShowerBathe', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130E1ShowerBatheGoal} onChange={(e) => handleInputChange('gg0130E1ShowerBatheGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">F. Upper body dressing</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130FUpperDressing} onChange={(e) => handleInputChange('gg0130FUpperDressing', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130FUpperDressingGoal} onChange={(e) => handleInputChange('gg0130FUpperDressingGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">G. Lower body dressing</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130GLowerDressing} onChange={(e) => handleInputChange('gg0130GLowerDressing', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130GLowerDressingGoal} onChange={(e) => handleInputChange('gg0130GLowerDressingGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">H. Putting on/taking off footwear</td><td className="border p-1"><VoiceDictationInput value={formData.gg0130HFootwear} onChange={(e) => handleInputChange('gg0130HFootwear', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0130HFootwearGoal} onChange={(e) => handleInputChange('gg0130HFootwearGoal', e.target.value)} /></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* GG0170 Mobility */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GG0170. Mobility</Label>
          <p className="text-xs text-gray-600 mb-2">Code the patient's usual performance at SOC/ROC for each activity using the 6-point scale (same as above).</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Mobility Activity</th>
                  <th className="border p-2 text-center">SOC/ROC Performance</th>
                  <th className="border p-2 text-center">Discharge Goal</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border p-2">A. Roll left and right</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170ARoll} onChange={(e) => handleInputChange('gg0170ARoll', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170ARollGoal} onChange={(e) => handleInputChange('gg0170ARollGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">B. Sit to lying</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170BSitToLying} onChange={(e) => handleInputChange('gg0170BSitToLying', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170BSitToLyingGoal} onChange={(e) => handleInputChange('gg0170BSitToLyingGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">C. Lying to sitting on side of bed</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170CLyingToSitting} onChange={(e) => handleInputChange('gg0170CLyingToSitting', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170CLyingToSittingGoal} onChange={(e) => handleInputChange('gg0170CLyingToSittingGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">D. Sit to stand</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170DSitToStand} onChange={(e) => handleInputChange('gg0170DSitToStand', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170DSitToStandGoal} onChange={(e) => handleInputChange('gg0170DSitToStandGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">E. Chair/bed-to-chair transfer</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170EChairTransfer} onChange={(e) => handleInputChange('gg0170EChairTransfer', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170EChairTransferGoal} onChange={(e) => handleInputChange('gg0170EChairTransferGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">F. Toilet transfer</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170FToiletTransfer} onChange={(e) => handleInputChange('gg0170FToiletTransfer', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170FToiletTransferGoal} onChange={(e) => handleInputChange('gg0170FToiletTransferGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">G. Car transfer</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170GCarTransfer} onChange={(e) => handleInputChange('gg0170GCarTransfer', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170GCarTransferGoal} onChange={(e) => handleInputChange('gg0170GCarTransferGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">I. Walk 10 feet</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170IWalk10Feet} onChange={(e) => handleInputChange('gg0170IWalk10Feet', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170IWalk10FeetGoal} onChange={(e) => handleInputChange('gg0170IWalk10FeetGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">J. Walk 50 feet with two turns</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170JWalk50Feet} onChange={(e) => handleInputChange('gg0170JWalk50Feet', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170JWalk50FeetGoal} onChange={(e) => handleInputChange('gg0170JWalk50FeetGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">K. Walk 150 feet</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170KWalk150Feet} onChange={(e) => handleInputChange('gg0170KWalk150Feet', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170KWalk150FeetGoal} onChange={(e) => handleInputChange('gg0170KWalk150FeetGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">L. Walking 10 feet on uneven surfaces</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170LWalkUneven} onChange={(e) => handleInputChange('gg0170LWalkUneven', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170LWalkUnevenGoal} onChange={(e) => handleInputChange('gg0170LWalkUnevenGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">M. 1 step (curb)</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170M1Step} onChange={(e) => handleInputChange('gg0170M1Step', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170M1StepGoal} onChange={(e) => handleInputChange('gg0170M1StepGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">N. 4 steps</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170N4Steps} onChange={(e) => handleInputChange('gg0170N4Steps', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170N4StepsGoal} onChange={(e) => handleInputChange('gg0170N4StepsGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">O. 12 steps</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170O12Steps} onChange={(e) => handleInputChange('gg0170O12Steps', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170O12StepsGoal} onChange={(e) => handleInputChange('gg0170O12StepsGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">P. Picking up object</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170PPickingUp} onChange={(e) => handleInputChange('gg0170PPickingUp', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170PPickingUpGoal} onChange={(e) => handleInputChange('gg0170PPickingUpGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">RR. Wheel 50 feet with two turns</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170RRWheel50Feet} onChange={(e) => handleInputChange('gg0170RRWheel50Feet', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170RRWheel50FeetGoal} onChange={(e) => handleInputChange('gg0170RRWheel50FeetGoal', e.target.value)} /></td></tr>
                <tr><td className="border p-2">SS. Wheel 150 feet</td><td className="border p-1"><VoiceDictationInput value={formData.gg0170SSWheel150Feet} onChange={(e) => handleInputChange('gg0170SSWheel150Feet', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.gg0170SSWheel150FeetGoal} onChange={(e) => handleInputChange('gg0170SSWheel150FeetGoal', e.target.value)} /></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Fall Risk - MAHC-10 */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">FALL RISK ASSESSMENT (MAHC-10)</Label>
          <p className="text-xs text-gray-600 mb-2">Missouri Alliance for Home Care 10-Item Fall Risk Assessment</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Risk Factor</th>
                  <th className="border p-2 text-center">Points</th>
                  <th className="border p-2 text-center">Score</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border p-2">1. History of falls within 3 months</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10HistoryFalls} onChange={(e) => handleInputChange('mahc10HistoryFalls', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">2. Primary or secondary diagnosis of stroke (CVA), Parkinson's, dementia, or other neurological dx</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10NeuroDx} onChange={(e) => handleInputChange('mahc10NeuroDx', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">3. Vision impairment</td><td className="border p-2 text-center">1</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10VisionImpairment} onChange={(e) => handleInputChange('mahc10VisionImpairment', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">4. Unsteady gait (observed or reported)</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10UnsteadyGait} onChange={(e) => handleInputChange('mahc10UnsteadyGait', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">5. Use of assistive device (cane, walker, wheelchair)</td><td className="border p-2 text-center">1</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10AssistiveDevice} onChange={(e) => handleInputChange('mahc10AssistiveDevice', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">6. Toileting/Transferring assistance required</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10ToiletingAssistance} onChange={(e) => handleInputChange('mahc10ToiletingAssistance', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">7. Patient on 4+ prescriptions</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc104PlusMeds} onChange={(e) => handleInputChange('mahc104PlusMeds', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">8. Patient on diuretic, sedative/hypnotic, or anticonvulsant</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10HighRiskMeds} onChange={(e) => handleInputChange('mahc10HighRiskMeds', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">9. Unsafe home environment observed/reported</td><td className="border p-2 text-center">2</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10UnsafeHome} onChange={(e) => handleInputChange('mahc10UnsafeHome', e.target.value)} className="w-16" /></td></tr>
                <tr><td className="border p-2">10. Age 65 or older</td><td className="border p-2 text-center">1</td><td className="border p-1"><VoiceDictationInput value={formData.mahc10Age65Plus} onChange={(e) => handleInputChange('mahc10Age65Plus', e.target.value)} className="w-16" /></td></tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 rounded">
            <div className="flex items-center gap-4">
              <Label className="font-bold text-sm">Total Score:</Label>
              <VoiceDictationInput value={formData.mahc10TotalScore} onChange={(e) => handleInputChange('mahc10TotalScore', e.target.value)} className="w-24" />
            </div>
            <div className="text-xs text-gray-600 mt-2">
              <p>4 or less = Low Risk | 5-10 = Moderate Risk | 11+ = High Risk</p>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <Label className="text-sm">Risk Level:</Label>
              <label className="flex items-center gap-2"><input type="radio" name="fallRiskLevel" checked={formData.fallRiskLevel === 'Low'} onChange={() => handleInputChange('fallRiskLevel', 'Low')} className="w-4 h-4" /><span className="text-sm">Low</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="fallRiskLevel" checked={formData.fallRiskLevel === 'Moderate'} onChange={() => handleInputChange('fallRiskLevel', 'Moderate')} className="w-4 h-4" /><span className="text-sm">Moderate</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="fallRiskLevel" checked={formData.fallRiskLevel === 'High'} onChange={() => handleInputChange('fallRiskLevel', 'High')} className="w-4 h-4" /><span className="text-sm">High</span></label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionH = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section H Bladder and Bowel</h3>
        
        {/* M1600 Urinary Tract Infection */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1600. Has this patient been treated for a Urinary Tract Infection in the past 14 days? (Enter Code)</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m1600" checked={formData.m1600UTI === '0'} onChange={() => handleInputChange('m1600UTI', '0')} className="w-4 h-4" /><span className="text-sm">0. No</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1600" checked={formData.m1600UTI === '1'} onChange={() => handleInputChange('m1600UTI', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1600" checked={formData.m1600UTI === 'NA'} onChange={() => handleInputChange('m1600UTI', 'NA')} className="w-4 h-4" /><span className="text-sm">NA - Patient on prophylactic treatment</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1600" checked={formData.m1600UTI === 'UK'} onChange={() => handleInputChange('m1600UTI', 'UK')} className="w-4 h-4" /><span className="text-sm">UK - Unknown</span></label>
          </div>
        </div>

        {/* M1610 Urinary Incontinence or Urinary Catheter */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1610. Urinary Incontinence or Urinary Catheter Presence (Enter Code)</Label>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. No incontinence or catheter (includes anuria or ostomy for urinary drainage)</p>
            <p>1. Patient is incontinent</p>
            <p>2. Patient requires a urinary catheter (specifically: external, indwelling, intermittent, or suprapubic)</p>
          </div>
          <VoiceDictationInput value={formData.m1610UrinaryIncontinence} onChange={(e) => handleInputChange('m1610UrinaryIncontinence', e.target.value)} placeholder="Enter 0, 1, or 2" className="w-32" />
          <p className="text-xs text-gray-500 mt-1">→ Skip to M1620, Bowel Incontinence Frequency, if 0 or 2</p>
        </div>

        {/* M1615 When Does Incontinence Occur */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1615. When does Urinary Incontinence occur? (Enter Code)</Label>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Timed-voiding defers incontinence</p>
            <p>1. Occasional stress incontinence</p>
            <p>2. During the night only</p>
            <p>3. During the day and night</p>
          </div>
          <VoiceDictationInput value={formData.m1615WhenIncontinenceOccurs} onChange={(e) => handleInputChange('m1615WhenIncontinenceOccurs', e.target.value)} placeholder="Enter 0, 1, 2, or 3" className="w-32" />
        </div>

        {/* M1620 Bowel Incontinence Frequency */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1620. Bowel Incontinence Frequency (Enter Code)</Label>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Very rarely or never has bowel incontinence</p>
            <p>1. Less than once weekly</p>
            <p>2. One to three times weekly</p>
            <p>3. Four to six times weekly</p>
            <p>4. On a daily basis</p>
            <p>5. More often than once daily</p>
            <p>NA - Patient has ostomy for bowel elimination</p>
            <p>UK - Unknown</p>
          </div>
          <VoiceDictationInput value={formData.m1620BowelIncontinence} onChange={(e) => handleInputChange('m1620BowelIncontinence', e.target.value)} placeholder="Enter 0-5, NA, or UK" className="w-32" />
        </div>

        {/* M1630 Ostomy for Bowel Elimination */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1630. Ostomy for Bowel Elimination (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Does the patient have an ostomy for bowel elimination that requires a change in device and target?</p>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m1630" checked={formData.m1630Ostomy === '0'} onChange={() => handleInputChange('m1630Ostomy', '0')} className="w-4 h-4" /><span className="text-sm">0. Patient does not have an ostomy for bowel elimination</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1630" checked={formData.m1630Ostomy === '1'} onChange={() => handleInputChange('m1630Ostomy', '1')} className="w-4 h-4" /><span className="text-sm">1. Patient's ostomy was not related to an inpatient stay and target does not require a change</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1630" checked={formData.m1630Ostomy === '2'} onChange={() => handleInputChange('m1630Ostomy', '2')} className="w-4 h-4" /><span className="text-sm">2. Patient's ostomy was related to an inpatient stay and target requires a change</span></label>
          </div>
        </div>

        {/* Bladder Assessment */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">BLADDER</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderNoProblems} onChange={(e) => handleInputChange('bladderNoProblems', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problems</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderHematuria} onChange={(e) => handleInputChange('bladderHematuria', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hematuria</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderDysuria} onChange={(e) => handleInputChange('bladderDysuria', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Dysuria</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderFrequency} onChange={(e) => handleInputChange('bladderFrequency', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Frequency</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderUrgency} onChange={(e) => handleInputChange('bladderUrgency', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Urgency</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderNocturia} onChange={(e) => handleInputChange('bladderNocturia', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Nocturia</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderRetention} onChange={(e) => handleInputChange('bladderRetention', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Retention</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderIncontinence} onChange={(e) => handleInputChange('bladderIncontinence', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Incontinence</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bladderInfection} onChange={(e) => handleInputChange('bladderInfection', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Infection</span></label>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Catheter:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.catheterIndwelling} onChange={(e) => handleInputChange('catheterIndwelling', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Indwelling</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.catheterSuprapubic} onChange={(e) => handleInputChange('catheterSuprapubic', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Suprapubic</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.catheterIntermittent} onChange={(e) => handleInputChange('catheterIntermittent', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Intermittent</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.catheterExternal} onChange={(e) => handleInputChange('catheterExternal', e.target.checked)} className="w-4 h-4" /><span className="text-sm">External</span></label>
            </div>
            {(formData.catheterIndwelling || formData.catheterSuprapubic) && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <VoiceDictationInput label="Size:" value={formData.catheterSize} onChange={(e) => handleInputChange('catheterSize', e.target.value)} />
                <VoiceDictationInput label="Type:" value={formData.catheterType} onChange={(e) => handleInputChange('catheterType', e.target.value)} />
                <VoiceDictationInput label="Date inserted:" value={formData.catheterDateInserted} onChange={(e) => handleInputChange('catheterDateInserted', e.target.value)} />
                <VoiceDictationInput label="Change frequency:" value={formData.catheterChangeFrequency} onChange={(e) => handleInputChange('catheterChangeFrequency', e.target.value)} />
              </div>
            )}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Urinary appliance:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.urinaryApplianceNone} onChange={(e) => handleInputChange('urinaryApplianceNone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">None</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.urinaryApplianceCondomCatheter} onChange={(e) => handleInputChange('urinaryApplianceCondomCatheter', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Condom catheter</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.urinaryApplianceBriefs} onChange={(e) => handleInputChange('urinaryApplianceBriefs', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Briefs</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.urinaryAppliancePads} onChange={(e) => handleInputChange('urinaryAppliancePads', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pads</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.urinaryApplianceOther} onChange={(e) => handleInputChange('urinaryApplianceOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.urinaryApplianceOther && <VoiceDictationInput value={formData.urinaryApplianceOtherSpecify} onChange={(e) => handleInputChange('urinaryApplianceOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
        </div>

        {/* Bowel Assessment */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">BOWEL</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelNoProblems} onChange={(e) => handleInputChange('bowelNoProblems', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problems</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelConstipation} onChange={(e) => handleInputChange('bowelConstipation', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Constipation</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelDiarrhea} onChange={(e) => handleInputChange('bowelDiarrhea', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Diarrhea</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelIncontinence} onChange={(e) => handleInputChange('bowelIncontinence', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Incontinence</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelFlatulence} onChange={(e) => handleInputChange('bowelFlatulence', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Flatulence</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelOstomy} onChange={(e) => handleInputChange('bowelOstomy', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Ostomy</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelRectalBleeding} onChange={(e) => handleInputChange('bowelRectalBleeding', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Rectal Bleeding</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bowelHemorrhoids} onChange={(e) => handleInputChange('bowelHemorrhoids', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hemorrhoids</span></label>
          </div>
          
          <VoiceDictationInput label="Usual bowel pattern:" value={formData.usualBowelPattern} onChange={(e) => handleInputChange('usualBowelPattern', e.target.value)} className="mt-3" />
          <VoiceDictationInput label="Last BM (date):" value={formData.lastBMDate} onChange={(e) => handleInputChange('lastBMDate', e.target.value)} className="mt-2 w-48" />
          
          {formData.bowelOstomy && (
            <div className="mt-3 p-3 bg-gray-50 rounded">
              <Label className="text-sm font-bold">Ostomy details:</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <VoiceDictationInput label="Type:" value={formData.ostomyType} onChange={(e) => handleInputChange('ostomyType', e.target.value)} />
                <VoiceDictationInput label="Location:" value={formData.ostomyLocation} onChange={(e) => handleInputChange('ostomyLocation', e.target.value)} />
                <VoiceDictationInput label="Stoma condition:" value={formData.stomaCondition} onChange={(e) => handleInputChange('stomaCondition', e.target.value)} />
                <VoiceDictationInput label="Appliance type:" value={formData.ostomyApplianceType} onChange={(e) => handleInputChange('ostomyApplianceType', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* Genitalia */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GENITALIA</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.genitaliaNoProblems} onChange={(e) => handleInputChange('genitaliaNoProblems', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problems</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.genitaliaNotAssessed} onChange={(e) => handleInputChange('genitaliaNotAssessed', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Not Assessed</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.genitaliaDischarge} onChange={(e) => handleInputChange('genitaliaDischarge', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Discharge</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.genitaliaLesions} onChange={(e) => handleInputChange('genitaliaLesions', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Lesions</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.genitaliaEdema} onChange={(e) => handleInputChange('genitaliaEdema', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Edema</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.genitaliaOther} onChange={(e) => handleInputChange('genitaliaOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
          </div>
          {formData.genitaliaOther && <VoiceDictationInput value={formData.genitaliaOtherSpecify} onChange={(e) => handleInputChange('genitaliaOtherSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>
      </div>
    </div>
  );

  const renderSectionI = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section I Active Diagnoses</h3>
        
        {/* M1021 Primary Diagnosis */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1021. Primary Diagnosis & ICD-10-CM Code</Label>
          <p className="text-xs text-gray-600 mb-2">Identify the patient's primary diagnosis (the diagnosis most related to current plan of care)</p>
          
          <div className="grid grid-cols-2 gap-4">
            <VoiceDictationInput label="Primary Diagnosis Description:" value={formData.m1021PrimaryDiagnosis} onChange={(e) => handleInputChange('m1021PrimaryDiagnosis', e.target.value)} />
            <VoiceDictationInput label="ICD-10-CM Code:" value={formData.m1021PrimaryDiagnosisCode} onChange={(e) => handleInputChange('m1021PrimaryDiagnosisCode', e.target.value)} />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Symptom Control Rating (0-4):</Label>
            <p className="text-xs text-gray-500">0=Asymptomatic | 1=Symptoms well controlled | 2=Symptoms controlled with difficulty | 3=Symptoms poorly controlled | 4=Symptoms uncontrolled</p>
            <VoiceDictationInput value={formData.m1021SymptomControlRating} onChange={(e) => handleInputChange('m1021SymptomControlRating', e.target.value)} placeholder="0-4" className="w-24 mt-1" />
          </div>
        </div>

        {/* M1023 Other Diagnoses */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1023. Other Diagnoses & ICD-10-CM Codes</Label>
          <p className="text-xs text-gray-600 mb-2">List each diagnosis for which the patient is receiving home care (maximum 24 active diagnoses plus the primary)</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">#</th>
                  <th className="border p-2 text-left">Diagnosis Description</th>
                  <th className="border p-2 text-center">ICD-10-CM Code</th>
                  <th className="border p-2 text-center">Symptom Rating (V)</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(6)].map((_, i) => (
                  <tr key={i}>
                    <td className="border p-2">{String.fromCharCode(98 + i)}.</td>
                    <td className="border p-1"><VoiceDictationInput value={formData[`m1023Diagnosis${i + 2}Description`]} onChange={(e) => handleInputChange(`m1023Diagnosis${i + 2}Description`, e.target.value)} /></td>
                    <td className="border p-1"><VoiceDictationInput value={formData[`m1023Diagnosis${i + 2}Code`]} onChange={(e) => handleInputChange(`m1023Diagnosis${i + 2}Code`, e.target.value)} className="w-32" /></td>
                    <td className="border p-1"><VoiceDictationInput value={formData[`m1023Diagnosis${i + 2}Rating`]} onChange={(e) => handleInputChange(`m1023Diagnosis${i + 2}Rating`, e.target.value)} className="w-16" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <VoiceDictationInput label="Additional Diagnoses (continue if needed):" value={formData.additionalDiagnoses} onChange={(e) => handleInputChange('additionalDiagnoses', e.target.value)} rows={3} className="mt-3" />
        </div>

        {/* M1028 Active Diagnoses - Comorbidities */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1028. Active Diagnoses - Comorbidities and Co-existing Conditions</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all that apply</p>
          
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1028PeripheralVascular} onChange={(e) => handleInputChange('m1028PeripheralVascular', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1. Peripheral Vascular Disease (PVD) or Peripheral Arterial Disease (PAD)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1028DMType1Insulin} onChange={(e) => handleInputChange('m1028DMType1Insulin', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2. Diabetes Mellitus (DM) - Type 1 - with or without insulin</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1028DMType2NoInsulin} onChange={(e) => handleInputChange('m1028DMType2NoInsulin', e.target.checked)} className="w-4 h-4" /><span className="text-sm">3. DM - Type 2 - No Insulin</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1028DMType2Insulin} onChange={(e) => handleInputChange('m1028DMType2Insulin', e.target.checked)} className="w-4 h-4" /><span className="text-sm">4. DM - Type 2 - Insulin</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1028NoneOfAbove} onChange={(e) => handleInputChange('m1028NoneOfAbove', e.target.checked)} className="w-4 h-4" /><span className="text-sm">5. None of the above</span></label>
          </div>
        </div>

        {/* Endocrine/Hematology */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">ENDOCRINE/HEMATOLOGY</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineNoProblems} onChange={(e) => handleInputChange('endocrineNoProblems', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problems</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineDiabetesType1} onChange={(e) => handleInputChange('endocrineDiabetesType1', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Diabetes Type 1</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineDiabetesType2} onChange={(e) => handleInputChange('endocrineDiabetesType2', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Diabetes Type 2</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineThyroid} onChange={(e) => handleInputChange('endocrineThyroid', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Thyroid disorder</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineAnemia} onChange={(e) => handleInputChange('endocrineAnemia', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Anemia</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineBleedingDisorder} onChange={(e) => handleInputChange('endocrineBleedingDisorder', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Bleeding disorder</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.endocrineOther} onChange={(e) => handleInputChange('endocrineOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
          </div>
          {formData.endocrineOther && <VoiceDictationInput value={formData.endocrineOtherSpecify} onChange={(e) => handleInputChange('endocrineOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          
          {(formData.endocrineDiabetesType1 || formData.endocrineDiabetesType2) && (
            <div className="mt-3 p-3 bg-gray-50 rounded">
              <Label className="text-sm font-bold">Diabetes Management:</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.diabetesManagementDiet} onChange={(e) => handleInputChange('diabetesManagementDiet', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Diet</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.diabetesManagementOralMeds} onChange={(e) => handleInputChange('diabetesManagementOralMeds', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Oral medications</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={formData.diabetesManagementInsulin} onChange={(e) => handleInputChange('diabetesManagementInsulin', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Insulin</span></label>
              </div>
              <VoiceDictationInput label="Blood glucose testing frequency:" value={formData.bloodGlucoseTestingFrequency} onChange={(e) => handleInputChange('bloodGlucoseTestingFrequency', e.target.value)} className="mt-2" />
              <VoiceDictationInput label="Target blood glucose range:" value={formData.targetBloodGlucoseRange} onChange={(e) => handleInputChange('targetBloodGlucoseRange', e.target.value)} className="mt-2" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSectionJ = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section J Health Conditions</h3>
        
        {/* M1033 Risk for Hospitalization */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1033. Risk for Hospitalization</Label>
          <p className="text-xs text-gray-600 mb-2">Which of the following signs or symptoms characterize this patient as at risk for hospitalization? ↓ Check all that apply</p>
          
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1033RecentDecline} onChange={(e) => handleInputChange('m1033RecentDecline', e.target.checked)} className="w-4 h-4" /><span className="text-sm">1. Recent decline in mental, emotional, or behavioral status</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1033MultipleHospitalizations} onChange={(e) => handleInputChange('m1033MultipleHospitalizations', e.target.checked)} className="w-4 h-4" /><span className="text-sm">2. Multiple hospitalizations (2 or more) in the past 6 months</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1033HistoryFalls} onChange={(e) => handleInputChange('m1033HistoryFalls', e.target.checked)} className="w-4 h-4" /><span className="text-sm">3. History of falls (2 or more falls - or any fall with an injury - in the past 12 months)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m10335PlusMedications} onChange={(e) => handleInputChange('m10335PlusMedications', e.target.checked)} className="w-4 h-4" /><span className="text-sm">4. Taking 5 or more medications</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1033Frailty} onChange={(e) => handleInputChange('m1033Frailty', e.target.checked)} className="w-4 h-4" /><span className="text-sm">5. Frailty indicators (e.g., unintentional weight loss, self-reported exhaustion)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1033OtherRisk} onChange={(e) => handleInputChange('m1033OtherRisk', e.target.checked)} className="w-4 h-4" /><span className="text-sm">6. Other (specify)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.m1033NoneOfAbove} onChange={(e) => handleInputChange('m1033NoneOfAbove', e.target.checked)} className="w-4 h-4" /><span className="text-sm">7. None of the above</span></label>
          </div>
          {formData.m1033OtherRisk && <VoiceDictationInput value={formData.m1033OtherRiskSpecify} onChange={(e) => handleInputChange('m1033OtherRiskSpecify', e.target.value)} className="mt-2 w-64" />}
        </div>

        {/* Pain Assessment */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PAIN ASSESSMENT</Label>
          
          <div className="mt-3">
            <Label className="text-sm">J0510. Pain Effect on Sleep (Enter Code)</Label>
            <p className="text-xs text-gray-600 mb-1">Over the past 5 days, how much of the time has pain made it hard for you to sleep at night?</p>
            <div className="text-xs text-gray-600 space-y-1 mb-2">
              <p>0. Does not apply - I have not had any pain or hurting in the past 5 days</p>
              <p>1. Rarely or not at all</p>
              <p>2. Occasionally</p>
              <p>3. Frequently</p>
              <p>4. Almost constantly</p>
              <p>8. Unable to answer</p>
            </div>
            <VoiceDictationInput value={formData.j0510PainEffectSleep} onChange={(e) => handleInputChange('j0510PainEffectSleep', e.target.value)} placeholder="Enter 0-4 or 8" className="w-32" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">J0520. Pain Interference with Therapy Activities (Enter Code)</Label>
            <p className="text-xs text-gray-600 mb-1">Over the past 5 days, how often have you limited your day-to-day activities because of pain?</p>
            <div className="text-xs text-gray-600 space-y-1 mb-2">
              <p>0. Does not apply - I have not had any pain or hurting in the past 5 days</p>
              <p>1. Rarely or not at all</p>
              <p>2. Occasionally</p>
              <p>3. Frequently</p>
              <p>4. Almost constantly</p>
              <p>8. Unable to answer</p>
            </div>
            <VoiceDictationInput value={formData.j0520PainInterference} onChange={(e) => handleInputChange('j0520PainInterference', e.target.value)} placeholder="Enter 0-4 or 8" className="w-32" />
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">J1800. Any Falls Since SOC/ROC (Enter Code)</Label>
            <p className="text-xs text-gray-600 mb-1">Has the patient had any falls since SOC/ROC?</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="radio" name="j1800" checked={formData.j1800FallsSinceSOCROC === '0'} onChange={() => handleInputChange('j1800FallsSinceSOCROC', '0')} className="w-4 h-4" /><span className="text-sm">0. No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="j1800" checked={formData.j1800FallsSinceSOCROC === '1'} onChange={() => handleInputChange('j1800FallsSinceSOCROC', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes</span></label>
            </div>
          </div>
          
          {formData.j1800FallsSinceSOCROC === '1' && (
            <div className="mt-3">
              <Label className="text-sm">J1900. Number of Falls Since SOC/ROC (Enter Code for each type)</Label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div>
                  <Label className="text-xs">A. No injury:</Label>
                  <VoiceDictationInput value={formData.j1900ANoInjury} onChange={(e) => handleInputChange('j1900ANoInjury', e.target.value)} placeholder="0, 1, 2+" className="w-24" />
                </div>
                <div>
                  <Label className="text-xs">B. Injury (except major):</Label>
                  <VoiceDictationInput value={formData.j1900BInjury} onChange={(e) => handleInputChange('j1900BInjury', e.target.value)} placeholder="0, 1, 2+" className="w-24" />
                </div>
                <div>
                  <Label className="text-xs">C. Major injury:</Label>
                  <VoiceDictationInput value={formData.j1900CMajorInjury} onChange={(e) => handleInputChange('j1900CMajorInjury', e.target.value)} placeholder="0, 1, 2+" className="w-24" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pain Characteristics */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PAIN CHARACTERISTICS</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Does patient currently have pain?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="hasPain" checked={formData.hasCurrentPain === 'No'} onChange={() => handleInputChange('hasCurrentPain', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="hasPain" checked={formData.hasCurrentPain === 'Yes'} onChange={() => handleInputChange('hasCurrentPain', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
          </div>
          
          {formData.hasCurrentPain === 'Yes' && (
            <>
              <div className="mt-3">
                <Label className="text-sm">Pain intensity (0-10 scale):</Label>
                <div className="grid grid-cols-3 gap-4 mt-1">
                  <VoiceDictationInput label="Current:" value={formData.painIntensityCurrent} onChange={(e) => handleInputChange('painIntensityCurrent', e.target.value)} placeholder="0-10" />
                  <VoiceDictationInput label="Worst (24 hrs):" value={formData.painIntensityWorst} onChange={(e) => handleInputChange('painIntensityWorst', e.target.value)} placeholder="0-10" />
                  <VoiceDictationInput label="Acceptable level:" value={formData.painIntensityAcceptable} onChange={(e) => handleInputChange('painIntensityAcceptable', e.target.value)} placeholder="0-10" />
                </div>
              </div>
              
              <VoiceDictationInput label="Location(s) of pain:" value={formData.painLocations} onChange={(e) => handleInputChange('painLocations', e.target.value)} className="mt-3" />
              
              <div className="mt-3">
                <Label className="text-sm">Quality/Description:</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualitySharp} onChange={(e) => handleInputChange('painQualitySharp', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Sharp</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualityDull} onChange={(e) => handleInputChange('painQualityDull', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Dull</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualityAching} onChange={(e) => handleInputChange('painQualityAching', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Aching</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualityBurning} onChange={(e) => handleInputChange('painQualityBurning', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Burning</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualityThrobbing} onChange={(e) => handleInputChange('painQualityThrobbing', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Throbbing</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualityRadiating} onChange={(e) => handleInputChange('painQualityRadiating', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Radiating</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.painQualityOther} onChange={(e) => handleInputChange('painQualityOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
                </div>
                {formData.painQualityOther && <VoiceDictationInput value={formData.painQualityOtherSpecify} onChange={(e) => handleInputChange('painQualityOtherSpecify', e.target.value)} className="mt-2 w-64" />}
              </div>
              
              <div className="mt-3">
                <Label className="text-sm">Frequency:</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  <label className="flex items-center gap-2"><input type="radio" name="painFreq" checked={formData.painFrequency === 'Constant'} onChange={() => handleInputChange('painFrequency', 'Constant')} className="w-4 h-4" /><span className="text-sm">Constant</span></label>
                  <label className="flex items-center gap-2"><input type="radio" name="painFreq" checked={formData.painFrequency === 'Intermittent'} onChange={() => handleInputChange('painFrequency', 'Intermittent')} className="w-4 h-4" /><span className="text-sm">Intermittent</span></label>
                  <label className="flex items-center gap-2"><input type="radio" name="painFreq" checked={formData.painFrequency === 'Occasional'} onChange={() => handleInputChange('painFrequency', 'Occasional')} className="w-4 h-4" /><span className="text-sm">Occasional</span></label>
                </div>
              </div>
              
              <VoiceDictationInput label="What relieves pain:" value={formData.painRelievedBy} onChange={(e) => handleInputChange('painRelievedBy', e.target.value)} className="mt-3" />
              <VoiceDictationInput label="What aggravates pain:" value={formData.painAggravatedBy} onChange={(e) => handleInputChange('painAggravatedBy', e.target.value)} className="mt-2" />
            </>
          )}
        </div>

        {/* Cardiopulmonary */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">CARDIOPULMONARY STATUS</Label>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">Cardiovascular:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularNoProblems} onChange={(e) => handleInputChange('cardiovascularNoProblems', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problems</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularChestPain} onChange={(e) => handleInputChange('cardiovascularChestPain', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Chest pain</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularPalpitations} onChange={(e) => handleInputChange('cardiovascularPalpitations', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Palpitations</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularEdema} onChange={(e) => handleInputChange('cardiovascularEdema', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Edema</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularDOE} onChange={(e) => handleInputChange('cardiovascularDOE', e.target.checked)} className="w-4 h-4" /><span className="text-sm">DOE</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularOrthopnea} onChange={(e) => handleInputChange('cardiovascularOrthopnea', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Orthopnea</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularPND} onChange={(e) => handleInputChange('cardiovascularPND', e.target.checked)} className="w-4 h-4" /><span className="text-sm">PND</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularPacemaker} onChange={(e) => handleInputChange('cardiovascularPacemaker', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pacemaker</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cardiovascularAICD} onChange={(e) => handleInputChange('cardiovascularAICD', e.target.checked)} className="w-4 h-4" /><span className="text-sm">AICD</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm font-medium">Respiratory:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respiratoryNoProblems} onChange={(e) => handleInputChange('respiratoryNoProblems', e.target.checked)} className="w-4 h-4" /><span className="text-sm">No Problems</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respiratoryDyspnea} onChange={(e) => handleInputChange('respiratoryDyspnea', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Dyspnea</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respiratoryCough} onChange={(e) => handleInputChange('respiratoryCough', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cough</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respiratoryWheezing} onChange={(e) => handleInputChange('respiratoryWheezing', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Wheezing</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respiratorySputum} onChange={(e) => handleInputChange('respiratorySputum', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Sputum production</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.respiratoryOxygenUse} onChange={(e) => handleInputChange('respiratoryOxygenUse', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Oxygen use</span></label>
            </div>
            {formData.respiratoryOxygenUse && (
              <div className="mt-2 p-3 bg-gray-50 rounded">
                <VoiceDictationInput label="Oxygen flow rate:" value={formData.oxygenFlowRate} onChange={(e) => handleInputChange('oxygenFlowRate', e.target.value)} className="mb-2" />
                <VoiceDictationInput label="Delivery method:" value={formData.oxygenDeliveryMethod} onChange={(e) => handleInputChange('oxygenDeliveryMethod', e.target.value)} />
              </div>
            )}
          </div>
        </div>

        {/* M1400 Dyspnea */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1400. When is the patient dyspneic or noticeably short of breath? (Enter Code)</Label>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Patient is not short of breath</p>
            <p>1. When walking more than 20 feet, climbing stairs</p>
            <p>2. With moderate exertion (e.g., while dressing, using commode, walking distances greater than 20 feet)</p>
            <p>3. With minimal exertion (e.g., while eating, talking, or performing other ADLs) or with agitation</p>
            <p>4. At rest (during day or night)</p>
          </div>
          <VoiceDictationInput value={formData.m1400Dyspnea} onChange={(e) => handleInputChange('m1400Dyspnea', e.target.value)} placeholder="Enter 0-4" className="w-32" />
        </div>
      </div>
    </div>
  );

  const renderSectionK = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section K Swallowing/Nutritional Status</h3>
        
        {/* M1060 Height and Weight */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1060. Height and Weight</Label>
          <p className="text-xs text-gray-600 mb-2">While measuring, if the number is X.1-X.4 round down; X.5 or greater round up</p>
          
          <div className="grid grid-cols-3 gap-4 mt-2">
            <div>
              <VoiceDictationInput label="a. Height (in inches):" value={formData.m1060Height} onChange={(e) => handleInputChange('m1060Height', e.target.value)} />
              <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={formData.m1060HeightUK} onChange={(e) => handleInputChange('m1060HeightUK', e.target.checked)} className="w-4 h-4" /><span className="text-xs">UK Unknown</span></label>
            </div>
            <div>
              <VoiceDictationInput label="b. Weight (in pounds):" value={formData.m1060Weight} onChange={(e) => handleInputChange('m1060Weight', e.target.value)} />
              <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={formData.m1060WeightUK} onChange={(e) => handleInputChange('m1060WeightUK', e.target.checked)} className="w-4 h-4" /><span className="text-xs">UK Unknown</span></label>
            </div>
            <div>
              <VoiceDictationInput label="BMI (calculated):" value={formData.bmiCalculated} onChange={(e) => handleInputChange('bmiCalculated', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Nutritional Screening */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">NUTRITIONAL SCREENING</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Has the patient lost or gained 10 lbs. in the last month?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="weightChange" checked={formData.weightChangeLast30Days === 'No'} onChange={() => handleInputChange('weightChangeLast30Days', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="weightChange" checked={formData.weightChangeLast30Days === 'Yes'} onChange={() => handleInputChange('weightChangeLast30Days', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.weightChangeLast30Days === 'Yes' && (
              <div className="flex gap-4 mt-2 ml-4">
                <label className="flex items-center gap-2"><input type="radio" name="weightChangeType" checked={formData.weightChangeType === 'Gain'} onChange={() => handleInputChange('weightChangeType', 'Gain')} className="w-4 h-4" /><span className="text-sm">Gain</span></label>
                <label className="flex items-center gap-2"><input type="radio" name="weightChangeType" checked={formData.weightChangeType === 'Loss'} onChange={() => handleInputChange('weightChangeType', 'Loss')} className="w-4 h-4" /><span className="text-sm">Loss</span></label>
                <VoiceDictationInput label="Amount:" value={formData.weightChangeAmount} onChange={(e) => handleInputChange('weightChangeAmount', e.target.value)} className="w-24" />
              </div>
            )}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Appetite:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="appetite" checked={formData.appetite === 'Good'} onChange={() => handleInputChange('appetite', 'Good')} className="w-4 h-4" /><span className="text-sm">Good</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="appetite" checked={formData.appetite === 'Fair'} onChange={() => handleInputChange('appetite', 'Fair')} className="w-4 h-4" /><span className="text-sm">Fair</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="appetite" checked={formData.appetite === 'Poor'} onChange={() => handleInputChange('appetite', 'Poor')} className="w-4 h-4" /><span className="text-sm">Poor</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Diet:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietRegular} onChange={(e) => handleInputChange('dietRegular', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Regular</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietDiabetic} onChange={(e) => handleInputChange('dietDiabetic', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Diabetic</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietLowSodium} onChange={(e) => handleInputChange('dietLowSodium', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Low sodium</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietLowFat} onChange={(e) => handleInputChange('dietLowFat', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Low fat</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietRenal} onChange={(e) => handleInputChange('dietRenal', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Renal</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietMechanicalSoft} onChange={(e) => handleInputChange('dietMechanicalSoft', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Mechanical soft</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietPureed} onChange={(e) => handleInputChange('dietPureed', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pureed</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietNPO} onChange={(e) => handleInputChange('dietNPO', e.target.checked)} className="w-4 h-4" /><span className="text-sm">NPO</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dietOther} onChange={(e) => handleInputChange('dietOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.dietOther && <VoiceDictationInput value={formData.dietOtherSpecify} onChange={(e) => handleInputChange('dietOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Fluid restriction:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="fluidRestriction" checked={formData.fluidRestriction === 'No'} onChange={() => handleInputChange('fluidRestriction', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="fluidRestriction" checked={formData.fluidRestriction === 'Yes'} onChange={() => handleInputChange('fluidRestriction', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.fluidRestriction === 'Yes' && <VoiceDictationInput label="Amount:" value={formData.fluidRestrictionAmount} onChange={(e) => handleInputChange('fluidRestrictionAmount', e.target.value)} className="mt-2 w-48" />}
          </div>
        </div>

        {/* K0520 Nutritional Approaches */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">K0520. Nutritional Approaches</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all that were received during the last 7 days</p>
          
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.k0520ParenteralIV} onChange={(e) => handleInputChange('k0520ParenteralIV', e.target.checked)} className="w-4 h-4" /><span className="text-sm">A. Parenteral/IV feeding</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.k0520FeedingTube} onChange={(e) => handleInputChange('k0520FeedingTube', e.target.checked)} className="w-4 h-4" /><span className="text-sm">B. Feeding tube (e.g., nasogastric or abdominal)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.k0520MechanicallyAlteredDiet} onChange={(e) => handleInputChange('k0520MechanicallyAlteredDiet', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C. Mechanically altered diet</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.k0520TherapeuticDiet} onChange={(e) => handleInputChange('k0520TherapeuticDiet', e.target.checked)} className="w-4 h-4" /><span className="text-sm">D. Therapeutic diet</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.k0520NoneOfAbove} onChange={(e) => handleInputChange('k0520NoneOfAbove', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Z. None of the above</span></label>
          </div>
        </div>

        {/* M1870 Feeding or Eating */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1870. Feeding or Eating (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Current ability to feed self meals and snacks safely. Note: This refers only to the process of eating, chewing, and swallowing, not preparing the food to be eaten.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to independently feed self</p>
            <p>1. Able to feed self independently but requires: (a) meal set-up; OR (b) intermittent assistance/supervision from another person; OR (c) a liquid, pureed, or ground meat diet</p>
            <p>2. Unable to feed self and must be assisted or supervised throughout the meal/snack</p>
            <p>3. Able to take in nutrients orally and receives supplemental nutrients through a nasogastric tube or gastrostomy</p>
            <p>4. Unable to take in nutrients orally and is fed nutrients through a nasogastric tube or gastrostomy</p>
            <p>5. Unable to take in nutrients orally or by tube feeding</p>
          </div>
          <VoiceDictationInput value={formData.m1870FeedingOrEating} onChange={(e) => handleInputChange('m1870FeedingOrEating', e.target.value)} placeholder="Enter 0-5" className="w-32" />
        </div>

        {/* Swallowing/Dysphagia */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">SWALLOWING/DYSPHAGIA</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Does patient have difficulty swallowing?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="swallowingDifficulty" checked={formData.swallowingDifficulty === 'No'} onChange={() => handleInputChange('swallowingDifficulty', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="swallowingDifficulty" checked={formData.swallowingDifficulty === 'Yes'} onChange={() => handleInputChange('swallowingDifficulty', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
          </div>
          
          {formData.swallowingDifficulty === 'Yes' && (
            <>
              <div className="mt-3">
                <Label className="text-sm">Type of dysphagia:</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaLiquids} onChange={(e) => handleInputChange('dysphagiaLiquids', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Liquids</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaSolids} onChange={(e) => handleInputChange('dysphagiaSolids', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Solids</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaPills} onChange={(e) => handleInputChange('dysphagiaPills', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pills</span></label>
                </div>
              </div>
              
              <div className="mt-3">
                <Label className="text-sm">Signs/symptoms:</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaCoughing} onChange={(e) => handleInputChange('dysphagiaCoughing', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Coughing during/after meals</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaChoking} onChange={(e) => handleInputChange('dysphagiaChoking', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Choking</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaDrooling} onChange={(e) => handleInputChange('dysphagiaDrooling', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Drooling</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dysphagiaPocketing} onChange={(e) => handleInputChange('dysphagiaPocketing', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pocketing food</span></label>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Enteral Feeding */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">ENTERAL FEEDING (if applicable)</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Type of tube:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.enteralTubeNG} onChange={(e) => handleInputChange('enteralTubeNG', e.target.checked)} className="w-4 h-4" /><span className="text-sm">NG tube</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.enteralTubePEG} onChange={(e) => handleInputChange('enteralTubePEG', e.target.checked)} className="w-4 h-4" /><span className="text-sm">PEG tube</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.enteralTubeJejunostomy} onChange={(e) => handleInputChange('enteralTubeJejunostomy', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Jejunostomy</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.enteralTubeOther} onChange={(e) => handleInputChange('enteralTubeOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.enteralTubeOther && <VoiceDictationInput value={formData.enteralTubeOtherSpecify} onChange={(e) => handleInputChange('enteralTubeOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <VoiceDictationInput label="Formula name:" value={formData.enteralFormulaName} onChange={(e) => handleInputChange('enteralFormulaName', e.target.value)} />
            <VoiceDictationInput label="Rate/frequency:" value={formData.enteralRateFrequency} onChange={(e) => handleInputChange('enteralRateFrequency', e.target.value)} />
            <VoiceDictationInput label="Tube size:" value={formData.enteralTubeSize} onChange={(e) => handleInputChange('enteralTubeSize', e.target.value)} />
            <VoiceDictationInput label="Date placed:" value={formData.enteralTubeDatePlaced} onChange={(e) => handleInputChange('enteralTubeDatePlaced', e.target.value)} />
          </div>
          
          <VoiceDictationInput label="Insertion site condition:" value={formData.enteralInsertionSiteCondition} onChange={(e) => handleInputChange('enteralInsertionSiteCondition', e.target.value)} className="mt-3" />
        </div>
      </div>
    </div>
  );

  const renderSectionM = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section M Skin Conditions</h3>
        
        {/* Integumentary Status */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">INTEGUMENTARY STATUS</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Skin condition:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinIntact} onChange={(e) => handleInputChange('skinIntact', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Intact</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinDry} onChange={(e) => handleInputChange('skinDry', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Dry</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinFragile} onChange={(e) => handleInputChange('skinFragile', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Fragile</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinDiscolored} onChange={(e) => handleInputChange('skinDiscolored', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Discolored</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinRash} onChange={(e) => handleInputChange('skinRash', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Rash</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinBruising} onChange={(e) => handleInputChange('skinBruising', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Bruising</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinEdema} onChange={(e) => handleInputChange('skinEdema', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Edema</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinJaundice} onChange={(e) => handleInputChange('skinJaundice', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Jaundice</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.skinWound} onChange={(e) => handleInputChange('skinWound', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Open wound(s)</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Turgor:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="skinTurgor" checked={formData.skinTurgor === 'Good'} onChange={() => handleInputChange('skinTurgor', 'Good')} className="w-4 h-4" /><span className="text-sm">Good</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="skinTurgor" checked={formData.skinTurgor === 'Fair'} onChange={() => handleInputChange('skinTurgor', 'Fair')} className="w-4 h-4" /><span className="text-sm">Fair</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="skinTurgor" checked={formData.skinTurgor === 'Poor'} onChange={() => handleInputChange('skinTurgor', 'Poor')} className="w-4 h-4" /><span className="text-sm">Poor</span></label>
            </div>
          </div>
        </div>

        {/* M1306 Unhealed Pressure Ulcer */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1306. Does this patient have at least one Unhealed Pressure Ulcer/Injury at Stage 2 or Higher or designated as Unstageable?</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m1306" checked={formData.m1306UnhealedPressureUlcer === '0'} onChange={() => handleInputChange('m1306UnhealedPressureUlcer', '0')} className="w-4 h-4" /><span className="text-sm">0. No → Skip to M1322</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1306" checked={formData.m1306UnhealedPressureUlcer === '1'} onChange={() => handleInputChange('m1306UnhealedPressureUlcer', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes</span></label>
          </div>
        </div>

        {/* M1311 Current Number of Unhealed Pressure Ulcers */}
        {formData.m1306UnhealedPressureUlcer === '1' && (
          <div className="bg-white p-4 rounded border mb-4">
            <Label className="font-bold text-sm">M1311. Current Number of Unhealed Pressure Ulcers/Injuries at Each Stage</Label>
            
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Stage</th>
                    <th className="border p-2 text-center">Number Present</th>
                    <th className="border p-2 text-center">Number New/Worsened</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border p-2">A. Stage 2</td><td className="border p-1"><VoiceDictationInput value={formData.m1311Stage2Present} onChange={(e) => handleInputChange('m1311Stage2Present', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.m1311Stage2Worsened} onChange={(e) => handleInputChange('m1311Stage2Worsened', e.target.value)} /></td></tr>
                  <tr><td className="border p-2">B. Stage 3</td><td className="border p-1"><VoiceDictationInput value={formData.m1311Stage3Present} onChange={(e) => handleInputChange('m1311Stage3Present', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.m1311Stage3Worsened} onChange={(e) => handleInputChange('m1311Stage3Worsened', e.target.value)} /></td></tr>
                  <tr><td className="border p-2">C. Stage 4</td><td className="border p-1"><VoiceDictationInput value={formData.m1311Stage4Present} onChange={(e) => handleInputChange('m1311Stage4Present', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.m1311Stage4Worsened} onChange={(e) => handleInputChange('m1311Stage4Worsened', e.target.value)} /></td></tr>
                  <tr><td className="border p-2">D. Unstageable - Non-removable dressing/device</td><td className="border p-1"><VoiceDictationInput value={formData.m1311UnstageableDressingPresent} onChange={(e) => handleInputChange('m1311UnstageableDressingPresent', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.m1311UnstageableDressingWorsened} onChange={(e) => handleInputChange('m1311UnstageableDressingWorsened', e.target.value)} /></td></tr>
                  <tr><td className="border p-2">E. Unstageable - Slough and/or eschar</td><td className="border p-1"><VoiceDictationInput value={formData.m1311UnstageableSloughPresent} onChange={(e) => handleInputChange('m1311UnstageableSloughPresent', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.m1311UnstageableSloughWorsened} onChange={(e) => handleInputChange('m1311UnstageableSloughWorsened', e.target.value)} /></td></tr>
                  <tr><td className="border p-2">F. Unstageable - Deep tissue injury</td><td className="border p-1"><VoiceDictationInput value={formData.m1311UnstageableDTIPresent} onChange={(e) => handleInputChange('m1311UnstageableDTIPresent', e.target.value)} /></td><td className="border p-1"><VoiceDictationInput value={formData.m1311UnstageableDTIWorsened} onChange={(e) => handleInputChange('m1311UnstageableDTIWorsened', e.target.value)} /></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* M1322 Current Number of Stage 1 Pressure Injuries */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1322. Current Number of Stage 1 Pressure Injuries</Label>
          <p className="text-xs text-gray-600 mb-2">Intact skin with non-blanchable redness of a localized area usually over a bony prominence.</p>
          <VoiceDictationInput value={formData.m1322Stage1PressureInjuries} onChange={(e) => handleInputChange('m1322Stage1PressureInjuries', e.target.value)} placeholder="Number (0-9+)" className="w-32" />
        </div>

        {/* M1324 Stage of Most Problematic Pressure Ulcer */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1324. Stage of Most Problematic Unhealed Pressure Ulcer/Injury that is Stageable (Enter Code)</Label>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>1. Stage 1</p>
            <p>2. Stage 2</p>
            <p>3. Stage 3</p>
            <p>4. Stage 4</p>
            <p>NA No stageable pressure ulcer/injury</p>
          </div>
          <VoiceDictationInput value={formData.m1324MostProblematicStage} onChange={(e) => handleInputChange('m1324MostProblematicStage', e.target.value)} placeholder="Enter 1-4 or NA" className="w-32" />
        </div>

        {/* M1330 Does this Patient Have a Stasis Ulcer */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1330. Does this patient have a Stasis Ulcer? (Enter Code)</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m1330" checked={formData.m1330StasisUlcer === '0'} onChange={() => handleInputChange('m1330StasisUlcer', '0')} className="w-4 h-4" /><span className="text-sm">0. No → Skip to M1340</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1330" checked={formData.m1330StasisUlcer === '1'} onChange={() => handleInputChange('m1330StasisUlcer', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes, patient has BOTH observable and unobservable stasis ulcers</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1330" checked={formData.m1330StasisUlcer === '2'} onChange={() => handleInputChange('m1330StasisUlcer', '2')} className="w-4 h-4" /><span className="text-sm">2. Yes, patient has observable stasis ulcers ONLY</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1330" checked={formData.m1330StasisUlcer === '3'} onChange={() => handleInputChange('m1330StasisUlcer', '3')} className="w-4 h-4" /><span className="text-sm">3. Yes, patient has unobservable stasis ulcers ONLY</span></label>
          </div>
        </div>

        {/* M1340 Does this Patient Have a Surgical Wound */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1340. Does this patient have a Surgical Wound? (Enter Code)</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m1340" checked={formData.m1340SurgicalWound === '0'} onChange={() => handleInputChange('m1340SurgicalWound', '0')} className="w-4 h-4" /><span className="text-sm">0. No → Skip to M1400</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1340" checked={formData.m1340SurgicalWound === '1'} onChange={() => handleInputChange('m1340SurgicalWound', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes, patient has BOTH observable and unobservable surgical wounds</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1340" checked={formData.m1340SurgicalWound === '2'} onChange={() => handleInputChange('m1340SurgicalWound', '2')} className="w-4 h-4" /><span className="text-sm">2. Yes, patient has observable surgical wounds ONLY</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1340" checked={formData.m1340SurgicalWound === '3'} onChange={() => handleInputChange('m1340SurgicalWound', '3')} className="w-4 h-4" /><span className="text-sm">3. Yes, patient has unobservable surgical wounds ONLY</span></label>
          </div>
        </div>

        {/* M1342 Status of Most Problematic Surgical Wound */}
        {(formData.m1340SurgicalWound === '1' || formData.m1340SurgicalWound === '2') && (
          <div className="bg-white p-4 rounded border mb-4">
            <Label className="font-bold text-sm">M1342. Status of Most Problematic Surgical Wound that is Observable (Enter Code)</Label>
            <div className="text-xs text-gray-600 space-y-1 mb-2">
              <p>0. Newly epithelialized</p>
              <p>1. Fully granulating</p>
              <p>2. Early/partial granulation</p>
              <p>3. Not healing</p>
            </div>
            <VoiceDictationInput value={formData.m1342SurgicalWoundStatus} onChange={(e) => handleInputChange('m1342SurgicalWoundStatus', e.target.value)} placeholder="Enter 0-3" className="w-32" />
          </div>
        )}

        {/* Wound Documentation */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">WOUND DOCUMENTATION</Label>
          <p className="text-xs text-gray-600 mb-2">Document each wound present</p>
          
          <div className="mt-3 p-3 bg-gray-50 rounded">
            <Label className="text-sm font-bold">Wound #1:</Label>
            <div className="grid grid-cols-3 gap-4 mt-2">
              <VoiceDictationInput label="Type:" value={formData.wound1Type} onChange={(e) => handleInputChange('wound1Type', e.target.value)} />
              <VoiceDictationInput label="Location:" value={formData.wound1Location} onChange={(e) => handleInputChange('wound1Location', e.target.value)} />
              <VoiceDictationInput label="Etiology:" value={formData.wound1Etiology} onChange={(e) => handleInputChange('wound1Etiology', e.target.value)} />
              <VoiceDictationInput label="Length (cm):" value={formData.wound1Length} onChange={(e) => handleInputChange('wound1Length', e.target.value)} />
              <VoiceDictationInput label="Width (cm):" value={formData.wound1Width} onChange={(e) => handleInputChange('wound1Width', e.target.value)} />
              <VoiceDictationInput label="Depth (cm):" value={formData.wound1Depth} onChange={(e) => handleInputChange('wound1Depth', e.target.value)} />
            </div>
            <VoiceDictationInput label="Wound bed description:" value={formData.wound1BedDescription} onChange={(e) => handleInputChange('wound1BedDescription', e.target.value)} className="mt-2" />
            <VoiceDictationInput label="Exudate type/amount:" value={formData.wound1Exudate} onChange={(e) => handleInputChange('wound1Exudate', e.target.value)} className="mt-2" />
            <VoiceDictationInput label="Periwound condition:" value={formData.wound1Periwound} onChange={(e) => handleInputChange('wound1Periwound', e.target.value)} className="mt-2" />
            <VoiceDictationInput label="Treatment/Dressing:" value={formData.wound1Treatment} onChange={(e) => handleInputChange('wound1Treatment', e.target.value)} className="mt-2" />
          </div>
          
          <VoiceDictationInput label="Additional wounds (document as above):" value={formData.additionalWounds} onChange={(e) => handleInputChange('additionalWounds', e.target.value)} rows={4} className="mt-3" />
        </div>

        {/* Diabetic Foot Exam */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">DIABETIC FOOT EXAM (if applicable)</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Foot condition:</Label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <Label className="text-xs font-medium">Left:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footLeftIntact} onChange={(e) => handleInputChange('footLeftIntact', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Intact</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footLeftDry} onChange={(e) => handleInputChange('footLeftDry', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Dry</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footLeftCallus} onChange={(e) => handleInputChange('footLeftCallus', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Callus</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footLeftUlcer} onChange={(e) => handleInputChange('footLeftUlcer', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Ulcer</span></label>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Right:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footRightIntact} onChange={(e) => handleInputChange('footRightIntact', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Intact</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footRightDry} onChange={(e) => handleInputChange('footRightDry', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Dry</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footRightCallus} onChange={(e) => handleInputChange('footRightCallus', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Callus</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.footRightUlcer} onChange={(e) => handleInputChange('footRightUlcer', e.target.checked)} className="w-4 h-4" /><span className="text-xs">Ulcer</span></label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Pedal pulses:</Label>
            <div className="grid grid-cols-2 gap-4 mt-1">
              <VoiceDictationInput label="Left:" value={formData.pedalPulsesLeft} onChange={(e) => handleInputChange('pedalPulsesLeft', e.target.value)} placeholder="Present/Absent/Weak" />
              <VoiceDictationInput label="Right:" value={formData.pedalPulsesRight} onChange={(e) => handleInputChange('pedalPulsesRight', e.target.value)} placeholder="Present/Absent/Weak" />
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Sensation (monofilament test):</Label>
            <div className="grid grid-cols-2 gap-4 mt-1">
              <VoiceDictationInput label="Left:" value={formData.sensationLeft} onChange={(e) => handleInputChange('sensationLeft', e.target.value)} placeholder="Intact/Impaired/Absent" />
              <VoiceDictationInput label="Right:" value={formData.sensationRight} onChange={(e) => handleInputChange('sensationRight', e.target.value)} placeholder="Intact/Impaired/Absent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionN = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section N Medications</h3>
        
        {/* N0415 High-Risk Drug Classes */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">N0415. High-Risk Drug Classes: Use and Indication</Label>
          <p className="text-xs text-gray-600 mb-2">Is the patient currently taking any medications in the following classes?</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Drug Class</th>
                  <th className="border p-2 text-center">Taking?</th>
                  <th className="border p-2 text-center">Indication Noted?</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border p-2">A. Antipsychotic</td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AntipsychoticTaking} onChange={(e) => handleInputChange('n0415AntipsychoticTaking', e.target.value)} placeholder="0/1" className="w-16" /></td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AntipsychoticIndication} onChange={(e) => handleInputChange('n0415AntipsychoticIndication', e.target.value)} placeholder="0/1/9" className="w-16" /></td></tr>
                <tr><td className="border p-2">B. Anticoagulant</td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AnticoagulantTaking} onChange={(e) => handleInputChange('n0415AnticoagulantTaking', e.target.value)} placeholder="0/1" className="w-16" /></td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AnticoagulantIndication} onChange={(e) => handleInputChange('n0415AnticoagulantIndication', e.target.value)} placeholder="0/1/9" className="w-16" /></td></tr>
                <tr><td className="border p-2">C. Antibiotic</td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AntibioticTaking} onChange={(e) => handleInputChange('n0415AntibioticTaking', e.target.value)} placeholder="0/1" className="w-16" /></td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AntibioticIndication} onChange={(e) => handleInputChange('n0415AntibioticIndication', e.target.value)} placeholder="0/1/9" className="w-16" /></td></tr>
                <tr><td className="border p-2">D. Opioid</td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415OpioidTaking} onChange={(e) => handleInputChange('n0415OpioidTaking', e.target.value)} placeholder="0/1" className="w-16" /></td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415OpioidIndication} onChange={(e) => handleInputChange('n0415OpioidIndication', e.target.value)} placeholder="0/1/9" className="w-16" /></td></tr>
                <tr><td className="border p-2">E. Antiplatelet</td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AntiplateletTaking} onChange={(e) => handleInputChange('n0415AntiplateletTaking', e.target.value)} placeholder="0/1" className="w-16" /></td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415AntiplateletIndication} onChange={(e) => handleInputChange('n0415AntiplateletIndication', e.target.value)} placeholder="0/1/9" className="w-16" /></td></tr>
                <tr><td className="border p-2">F. Hypoglycemic (Diabetic)</td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415HypoglycemicTaking} onChange={(e) => handleInputChange('n0415HypoglycemicTaking', e.target.value)} placeholder="0/1" className="w-16" /></td><td className="border p-1 text-center"><VoiceDictationInput value={formData.n0415HypoglycemicIndication} onChange={(e) => handleInputChange('n0415HypoglycemicIndication', e.target.value)} placeholder="0/1/9" className="w-16" /></td></tr>
                <tr><td className="border p-2">Z. None of the above</td><td className="border p-1 text-center" colSpan="2"><input type="checkbox" checked={formData.n0415NoneOfAbove} onChange={(e) => handleInputChange('n0415NoneOfAbove', e.target.checked)} className="w-4 h-4" /></td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">0 = No, 1 = Yes for Taking; 0 = No, 1 = Yes, 9 = NA for Indication</p>
        </div>

        {/* M2001 Drug Regimen Review */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M2001. Drug Regimen Review</Label>
          <p className="text-xs text-gray-600 mb-2">Did a complete drug regimen review identify potential clinically significant medication issues?</p>
          <div className="flex flex-col gap-2 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m2001" checked={formData.m2001DrugRegimenReview === '0'} onChange={() => handleInputChange('m2001DrugRegimenReview', '0')} className="w-4 h-4" /><span className="text-sm">0. No - No issues found during review → Skip to M2010</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m2001" checked={formData.m2001DrugRegimenReview === '1'} onChange={() => handleInputChange('m2001DrugRegimenReview', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes - Issues found during review</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m2001" checked={formData.m2001DrugRegimenReview === '9'} onChange={() => handleInputChange('m2001DrugRegimenReview', '9')} className="w-4 h-4" /><span className="text-sm">9. NA - Patient is not taking any medications → Skip to M2040</span></label>
          </div>
        </div>

        {/* M2003 Medication Follow-up */}
        {formData.m2001DrugRegimenReview === '1' && (
          <div className="bg-white p-4 rounded border mb-4">
            <Label className="font-bold text-sm">M2003. Medication Follow-up</Label>
            <p className="text-xs text-gray-600 mb-2">Did the agency contact a physician (or physician-designee) by midnight of the next calendar day and complete prescribed/recommended actions in response to the identified potential clinically significant medication issues?</p>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2"><input type="radio" name="m2003" checked={formData.m2003MedicationFollowup === '0'} onChange={() => handleInputChange('m2003MedicationFollowup', '0')} className="w-4 h-4" /><span className="text-sm">0. No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="m2003" checked={formData.m2003MedicationFollowup === '1'} onChange={() => handleInputChange('m2003MedicationFollowup', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes</span></label>
            </div>
          </div>
        )}

        {/* M2010 Patient/Caregiver High-Risk Drug Education */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M2010. Patient/Caregiver High-Risk Drug Education</Label>
          <p className="text-xs text-gray-600 mb-2">Has the patient/caregiver been instructed by agency staff or other health care provider to monitor the effectiveness of high-risk drug therapy, how and when to report problems that may occur, and any specific safety precautions?</p>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m2010" checked={formData.m2010HighRiskDrugEducation === '0'} onChange={() => handleInputChange('m2010HighRiskDrugEducation', '0')} className="w-4 h-4" /><span className="text-sm">0. No</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m2010" checked={formData.m2010HighRiskDrugEducation === '1'} onChange={() => handleInputChange('m2010HighRiskDrugEducation', '1')} className="w-4 h-4" /><span className="text-sm">1. Yes</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m2010" checked={formData.m2010HighRiskDrugEducation === 'NA'} onChange={() => handleInputChange('m2010HighRiskDrugEducation', 'NA')} className="w-4 h-4" /><span className="text-sm">NA - Patient not taking any high-risk drugs OR patient/caregiver fully knowledgeable</span></label>
          </div>
        </div>

        {/* M2020 Management of Oral Medications */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M2020. Management of Oral Medications (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Patient's current ability to prepare and take all oral medications reliably and safely, including administration of the correct dosage at the appropriate times/intervals.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to independently take the correct oral medication(s) and proper dosage(s) at the correct times</p>
            <p>1. Able to take medication(s) at the correct times if: individual dosages are prepared in advance by another person; OR another person develops a drug diary or chart</p>
            <p>2. Able to take medication(s) at the correct times if given reminders by another person at the appropriate times</p>
            <p>3. Unable to take medication unless administered by another person</p>
            <p>NA - No oral medications prescribed</p>
          </div>
          <VoiceDictationInput value={formData.m2020ManagementOralMeds} onChange={(e) => handleInputChange('m2020ManagementOralMeds', e.target.value)} placeholder="Enter 0-3 or NA" className="w-32" />
        </div>

        {/* M2030 Management of Injectable Medications */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M2030. Management of Injectable Medications (Enter Code)</Label>
          <p className="text-xs text-gray-600 mb-2">Patient's current ability to prepare and take all injectable medications reliably and safely.</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. Able to independently take the correct medication(s) and proper dosage(s) at the correct times</p>
            <p>1. Able to take medication(s) at the correct times if: individual dosages are prepared in advance by another person; OR another person develops a drug diary or chart</p>
            <p>2. Able to take medication(s) at the correct times if given reminders by another person</p>
            <p>3. Unable to take medication unless administered by another person</p>
            <p>NA - No injectable medications prescribed</p>
          </div>
          <VoiceDictationInput value={formData.m2030ManagementInjectableMeds} onChange={(e) => handleInputChange('m2030ManagementInjectableMeds', e.target.value)} placeholder="Enter 0-3 or NA" className="w-32" />
        </div>

        {/* IV/Infusion Therapy */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">IV/INFUSION THERAPY (if applicable)</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Type of access:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.ivAccessPeripheral} onChange={(e) => handleInputChange('ivAccessPeripheral', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Peripheral</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.ivAccessPICC} onChange={(e) => handleInputChange('ivAccessPICC', e.target.checked)} className="w-4 h-4" /><span className="text-sm">PICC</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.ivAccessCentralLine} onChange={(e) => handleInputChange('ivAccessCentralLine', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Central line</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.ivAccessPort} onChange={(e) => handleInputChange('ivAccessPort', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Port</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.ivAccessMidline} onChange={(e) => handleInputChange('ivAccessMidline', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Midline</span></label>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <VoiceDictationInput label="Location:" value={formData.ivLocation} onChange={(e) => handleInputChange('ivLocation', e.target.value)} />
            <VoiceDictationInput label="Date inserted:" value={formData.ivDateInserted} onChange={(e) => handleInputChange('ivDateInserted', e.target.value)} />
            <VoiceDictationInput label="Site condition:" value={formData.ivSiteCondition} onChange={(e) => handleInputChange('ivSiteCondition', e.target.value)} />
            <VoiceDictationInput label="Dressing change frequency:" value={formData.ivDressingChangeFrequency} onChange={(e) => handleInputChange('ivDressingChangeFrequency', e.target.value)} />
          </div>
          
          <VoiceDictationInput label="IV medications/fluids administered:" value={formData.ivMedicationsAdministered} onChange={(e) => handleInputChange('ivMedicationsAdministered', e.target.value)} rows={2} className="mt-3" />
        </div>

        {/* Medication List Note */}
        <div className="bg-yellow-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">MEDICATION LIST</Label>
          <p className="text-sm mt-2">Complete medication reconciliation should be performed and documented separately. Include all prescription and over-the-counter medications, vitamins, and supplements.</p>
          <VoiceDictationInput label="Comments/Notes:" value={formData.medicationListComments} onChange={(e) => handleInputChange('medicationListComments', e.target.value)} rows={3} className="mt-3" />
        </div>
      </div>
    </div>
  );

  const renderSectionO = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section O Special Treatments, Procedures, and Programs</h3>
        
        {/* O0110 Special Treatments */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">O0110. Special Treatments, Procedures, and Programs</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all that apply</p>
          
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Chemotherapy} onChange={(e) => handleInputChange('o0110Chemotherapy', e.target.checked)} className="w-4 h-4" /><span className="text-sm">A1. Chemotherapy</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Radiation} onChange={(e) => handleInputChange('o0110Radiation', e.target.checked)} className="w-4 h-4" /><span className="text-sm">B1. Radiation</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110OxygenContinuous} onChange={(e) => handleInputChange('o0110OxygenContinuous', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C1. Oxygen (Continuous)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110OxygenIntermittent} onChange={(e) => handleInputChange('o0110OxygenIntermittent', e.target.checked)} className="w-4 h-4" /><span className="text-sm">C2. Oxygen (Intermittent)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Suctioning} onChange={(e) => handleInputChange('o0110Suctioning', e.target.checked)} className="w-4 h-4" /><span className="text-sm">D1. Suctioning</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Tracheostomy} onChange={(e) => handleInputChange('o0110Tracheostomy', e.target.checked)} className="w-4 h-4" /><span className="text-sm">E1. Tracheostomy care</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Ventilator} onChange={(e) => handleInputChange('o0110Ventilator', e.target.checked)} className="w-4 h-4" /><span className="text-sm">F1. Invasive mechanical ventilator</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110BiPAPCPAP} onChange={(e) => handleInputChange('o0110BiPAPCPAP', e.target.checked)} className="w-4 h-4" /><span className="text-sm">F2. Non-invasive mechanical ventilator (BiPAP, CPAP)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110IVMedications} onChange={(e) => handleInputChange('o0110IVMedications', e.target.checked)} className="w-4 h-4" /><span className="text-sm">G1. IV Medications</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110TransfusionBlood} onChange={(e) => handleInputChange('o0110TransfusionBlood', e.target.checked)} className="w-4 h-4" /><span className="text-sm">H1. Transfusions</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Dialysis} onChange={(e) => handleInputChange('o0110Dialysis', e.target.checked)} className="w-4 h-4" /><span className="text-sm">I1. Dialysis</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110Hospice} onChange={(e) => handleInputChange('o0110Hospice', e.target.checked)} className="w-4 h-4" /><span className="text-sm">O1. Hospice care</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.o0110NoneOfAbove} onChange={(e) => handleInputChange('o0110NoneOfAbove', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Z1. None of the above</span></label>
          </div>
        </div>

        {/* M2200 Therapy Need */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M2200. Therapy Need</Label>
          <p className="text-xs text-gray-600 mb-2">In the home health plan of care for the Medicare payment episode for which this assessment will define a case mix group, what is the indicated need for therapy visits?</p>
          <div className="text-xs text-gray-600 space-y-1 mb-2">
            <p>0. No therapy visits indicated</p>
            <p>1. Therapy visits indicated</p>
            <p>NA - Not applicable</p>
          </div>
          <VoiceDictationInput value={formData.m2200TherapyNeed} onChange={(e) => handleInputChange('m2200TherapyNeed', e.target.value)} placeholder="Enter 0, 1, or NA" className="w-32" />
        </div>

        {/* Education/Training */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">EDUCATION/TRAINING PROVIDED</Label>
          <p className="text-xs text-gray-600 mb-2">↓ Check all topics covered during this visit</p>
          
          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationDiseaseProcess} onChange={(e) => handleInputChange('educationDiseaseProcess', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Disease process</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationMedications} onChange={(e) => handleInputChange('educationMedications', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Medications</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationDiet} onChange={(e) => handleInputChange('educationDiet', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Diet/Nutrition</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationActivity} onChange={(e) => handleInputChange('educationActivity', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Activity/Exercise</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationSafetyPrecautions} onChange={(e) => handleInputChange('educationSafetyPrecautions', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Safety precautions</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationFallPrevention} onChange={(e) => handleInputChange('educationFallPrevention', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Fall prevention</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationWoundCare} onChange={(e) => handleInputChange('educationWoundCare', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Wound care</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationPainManagement} onChange={(e) => handleInputChange('educationPainManagement', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pain management</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationEmergencySigns} onChange={(e) => handleInputChange('educationEmergencySigns', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Signs/symptoms to report</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationDME} onChange={(e) => handleInputChange('educationDME', e.target.checked)} className="w-4 h-4" /><span className="text-sm">DME use</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationInfectionControl} onChange={(e) => handleInputChange('educationInfectionControl', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Infection control</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationOther} onChange={(e) => handleInputChange('educationOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
          </div>
          {formData.educationOther && <VoiceDictationInput value={formData.educationOtherSpecify} onChange={(e) => handleInputChange('educationOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          
          <div className="mt-3">
            <Label className="text-sm">Education provided to:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationProvidedToPatient} onChange={(e) => handleInputChange('educationProvidedToPatient', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Patient</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationProvidedToFamily} onChange={(e) => handleInputChange('educationProvidedToFamily', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Family</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.educationProvidedToCaregiver} onChange={(e) => handleInputChange('educationProvidedToCaregiver', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Caregiver</span></label>
            </div>
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Response to teaching:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="teachingResponse" checked={formData.responseToTeaching === 'Verbalized'} onChange={() => handleInputChange('responseToTeaching', 'Verbalized')} className="w-4 h-4" /><span className="text-sm">Verbalized understanding</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="teachingResponse" checked={formData.responseToTeaching === 'Demonstrated'} onChange={() => handleInputChange('responseToTeaching', 'Demonstrated')} className="w-4 h-4" /><span className="text-sm">Demonstrated understanding</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="teachingResponse" checked={formData.responseToTeaching === 'RequiresReinforcement'} onChange={() => handleInputChange('responseToTeaching', 'RequiresReinforcement')} className="w-4 h-4" /><span className="text-sm">Requires reinforcement</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="teachingResponse" checked={formData.responseToTeaching === 'UnableToLearn'} onChange={() => handleInputChange('responseToTeaching', 'UnableToLearn')} className="w-4 h-4" /><span className="text-sm">Unable to learn</span></label>
            </div>
          </div>
        </div>

        {/* Care Coordination */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">CARE COORDINATION</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Physician contact:</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="physicianContact" checked={formData.physicianContact === 'No'} onChange={() => handleInputChange('physicianContact', 'No')} className="w-4 h-4" /><span className="text-sm">No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="physicianContact" checked={formData.physicianContact === 'Yes'} onChange={() => handleInputChange('physicianContact', 'Yes')} className="w-4 h-4" /><span className="text-sm">Yes</span></label>
            </div>
            {formData.physicianContact === 'Yes' && (
              <VoiceDictationInput label="Reason and outcome:" value={formData.physicianContactDetails} onChange={(e) => handleInputChange('physicianContactDetails', e.target.value)} className="mt-2" />
            )}
          </div>
          
          <div className="mt-3">
            <Label className="text-sm">Other disciplines involved:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.disciplinePT} onChange={(e) => handleInputChange('disciplinePT', e.target.checked)} className="w-4 h-4" /><span className="text-sm">PT</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.disciplineOT} onChange={(e) => handleInputChange('disciplineOT', e.target.checked)} className="w-4 h-4" /><span className="text-sm">OT</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.disciplineSLP} onChange={(e) => handleInputChange('disciplineSLP', e.target.checked)} className="w-4 h-4" /><span className="text-sm">SLP</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.disciplineMSW} onChange={(e) => handleInputChange('disciplineMSW', e.target.checked)} className="w-4 h-4" /><span className="text-sm">MSW</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.disciplineHHA} onChange={(e) => handleInputChange('disciplineHHA', e.target.checked)} className="w-4 h-4" /><span className="text-sm">HHA</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.disciplineDietitian} onChange={(e) => handleInputChange('disciplineDietitian', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Dietitian</span></label>
            </div>
          </div>
        </div>

        {/* DME/Supplies */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">DME/SUPPLIES</Label>
          
          <div className="mt-3">
            <Label className="text-sm">Equipment in home:</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeWalker} onChange={(e) => handleInputChange('dmeWalker', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Walker</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeCane} onChange={(e) => handleInputChange('dmeCane', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cane</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeWheelchair} onChange={(e) => handleInputChange('dmeWheelchair', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Wheelchair</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeHospitalBed} onChange={(e) => handleInputChange('dmeHospitalBed', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Hospital bed</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeBedside} onChange={(e) => handleInputChange('dmeBedside', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Bedside commode</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeOxygen} onChange={(e) => handleInputChange('dmeOxygen', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Oxygen</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeNebulizer} onChange={(e) => handleInputChange('dmeNebulizer', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Nebulizer</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeGlucometer} onChange={(e) => handleInputChange('dmeGlucometer', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Glucometer</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeBPMonitor} onChange={(e) => handleInputChange('dmeBPMonitor', e.target.checked)} className="w-4 h-4" /><span className="text-sm">BP monitor</span></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.dmeOther} onChange={(e) => handleInputChange('dmeOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
            </div>
            {formData.dmeOther && <VoiceDictationInput value={formData.dmeOtherSpecify} onChange={(e) => handleInputChange('dmeOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          </div>
          
          <VoiceDictationInput label="DME/Supplies needed:" value={formData.dmeSuppliesNeeded} onChange={(e) => handleInputChange('dmeSuppliesNeeded', e.target.value)} rows={2} className="mt-3" />
        </div>
      </div>
    </div>
  );

  const renderHomeboundSummary = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Homebound Status and Assessment Summary</h3>
        
        {/* Homebound Status */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">HOMEBOUND STATUS</Label>
          <p className="text-xs text-gray-600 mb-2">Patient qualifies as homebound because leaving home requires considerable and taxing effort due to:</p>
          
          <div className="grid grid-cols-2 gap-2 mt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundBedrest} onChange={(e) => handleInputChange('homeboundBedrest', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Bedrest orders</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundAmbulationDeficit} onChange={(e) => handleInputChange('homeboundAmbulationDeficit', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Ambulation deficit</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundMedicalRestriction} onChange={(e) => handleInputChange('homeboundMedicalRestriction', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Medical restriction</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundNeedsAssistiveDevice} onChange={(e) => handleInputChange('homeboundNeedsAssistiveDevice', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Needs assistive device(s)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundSOB} onChange={(e) => handleInputChange('homeboundSOB', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Shortness of breath with activity</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundPain} onChange={(e) => handleInputChange('homeboundPain', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Pain with activity</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundWeakness} onChange={(e) => handleInputChange('homeboundWeakness', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Weakness/fatigue</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundCognitiveImpairment} onChange={(e) => handleInputChange('homeboundCognitiveImpairment', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Cognitive impairment</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundPsychiatricCondition} onChange={(e) => handleInputChange('homeboundPsychiatricCondition', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Psychiatric condition</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundNeedsAssistanceOfAnother} onChange={(e) => handleInputChange('homeboundNeedsAssistanceOfAnother', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Needs assistance of another person</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundUnsafeToLeaveAlone} onChange={(e) => handleInputChange('homeboundUnsafeToLeaveAlone', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Unsafe to leave home alone</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.homeboundOther} onChange={(e) => handleInputChange('homeboundOther', e.target.checked)} className="w-4 h-4" /><span className="text-sm">Other:</span></label>
          </div>
          {formData.homeboundOther && <VoiceDictationInput value={formData.homeboundOtherSpecify} onChange={(e) => handleInputChange('homeboundOtherSpecify', e.target.value)} className="mt-2 w-64" />}
          
          <VoiceDictationInput label="Additional homebound justification:" value={formData.homeboundJustification} onChange={(e) => handleInputChange('homeboundJustification', e.target.value)} rows={3} className="mt-3" />
        </div>

        {/* Assessment Summary */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">ASSESSMENT SUMMARY/CLINICAL FINDINGS</Label>
          <VoiceDictationInput value={formData.assessmentSummary} onChange={(e) => handleInputChange('assessmentSummary', e.target.value)} rows={6} className="mt-2" placeholder="Summarize key clinical findings, patient status, and overall assessment..." />
        </div>

        {/* AI OASIS Clinical Summary Generator */}
        <AIOasisSummaryGenerator 
          formData={formData}
          patientName={patients.find(p => p.id === formData.patientId)?.firstName + ' ' + patients.find(p => p.id === formData.patientId)?.lastName || 'Patient'}
          assessmentType={formData.reasonForAssessment || 'SOC/ROC'}
          onSummaryGenerated={(summary) => handleInputChange('assessmentSummary', summary)}
        />

        {/* Skilled Nursing Needs */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">SKILLED NURSING NEEDS IDENTIFIED</Label>
          <VoiceDictationInput value={formData.skilledNursingNeeds} onChange={(e) => handleInputChange('skilledNursingNeeds', e.target.value)} rows={4} className="mt-2" placeholder="Document skilled nursing interventions required..." />
        </div>

        {/* Goals */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GOALS/EXPECTED OUTCOMES</Label>
          <VoiceDictationInput value={formData.goalsExpectedOutcomes} onChange={(e) => handleInputChange('goalsExpectedOutcomes', e.target.value)} rows={4} className="mt-2" placeholder="Document patient-centered goals and expected outcomes..." />
        </div>

        {/* Plan of Care */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PLAN OF CARE</Label>
          <VoiceDictationInput value={formData.planOfCare} onChange={(e) => handleInputChange('planOfCare', e.target.value)} rows={4} className="mt-2" placeholder="Describe the plan of care including frequency and duration of visits..." />
        </div>

        {/* Signatures */}
        <div className="bg-gray-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">SIGNATURES</Label>
          <p className="text-sm text-gray-600 mt-1 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            <div className="space-y-3">
              <VoiceDictationInput label="Clinician Name (Print):" value={formData.clinicianNamePrint} onChange={(e) => handleInputChange('clinicianNamePrint', e.target.value)} />
              <SignatureInput 
                label="Clinician Signature" 
                value={formData.clinicianSignature} 
                onChange={(value) => handleInputChange('clinicianSignature', value)} 
              />
              <VoiceDictationInput label="Credentials:" value={formData.clinicianCredentials} onChange={(e) => handleInputChange('clinicianCredentials', e.target.value)} />
              <VoiceDictationInput label="Date:" value={formData.clinicianSignatureDate} onChange={(e) => handleInputChange('clinicianSignatureDate', e.target.value)} />
            </div>
            <div className="space-y-3">
              <SignatureInput 
                label="Patient/Representative Signature" 
                value={formData.patientSignature} 
                onChange={(value) => handleInputChange('patientSignature', value)} 
              />
              <VoiceDictationInput label="Date:" value={formData.patientSignatureDate} onChange={(e) => handleInputChange('patientSignatureDate', e.target.value)} />
              <div>
                <Label className="text-sm">If signed by representative, relationship:</Label>
                <VoiceDictationInput value={formData.representativeRelationship} onChange={(e) => handleInputChange('representativeRelationship', e.target.value)} className="mt-1" />
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
            <Label className="text-sm font-bold">Patient Rights Acknowledgment:</Label>
            <div className="flex items-center gap-2 mt-2">
              <input type="checkbox" checked={formData.patientRightsProvided} onChange={(e) => handleInputChange('patientRightsProvided', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">Patient/representative has been provided with a copy of patient rights and responsibilities</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={formData.advanceDirectivesDiscussed} onChange={(e) => handleInputChange('advanceDirectivesDiscussed', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">Advance directives have been discussed with the patient/representative</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={formData.hipaaNoticeProvided} onChange={(e) => handleInputChange('hipaaNoticeProvided', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">HIPAA Notice of Privacy Practices has been provided</span>
            </div>
          </div>
        </div>

        {/* Physician Certification */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">PHYSICIAN CERTIFICATION</Label>
          <p className="text-xs text-gray-600 mt-1">I certify that this patient is confined to his/her home and needs intermittent skilled nursing care, physical therapy and/or speech-language pathology services for the condition(s) indicated above. The patient is under my care, and I have authorized the services on this plan of care and will periodically review the plan.</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            <SignatureInput 
              label="Physician Signature" 
              value={formData.physicianCertificationSignature} 
              onChange={(value) => handleInputChange('physicianCertificationSignature', value)} 
            />
            <VoiceDictationInput label="Date:" value={formData.physicianCertificationDate} onChange={(e) => handleInputChange('physicianCertificationDate', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (currentSection) {
      case 0: return renderSectionA();
      case 1: return renderSectionB();
      case 2: return renderSectionC();
      case 3: return renderSectionD();
      case 4: return renderSectionE();
      case 5: return renderSectionF();
      case 6: return renderSectionG();
      case 7: return renderSectionGG();
      case 8: return renderSectionH();
      case 9: return renderSectionI();
      case 10: return renderSectionJ();
      case 11: return renderSectionK();
      case 12: return renderSectionM();
      case 13: return renderSectionN();
      case 14: return renderSectionO();
      case 15: return renderHomeboundSummary();
      default: return renderSectionA();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ea580c]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                SOC/ROC OASIS COMPREHENSIVE NURSING ASSESSMENT
              </h1>
              <p className="text-sm text-gray-500">
                WITH PLAN OF CARE INFORMATION
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          </div>
        </div>

        {/* Section Tabs */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-1 min-w-max pb-2">
            {sections.map((section, idx) => {
              const Icon = section.icon;
              return (
                <button
                  key={idx}
                  onClick={() => setCurrentSection(idx)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                    currentSection === idx
                      ? 'bg-[#ea580c] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="hidden lg:inline">{section.name.split(':')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          {renderSectionContent()}
        </div>

        {/* Navigation & Actions */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button variant="outline">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
          
          <Button
            onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
            disabled={currentSection === sections.length - 1}
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SOCROCAssessmentFormV2;
